import React, { useRef, useEffect, useState, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { warn } from "../lib/log";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { colors } from "../theme";
import { InfoTip } from "./InfoTip";
import ImportGuide from "./ImportGuide";
import { useStudio, locationSurfaceArea } from "../store/useStudio";
import { GLAZING_PRESETS, DEFAULT_GLAZING, glazingAggregate, tagIsGlazed, tagIsOpaque } from "../lib/glazing";
import { daySunSamples, SEASON_LABELS } from "../lib/solar";
import { HIGHLIGHT_COLORS, RING_HEX, RING_CSS, IDENTIFY_HEX, CLASSIFY_CATS, BASE_CATS, MATERIAL_DEFS, BUCKET_TO_BASE, CLASSIFY_HEX, ANALYSIS_TITLE, UNIT_TO_M, MODEL_EXTS, HEAT_VERT, HEAT_FRAG } from "./viewport/constants";
import { componentWorldVerts, componentWorldTris, principalAxesXZ, convexHullXZ, buildingBoundsY, semanticTag, orientationLabel, bucketFromNormal, buildCoplanarHighlight, rectShape, polygonArea, polygonCentroid, coplanarFace, clayMaterial, hatchTexture, addEdges, rhinoLayerColor, deriveNorthDeg } from "./viewport/geometry";
import { styles } from "./viewport/styles";

export default function Viewport3D({ modelFile, useSample, highlights, model, areaFocusSystem, faceScores, onPickModel, onUseSample, onClear, onImportProject, onSurfaceArea, setModel, onSurfaceTag, onNotify, onAllocArea, onSaveProject, onGenerateReport, onReportDetails, onExportProject, captureRef, embedded = false }) {
  const mountRef = useRef(null);
  const apiRef = useRef(null);
  const highlightRef = useRef(highlights || []);
  const areaFocusRef = useRef(areaFocusSystem);
  const onSurfaceAreaRef = useRef(onSurfaceArea);
  const onSurfaceTagRef = useRef(onSurfaceTag);
  const selectedMeshRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [layers, setLayers] = useState([]);      // Rhino layer table for the toggle overlay
  const [biaPrompt, setBiaPrompt] = useState(false); // ask to enter optimization mode on BIA tag
  const setOptimizationMode = useStudio((s) => s.setOptimizationMode);
  const scope = useStudio((s) => s.scope);
  const [layersOpen, setLayersOpen] = useState(true);
  // Item 3 — site/floor anchor prompt: when a model has multiple layers, ask the user which
  // layer(s) are the ground floor / site slab so context masses don't skew the ground datum.
  const [showAnchorPrompt, setShowAnchorPrompt] = useState(false);
  const [anchorSel, setAnchorSel] = useState(() => new Set());
  const [siteAnchored, setSiteAnchored] = useState(false);
  const measureMode = useStudio((s) => s.measureActive);    // spot-check measure tool
  const setMeasureMode = useStudio((s) => s.setMeasureActive);
  const measureInfo = useStudio((s) => s.measureInfo);      // { dist } in metres
  const setMeasureInfo = useStudio((s) => s.setMeasureInfo);
  const sectionOn = useStudio((s) => s.sectionOn);          // Phase 2 — Section Box
  const setSectionOn = useStudio((s) => s.setSectionOn);
  const sectionXYZ = useStudio((s) => s.sectionXYZ);
  const setSectionXYZ = useStudio((s) => s.setSectionXYZ);
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  useEffect(() => { apiRef.current?.setSectionBox?.({ active: sectionOn, ...sectionXYZ }); }, [sectionOn, sectionXYZ]);
  const measureRef = useRef(false);
  // Manual surface classification
  const classifyMode = useStudio((s) => s.classifyMode);
  const classifyCategory = useStudio((s) => s.classifyCategory);
  const classifyLayer = useStudio((s) => s.classifyLayer);
  const classifyMaterial = useStudio((s) => s.classifyMaterial);
  const classifyDrawMode = useStudio((s) => s.classifyDrawMode);
  const surfaceTags = useStudio((s) => s.surfaceTags);
  const updateSurfaceTag = useStudio((s) => s.updateSurfaceTag);
  const classifyWeather = useStudio((s) => s.weather);
  const classifyGeo = useStudio((s) => s.geo);
  const setClassifyMode = useStudio((s) => s.setClassifyMode);
  const setClassifyCategory = useStudio((s) => s.setClassifyCategory);
  const setClassifyLayer = useStudio((s) => s.setClassifyLayer);
  const setClassifyMaterial = useStudio((s) => s.setClassifyMaterial);
  const setClassifyDrawMode = useStudio((s) => s.setClassifyDrawMode);
  const clearSurfaceTags = useStudio((s) => s.clearSurfaceTags);
  const undoSurfaceTags = useStudio((s) => s.undoSurfaceTags);
  const canUndoClassify = useStudio((s) => s.surfaceTagsPast.length > 0);
  const classifyModeRef = useRef(false);
  const classifyCatRef = useRef("roof");
  const classifyLayerRef = useRef("base");
  const classifyMaterialRef = useRef("glazed");
  const classifyDrawRef = useRef("face");
  useEffect(() => { classifyModeRef.current = classifyMode; if (!classifyMode) { apiRef.current?.clearClassifyHover?.(); apiRef.current?.cancelClassifyPoly?.(); } }, [classifyMode]);
  useEffect(() => { classifyCatRef.current = classifyCategory; }, [classifyCategory]);
  useEffect(() => { classifyLayerRef.current = classifyLayer; }, [classifyLayer]);
  useEffect(() => { classifyMaterialRef.current = classifyMaterial; }, [classifyMaterial]);
  useEffect(() => { classifyDrawRef.current = classifyDrawMode; apiRef.current?.cancelClassifyPoly?.(); }, [classifyDrawMode]);
  useEffect(() => { apiRef.current?.syncSurfaceTags?.(surfaceTags); }, [surfaceTags]);
  // Re-derive gross areas when manual classifications change (engine override).
  useEffect(() => {
    const g = apiRef.current?.computeGross?.();
    if (g) useStudio.getState().setGrossAreas(g);
  }, [surfaceTags]);
  useEffect(() => { measureRef.current = measureMode; apiRef.current?.setMeasureActive?.(measureMode); if (!measureMode) apiRef.current?.clearMeasure?.(); }, [measureMode]);
  const measureClearNonce = useStudio((s) => s.measureClearNonce);
  const [toolsMin, setToolsMin] = useState(false); // floating tool dock minimized to a left tab
  const saveStatus = useStudio((s) => s.saveStatus);
  // Draggable "Show Possibilities" (analysis) window — move it off the 3D canvas.
  const [analysisPos, setAnalysisPos] = useState(null); // {x,y} in viewport-container px
  const analysisDragRef = useRef(null);
  const flexBarRef = useRef(null);
  const startAnalysisDrag = useCallback((e) => {
    const el = flexBarRef.current;
    if (!el) return;
    const parent = el.offsetParent;
    const rect = el.getBoundingClientRect();
    const prect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
    analysisDragRef.current = {
      dx: e.clientX - rect.left, dy: e.clientY - rect.top,
      pleft: prect.left, ptop: prect.top,
      pw: parent?.clientWidth || window.innerWidth, ph: parent?.clientHeight || window.innerHeight,
      w: rect.width, h: rect.height,
    };
    e.preventDefault();
  }, []);
  useEffect(() => {
    const onMove = (e) => {
      const d = analysisDragRef.current; if (!d) return;
      const x = Math.max(0, Math.min(d.pw - d.w, e.clientX - d.pleft - d.dx));
      const y = Math.max(0, Math.min(d.ph - d.h, e.clientY - d.ptop - d.dy));
      setAnalysisPos({ x, y });
    };
    const onUp = () => { analysisDragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);
  useEffect(() => { if (measureClearNonce) apiRef.current?.clearMeasure?.(); }, [measureClearNonce]);
  const toggleMeasure = useCallback(() => {
    const next = !useStudio.getState().measureActive;
    if (next) useStudio.getState().setAreaFocus(null); // measuring exits Drafting Mode
    useStudio.getState().setMeasureActive(next);
  }, []);
  const [hoverInfo, setHoverInfo] = useState(null); // { tag, area } for the semantic badge
  // Safety auto-hide: the hover/stamp status pill must never get "stuck". It refreshes on every
  // pointer move (new object each time, resetting this timer), and clears ~2.5s after the last update.
  useEffect(() => {
    if (!hoverInfo) return undefined;
    const t = setTimeout(() => setHoverInfo(null), 2500);
    return () => clearTimeout(t);
  }, [hoverInfo]);
  const [identify, setIdentify] = useState(null); // passive face-ID popup { tag, kind, x, y }

  // Active ring drives the hover-overlay color + click behavior
  const rootPillar = useStudio((s) => s.rootPillar);
  const rootPillarRef = useRef(rootPillar);
  const placeResource = useStudio((s) => s.placeResource);
  const placeResourceRef = useRef(placeResource);
  useEffect(() => { placeResourceRef.current = placeResource; }, [placeResource]);
  const northOffset = useStudio((s) => s.northOffset);
  const northOffsetRef = useRef(northOffset);
  useEffect(() => { northOffsetRef.current = northOffset; apiRef.current?.applyNorth?.(northOffset); apiRef.current?.rebakeAnalysis?.(); }, [northOffset]);

  // Section 6 — Analysis Mode state (ring-synced heatmap + flex controls)
  const analysisMode = useStudio((s) => s.analysisMode);
  const season = useStudio((s) => s.season);
  const dliThreshold = useStudio((s) => s.dliThreshold);
  const areaThreshold = useStudio((s) => s.areaThreshold);
  const siteDliOverride = useStudio((s) => s.siteDliOverride);
  const geo = useStudio((s) => s.geo);
  const weather = useStudio((s) => s.weather);
  const setSeason = useStudio((s) => s.setSeason);
  const setDliThreshold = useStudio((s) => s.setDliThreshold);
  const setAreaThreshold = useStudio((s) => s.setAreaThreshold);
  const setSiteDliOverride = useStudio((s) => s.setSiteDliOverride);
  const analysisRef = useRef(analysisMode);
  const seasonRef = useRef(season);
  const dliThreshRef = useRef(dliThreshold);
  const areaThreshRef = useRef(areaThreshold);
  const siteDliOverrideRef = useRef(siteDliOverride);
  const geoLatRef = useRef(geo?.lat);
  const weatherRef = useRef(weather);
  useEffect(() => { analysisRef.current = analysisMode; apiRef.current?.applyAnalysis?.(); }, [analysisMode]);
  useEffect(() => { seasonRef.current = season; apiRef.current?.rebakeAnalysis?.(); }, [season]);
  useEffect(() => { dliThreshRef.current = dliThreshold; if (analysisMode) apiRef.current?.updateHeatUniforms?.(); }, [dliThreshold, analysisMode]);
  useEffect(() => { areaThreshRef.current = areaThreshold; if (analysisMode) apiRef.current?.updateHeatUniforms?.(); }, [areaThreshold, analysisMode]);
  useEffect(() => { siteDliOverrideRef.current = siteDliOverride; if (analysisMode) apiRef.current?.updateHeatUniforms?.(); }, [siteDliOverride, analysisMode]);
  useEffect(() => { geoLatRef.current = geo?.lat; apiRef.current?.rebakeAnalysis?.(); }, [geo]);
  useEffect(() => { weatherRef.current = weather; if (analysisMode) apiRef.current?.updateHeatUniforms?.(); }, [weather, analysisMode]);
  // Recompute real context-shade whenever geometry readiness, North, season or site changes.
  useEffect(() => {
    if (model.status !== "ready") return;
    const t = setTimeout(() => apiRef.current?.computeContextShade?.(), 350);
    return () => clearTimeout(t);
  }, [model.status, northOffset, season, geo]);
  const hoveringRef = useRef(false);
  const hoverKeyRef = useRef(null);
  const hoverTagRef = useRef(null);
  // Phase 2 — armed 3D interaction tool (Location surface-pick / Systems volume-pick). Building
  // is inert until this (or an armed system for drafting) is set.
  const pickTool = useStudio((s) => s.pickTool);
  const setPickToolStore = useStudio((s) => s.setPickTool);
  const pickToolRef = useRef(pickTool);
  useEffect(() => {
    pickToolRef.current = pickTool;
    if (!pickTool) { apiRef.current?.clearHoverNow?.(); }
    if (rendererRef.current) rendererRef.current.domElement.style.cursor = pickTool ? "crosshair" : "";
  }, [pickTool]);
  const allocDraw = useStudio((s) => s.allocDraw);
  const allocDrawRef = useRef(allocDraw);
  useEffect(() => {
    allocDrawRef.current = allocDraw;
    if (rendererRef.current) rendererRef.current.domElement.style.cursor = allocDraw ? "crosshair" : (pickTool ? "crosshair" : "");
  }, [allocDraw, pickTool]);
  const draftDrawMode = useStudio((s) => s.draftDrawMode);
  const draftModeRef = useRef(draftDrawMode);
  useEffect(() => { draftModeRef.current = draftDrawMode; apiRef.current?.cancelDraftPoly?.(); }, [draftDrawMode]);

  const blockResize = useStudio((s) => s.blockResize);
  useEffect(() => { if (blockResize) apiRef.current?.resizeBlock?.(blockResize); }, [blockResize]);
  const blockRotate = useStudio((s) => s.blockRotate);
  useEffect(() => { if (blockRotate) apiRef.current?.rotateBlock?.(blockRotate); }, [blockRotate]);
  const blockXformMode = useStudio((s) => s.blockXformMode);
  const setBlockXformMode = useStudio((s) => s.setBlockXformMode);
  const blockXformModeRef = useRef(blockXformMode);
  useEffect(() => { blockXformModeRef.current = blockXformMode; apiRef.current?.applyXformMode?.(); }, [blockXformMode]);
  const rendererRef = useRef(null);
  const modelStatusRef = useRef(model.status);
  useEffect(() => { modelStatusRef.current = model.status; }, [model.status]);
  const modelScaleRef = useRef(20); // largest model dimension (m) — drives scale-relative offsets
  useEffect(() => {
    rootPillarRef.current = rootPillar;
    const ov = apiRef.current?.hoverOverlay;
    if (ov && hoveringRef.current) ov.material.color.setHex(RING_HEX[rootPillar] || 0x0a84ff);
    if (analysisRef.current) apiRef.current?.updateHeatUniforms?.();
    apiRef.current?.applyRingScope?.(rootPillar); // Fix #2: contextual ring-scoped visibility
  }, [rootPillar]);
  useEffect(() => { onSurfaceTagRef.current = onSurfaceTag; }, [onSurfaceTag]);
  const onNotifyRef = useRef(onNotify);
  useEffect(() => { onNotifyRef.current = onNotify; }, [onNotify]);
  const onAllocAreaRef = useRef(onAllocArea);
  useEffect(() => { onAllocAreaRef.current = onAllocArea; }, [onAllocArea]);

  // §10/§11 — scenario faces (persistent selection + performance gradient + footprint)
  const scenarioFaces = useStudio((s) => s.scenarioFaces);
  const activeFaceId = useStudio((s) => s.activeFaceId);
  const placementRequest = useStudio((s) => s.placementRequest);
  const faceScoresRef = useRef(faceScores);
  useEffect(() => { faceScoresRef.current = faceScores; }, [faceScores]);
  useEffect(() => { apiRef.current?.syncScenarioFaces3D?.(scenarioFaces, faceScores, activeFaceId); }, [scenarioFaces, faceScores, activeFaceId]);
  useEffect(() => {
    if (!placementRequest) return;
    const req = placementRequest;
    // defer store writes out of React's commit phase to avoid cross-component setState warnings
    queueMicrotask(() => {
      const ok = apiRef.current?.extrudeFace?.(req.faceId, req.systemId);
      if (ok) useStudio.getState().updateScenarioFace(req.faceId, { extruded: true, systemId: req.systemId });
      useStudio.getState().clearPlacementRequest();
    });
  }, [placementRequest]);

  // General Entry "Apply" → auto-drop a block of the entered area on the largest roof
  // face, then zero that system's manual area so total area isn't double-counted.
  const autoPlaceRequest = useStudio((s) => s.autoPlaceRequest);
  const processedAutoPlaceRef = useRef(null);
  useEffect(() => {
    if (!autoPlaceRequest) return;
    if (processedAutoPlaceRef.current === autoPlaceRequest._id) return; // idempotent guard
    processedAutoPlaceRef.current = autoPlaceRequest._id;
    const req = autoPlaceRequest;
    queueMicrotask(() => {
      const placed = apiRef.current?.autoPlaceBlock?.(req.systemId, req.area);
      if (placed != null) {
        useStudio.getState().setArea(req.systemId, 0); // block now defines the area
        onNotifyRef.current?.(`Placed ${placed} m² block on the largest rooftop surface`);
      } else {
        onNotifyRef.current?.("No open rooftop surface; area logged as manual entry");
      }
      useStudio.getState().clearAutoPlaceRequest();
    });
  }, [autoPlaceRequest]);

  // Location-ring "Apply" → drop a flat highlighted footprint of the entered area.
  const autoPlaceLocationRequest = useStudio((s) => s.autoPlaceLocationRequest);
  const locationBlocks = useStudio((s) => s.locationBlocks);
  const processedLocPlaceRef = useRef(null);
  useEffect(() => {
    if (!autoPlaceLocationRequest) return;
    if (processedLocPlaceRef.current === autoPlaceLocationRequest._id) return;
    processedLocPlaceRef.current = autoPlaceLocationRequest._id;
    const req = autoPlaceLocationRequest;
    queueMicrotask(() => {
      const placed = apiRef.current?.autoPlaceLocationFootprint?.(req.locationId, req.area);
      if (placed != null) onNotifyRef.current?.(`Dropped ${placed} m² footprint on a valid ${req.locationId.replace("_", " ")} surface`);
      else onNotifyRef.current?.("No matching surface found for that location");
      useStudio.getState().clearAutoPlaceLocationRequest();
    });
  }, [autoPlaceLocationRequest]);
  useEffect(() => { apiRef.current?.syncLocationBlocks?.(locationBlocks); }, [locationBlocks]);

  // ZONE-FIRST LOCK (issue #4 / #3): when the active zone is cleared from the UI (cancel a draft,
  // finish, or press the cancel button), hide the painted zone overlay so no rectangle lingers.
  const activeZone = useStudio((s) => s.activeZone);
  useEffect(() => { if (!activeZone) apiRef.current?.clearActiveZoneVisual?.(); }, [activeZone]);
  const hoveredZone = useStudio((s) => s.hoveredZone);
  useEffect(() => { apiRef.current?.highlightZone?.(hoveredZone); }, [hoveredZone]);

  // Drafted system blocks (Section 7/8) — reconcile 3D meshes with store state
  const systemBlocks = useStudio((s) => s.systemBlocks);
  const activeBlockId = useStudio((s) => s.activeBlockId);
  useEffect(() => { apiRef.current?.syncSystemBlocks?.(systemBlocks); }, [systemBlocks]);
  useEffect(() => { apiRef.current?.setActiveBlock3D?.(activeBlockId); }, [activeBlockId]);
  // Neighborhood scope — recompute gross areas so Context masses pool in (or drop out) immediately,
  // and re-apply the heatmap so context masses are colorized only under Neighborhood scope.
  useEffect(() => { const g = apiRef.current?.computeGross?.(); if (g) useStudio.getState().setGrossAreas(g); if (analysisRef.current) apiRef.current?.applyAnalysis?.(); }, [scope]);

  const toggleLayer = useCallback((index) => {
    const groups = apiRef.current?.layerGroups || {};
    const meshes = groups[index] || [];
    const next = !meshes.every((m) => m.visible);
    meshes.forEach((m) => { m.visible = next; });
    setLayers((prev) => prev.map((l) => (l.index === index ? { ...l, visible: next } : l)));
  }, []);

  // Cycle a layer's role: building (draftable, counts toward opportunity) → context (locked, excluded)
  // → BIA system (pre-accounted existing system, locked & excluded). Re-gates meshes AND recomputes
  // the gross building areas immediately so the Dynamic Opportunity Area reflects the new tagging.
  const toggleLayerRole = useCallback((index) => {
    const roles = apiRef.current?.layerRoles || {};
    const cur = roles[index] || "building";
    const role = cur === "building" ? "context" : cur === "context" ? "bia" : "building";
    const meshes = (apiRef.current?.layerGroups || {})[index] || [];
    meshes.forEach((m) => { m.userData.layerRole = role; m.userData.isBuilding = role === "building"; });
    if (apiRef.current) apiRef.current.layerRoles = { ...roles, [index]: role };
    setLayers((prev) => prev.map((l) => (l.index === index ? { ...l, role } : l)));
    // Only 'building' faces count toward the opportunity envelope → recompute + push to the store,
    // which retriggers the backend Dynamic Opportunity Area calc (App.js effect on grossAreas).
    const g = apiRef.current?.computeGross?.();
    if (g) useStudio.getState().setGrossAreas(g);
    // Re-apply the heatmap so a newly tagged context layer drops its heat color (and vice-versa).
    if (analysisRef.current) apiRef.current?.applyAnalysis?.();
    // Surface the BIA-systems prompt when a layer is first tagged as a pre-accounted system.
    if (role === "bia") setBiaPrompt(true);
  }, []);

  useEffect(() => { highlightRef.current = highlights || []; }, [highlights]);
  useEffect(() => { areaFocusRef.current = areaFocusSystem; }, [areaFocusSystem]);
  useEffect(() => { onSurfaceAreaRef.current = onSurfaceArea; }, [onSurfaceArea]);

  /* ---- Scene setup (once) ---- */
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth || 900;
    let height = mount.clientHeight || 700;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0); // transparent — the CSS gradient shows through
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.localClippingEnabled = true; // Phase 2 — Section Box slicing
    rendererRef.current = renderer;
    // Touch drawing (mobile WebView): without this, a one-finger drag is treated as a page
    // scroll/pan and the browser cancels the pointer stream, so zone/system sketching never fires.
    // `none` lets pointer events flow to both OrbitControls and the draft handlers on touch.
    renderer.domElement.style.touchAction = "none";
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    // Background = subtle CSS gradient on the mount (#E8ECEF -> #F8FAFC); scene stays transparent.

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    // Orthographic (isometric) camera
    const frustum = 30;
    const aspect = width / height;
    const camera = new THREE.OrthographicCamera(-frustum * aspect, frustum * aspect, frustum, -frustum, -1000, 2000);
    camera.position.set(60, 50, 60);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.maxPolarAngle = Math.PI / 2 - 0.03;
    controls.target.set(0, 5, 0);
    controls.autoRotate = false; // off by default — only kicks in after 120s idle
    controls.autoRotateSpeed = 0.45;
    // Idle-driven auto-rotate: any user interaction resets the 60s timer and stops rotation.
    let lastInteract = performance.now();
    const IDLE_ROTATE_MS = 120000;
    const markInteract = () => { lastInteract = performance.now(); controls.autoRotate = false; };
    controls.addEventListener("start", markInteract);
    renderer.domElement.addEventListener("wheel", markInteract, { passive: true });
    renderer.domElement.addEventListener("pointerdown", markInteract);

    // Architectural studio lighting
    const hemi = new THREE.HemisphereLight(0xffffff, 0xdfe0e6, 0.55);
    scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 2.5);
    key.position.set(30, 48, 24);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    Object.assign(key.shadow.camera, { near: 1, far: 400, left: -80, right: 80, top: 80, bottom: -80 });
    key.shadow.bias = -0.0004;
    key.shadow.radius = 6;
    scene.add(key);
    // Rotate the sun azimuth with True North so shadows/solar update live
    const sunBase = new THREE.Vector3(30, 48, 24);
    const applyNorth = (deg) => {
      const a = (deg || 0) * (Math.PI / 180);
      const cos = Math.cos(a), sin = Math.sin(a);
      key.position.set(sunBase.x * cos - sunBase.z * sin, sunBase.y, sunBase.x * sin + sunBase.z * cos);
      key.target.updateMatrixWorld();
    };
    const fill = new THREE.DirectionalLight(0xeaf2ff, 0.4);
    fill.position.set(-26, 16, -22);
    scene.add(fill);

    const ground = new THREE.Mesh(new THREE.CircleGeometry(160, 64), new THREE.MeshStandardMaterial({ color: 0xf0f0f3, roughness: 1, metalness: 0 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    const grid = new THREE.GridHelper(320, 160, 0xdadade, 0xe7e7ea);
    grid.position.y = 0.01;
    grid.material.transparent = true;
    grid.material.opacity = 0.5;
    scene.add(grid);

    const content = new THREE.Group();
    scene.add(content);

    // ---- Manual surface classification: colored overlay group + rectangle-draw preview ----
    const classifyGroup = new THREE.Group();
    classifyGroup.renderOrder = 3;
    scene.add(classifyGroup);
    const classifyPreview = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 })
    );
    classifyPreview.renderOrder = 5; classifyPreview.raycast = () => {};
    scene.add(classifyPreview);
    // Polygon guide overlay: rubber-band edge line + a dot at each placed vertex (first dot is larger
    // so it's an obvious "click here to close" target).
    const classifyPolyGroup = new THREE.Group();
    classifyPolyGroup.renderOrder = 6;
    scene.add(classifyPolyGroup);
    // Region editing: selected placed tag + draggable corner handles.
    const editGroup = new THREE.Group();
    editGroup.renderOrder = 9;
    scene.add(editGroup);
    const editSel = { id: null, kind: null, origin: null, u: null, v: null, n: null, outline: [], snapW: null, faceOutline: null };
    let editDrag = -1; // index of the corner currently being dragged, or -1
    const classifyRect = { active: false };
    // Free-form polygon region draw: locks onto the first-clicked face plane, then collects (u,v) points.
    const classifyPoly = { active: false, pts: [], plane: null, u: null, v: null, n: null, origin: null, snapW: null };
    // Build a world-space filled geometry for a polygon given (u,v) outline points and the face basis.
    function polyGeomWorld(outline, o, u, v) {
      if (!outline || outline.length < 3) return null;
      const g = new THREE.ShapeGeometry(new THREE.Shape(outline)); // outline = Vector2[] in the (u,v) plane
      const w = new THREE.Vector3().crossVectors(u, v).normalize();
      g.applyMatrix4(new THREE.Matrix4().makeBasis(u, v, w).setPosition(o));
      return g;
    }
    // Ray-casting point-in-polygon test on 2D outline points ([a,b] pairs), for zone containment.
    function pointInPoly2D(a, b, outline) {
      let inside = false;
      for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
        const xi = outline[i][0], yi = outline[i][1], xj = outline[j][0], yj = outline[j][1];
        if ((yi > b) !== (yj > b) && a < ((xj - xi) * (b - yi)) / (yj - yi) + xi) inside = !inside;
      }
      return inside;
    }

    function hexInt(cat) { return parseInt((CLASSIFY_HEX[cat] || "#51aafe").slice(1), 16); }
    // The colour id the active classify layer paints with (material id when on the material layer).
    function activeCatColorId() { return classifyLayerRef.current === "material" ? classifyMaterialRef.current : classifyCatRef.current; }

    // Rebuild the classification overlays from the store's surfaceTags (persist-safe: geometry
    // stored as world-space position arrays for faces, or origin+basis+extents for rectangles).
    function syncSurfaceTags(tags) {
      while (classifyGroup.children.length) {
        const c = classifyGroup.children.pop();
        c.geometry?.dispose?.(); c.material?.dispose?.();
      }
      (tags || []).forEach((t) => {
        let geom = null;
        if (t.kind === "face" && t.geo?.positions?.length) {
          geom = new THREE.BufferGeometry();
          geom.setAttribute("position", new THREE.Float32BufferAttribute(t.geo.positions, 3));
          geom.computeVertexNormals();
        } else if (t.kind === "rect" && t.geo?.origin) {
          const o = new THREE.Vector3().fromArray(t.geo.origin);
          const u = new THREE.Vector3().fromArray(t.geo.u);
          const v = new THREE.Vector3().fromArray(t.geo.v);
          const p1 = o.clone().addScaledVector(u, t.geo.w);
          const p2 = p1.clone().addScaledVector(v, t.geo.h);
          const p3 = o.clone().addScaledVector(v, t.geo.h);
          geom = new THREE.BufferGeometry();
          geom.setAttribute("position", new THREE.Float32BufferAttribute([
            o.x, o.y, o.z, p1.x, p1.y, p1.z, p2.x, p2.y, p2.z,
            o.x, o.y, o.z, p2.x, p2.y, p2.z, p3.x, p3.y, p3.z,
          ], 3));
          geom.computeVertexNormals();
        } else if (t.kind === "poly" && t.geo?.outline && t.geo?.origin) {
          const o = new THREE.Vector3().fromArray(t.geo.origin);
          const u = new THREE.Vector3().fromArray(t.geo.u);
          const v = new THREE.Vector3().fromArray(t.geo.v);
          geom = polyGeomWorld(t.geo.outline.map(([a, b]) => new THREE.Vector2(a, b)), o, u, v);
        }
        if (!geom) return;
        const glazed = tagIsGlazed(t), opaque = tagIsOpaque(t);
        // Base type overlay (coloured by surface type). Glazed drops the fill right down so the
        // surface reads see-through; a separate glass sheen is layered on top below.
        const baseId = (t.category && t.category !== "glazed" && t.category !== "opaque") ? t.category : "exterior";
        const base = new THREE.Mesh(geom, new THREE.MeshBasicMaterial({
          color: hexInt(baseId), transparent: true, opacity: glazed ? 0.14 : 0.32, side: THREE.DoubleSide,
          depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
        }));
        base.renderOrder = 3; base.raycast = () => {}; base.userData.tagId = t.id;
        classifyGroup.add(base);
        // Independent material layer: glazed → translucent glass panel; opaque → subtle dark tint.
        if (glazed || opaque) {
          const mg = geom.clone();
          const matMesh = new THREE.Mesh(mg, new THREE.MeshBasicMaterial({
            color: hexInt(glazed ? "glazed" : "opaque"), transparent: true,
            opacity: glazed ? 0.24 : 0.3, side: THREE.DoubleSide,
            depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3,
          }));
          matMesh.renderOrder = 4; matMesh.raycast = () => {}; matMesh.userData.tagId = t.id;
          classifyGroup.add(matMesh);
        }
      });
    }
    function clearClassifyHover() { hoverOverlay.material.opacity = 0; classifyPreview.material.opacity = 0; }

    // Find a manual classification covering a hovered world point (same plane orientation, on the
    // plane, within the tagged region's radius). Lets the identify popup reflect re-classifications.
    function manualTagAt(point, normal) {
      const tags = useStudio.getState().surfaceTags || [];
      let best = null, bestD = Infinity;
      const planeTol = Math.max((modelScaleRef.current || 20) * 0.15, 1.2);
      for (const t of tags) {
        const g = t.geo; if (!g?.center || !g?.normal) continue;
        const n = new THREE.Vector3().fromArray(g.normal);
        if (Math.abs(n.dot(normal)) < 0.85) continue;
        const c = new THREE.Vector3().fromArray(g.center);
        if (Math.abs(point.clone().sub(c).dot(n)) > planeTol) continue;
        const d = point.distanceTo(c);
        if (d <= (g.radius || 3) * 1.1 && d < bestD) { bestD = d; best = t; }
      }
      return best;
    }

    function paintClassifyHover(cf, cat) {
      if (hoverOverlay.geometry && hoverOverlay.geometry !== cf.built.geometry) hoverOverlay.geometry.dispose?.();
      hoverOverlay.geometry = cf.built.geometry;
      hoverOverlay.material.color.setHex(hexInt(cat));
      hoverOverlay.material.opacity = 0.32;
      hoverOverlay.matrix.identity();
    }
    function updateClassifyPreview(p0, p1, u, v, cat) {
      const d = p1.clone().sub(p0);
      const a = d.dot(u), b = d.dot(v);
      const o = p0.clone();
      const c1 = o.clone().addScaledVector(u, a);
      const c2 = c1.clone().addScaledVector(v, b);
      const c3 = o.clone().addScaledVector(v, b);
      const g = classifyPreview.geometry;
      g.setAttribute("position", new THREE.Float32BufferAttribute([
        o.x, o.y, o.z, c1.x, c1.y, c1.z, c2.x, c2.y, c2.z,
        o.x, o.y, o.z, c2.x, c2.y, c2.z, c3.x, c3.y, c3.z,
      ], 3));
      g.attributes.position.needsUpdate = true;
      classifyPreview.material.color.setHex(hexInt(cat));
      classifyPreview.material.opacity = 0.4;
    }
    // Live polygon-region preview: filled shape (≥3 pts) + a rubber-band edge line and a dot at each
    // placed vertex. The FIRST dot is drawn larger/white-ringed so it reads as the "click to close" target.
    function clearPolyGuides() {
      while (classifyPolyGroup.children.length) {
        const c = classifyPolyGroup.children.pop();
        c.geometry?.dispose?.(); c.material?.dispose?.();
      }
    }
    function updateClassifyPolyPreview(cursorW) {
      const { origin: o, u, v } = classifyPoly;
      if (!o) return;
      const col = hexInt(activeCatColorId());
      const vpts = classifyPoly.pts.map((p) => o.clone().addScaledVector(u, p.x).addScaledVector(v, p.y));
      const linePts = vpts.slice();
      if (cursorW) linePts.push(cursorW.clone());

      // Filled area (needs ≥3 points including the cursor)
      const outline = classifyPoly.pts.map((p) => p.clone());
      if (cursorW) { const d = cursorW.clone().sub(o); outline.push(new THREE.Vector2(d.dot(u), d.dot(v))); }
      const g = polyGeomWorld(outline, o, u, v);
      const old = classifyPreview.geometry;
      if (g) { classifyPreview.geometry = g; if (old && old !== g) old.dispose?.(); classifyPreview.material.color.setHex(col); classifyPreview.material.opacity = 0.35; }
      else { classifyPreview.material.opacity = 0; }

      // Edge line + vertex dots
      clearPolyGuides();
      if (linePts.length >= 2) {
        const lg = new THREE.BufferGeometry().setFromPoints(linePts);
        classifyPolyGroup.add(new THREE.Line(lg, new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.95, depthTest: false })));
      }
      const dotR = Math.max((modelScaleRef.current || 20) * 0.012, 0.15);
      vpts.forEach((p, i) => {
        const first = i === 0;
        const dot = new THREE.Mesh(
          new THREE.SphereGeometry(first ? dotR * 1.9 : dotR, 12, 12),
          new THREE.MeshBasicMaterial({ color: first ? 0xffffff : col, depthTest: false })
        );
        dot.position.copy(p); dot.renderOrder = 7; dot.raycast = () => {};
        classifyPolyGroup.add(dot);
        if (first) {
          const ring = new THREE.Mesh(new THREE.SphereGeometry(dotR * 2.4, 14, 14), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.35, depthTest: false }));
          ring.position.copy(p); ring.renderOrder = 6; ring.raycast = () => {};
          classifyPolyGroup.add(ring);
        }
      });
    }
    // World point → client (screen) pixels, for precise "click the first vertex to close" detection.
    function worldToScreen(v3) {
      const rect = renderer.domElement.getBoundingClientRect();
      const p = v3.clone().project(camera);
      return { x: rect.left + (p.x * 0.5 + 0.5) * rect.width, y: rect.top + (-p.y * 0.5 + 0.5) * rect.height };
    }
    function cancelClassifyPoly() {
      classifyPoly.active = false; classifyPoly.pts = []; classifyPoly.plane = null; classifyPoly.origin = null; classifyPoly.snapW = null;
      classifyPreview.material.opacity = 0;
      clearPolyGuides();
      hideSnapDot();
    }
    // ---- Region editing ----
    function clearEditHandles() {
      while (editGroup.children.length) { const c = editGroup.children.pop(); c.geometry?.dispose?.(); c.material?.dispose?.(); }
    }
    function renderEditHandles() {
      clearEditHandles();
      if (!editSel.id || !editSel.outline.length) return;
      const dotR = Math.max((modelScaleRef.current || 20) * 0.014, 0.18);
      editSel.outline.forEach((p, i) => {
        const wp = editSel.origin.clone().addScaledVector(editSel.u, p.x).addScaledVector(editSel.v, p.y);
        const dot = new THREE.Mesh(new THREE.SphereGeometry(dotR, 14, 14), new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false }));
        dot.position.copy(wp); dot.renderOrder = 10; dot.userData.cornerIndex = i;
        editGroup.add(dot);
        const ring = new THREE.Mesh(new THREE.SphereGeometry(dotR * 1.5, 14, 14), new THREE.MeshBasicMaterial({ color: hexInt(editSel.category || "roof"), transparent: true, opacity: 0.4, depthTest: false }));
        ring.position.copy(wp); ring.renderOrder = 9; ring.raycast = () => {};
        editGroup.add(ring);
      });
    }
    function rectOutline(t) {
      // Synthesize the 4 corner points (in u,v) of a rectangle tag so it can be edited as a polygon.
      return [new THREE.Vector2(0, 0), new THREE.Vector2(t.geo.w, 0), new THREE.Vector2(t.geo.w, t.geo.h), new THREE.Vector2(0, t.geo.h)];
    }
    function selectTagForEdit(t) {
      editSel.id = t.id; editSel.kind = t.kind; editSel.category = t.category;
      editSel.faceOutline = t.geo?.faceOutline || null;
      if (t.kind === "rect" || t.kind === "poly") {
        editSel.origin = new THREE.Vector3().fromArray(t.geo.origin);
        editSel.u = new THREE.Vector3().fromArray(t.geo.u);
        editSel.v = new THREE.Vector3().fromArray(t.geo.v);
        editSel.n = new THREE.Vector3().fromArray(t.geo.n || t.geo.normal);
        editSel.outline = (t.kind === "rect") ? rectOutline(t) : t.geo.outline.map(([a, b]) => new THREE.Vector2(a, b));
        // Snap-to-edges: rebuild the underlying face boundary (world) if we cached it at classify time.
        editSel.snapW = editSel.faceOutline
          ? editSel.faceOutline.map(([a, b]) => editSel.origin.clone().addScaledVector(editSel.u, a).addScaledVector(editSel.v, b))
          : null;
      } else {
        editSel.origin = null; editSel.outline = []; editSel.snapW = null; // whole-face tag: delete-only
      }
      renderEditHandles();
      useStudio.getState().setSelectedSurfaceTag(t.id);
      onNotifyRef.current?.(editSel.outline.length ? "Region selected — drag a corner to reshape (snaps to surface edges; hold Alt to free-drag), or press Delete to remove." : "Region selected — press Delete to remove.");
    }
    function clearEditSel() {
      editSel.id = null; editSel.outline = []; editSel.origin = null; editSel.snapW = null; editSel.faceOutline = null; editDrag = -1;
      clearEditHandles();
      hideSnapDot();
      if (useStudio.getState().selectedSurfaceTag) useStudio.getState().setSelectedSurfaceTag(null);
    }
    function persistEditedTag() {
      if (!editSel.id || editSel.outline.length < 3) return;
      const pts = editSel.outline;
      const area = polygonArea(pts);
      const cen = polygonCentroid(pts);
      const centerW = editSel.origin.clone().addScaledVector(editSel.u, cen.x).addScaledVector(editSel.v, cen.y);
      let rad = 0; pts.forEach((p) => { rad = Math.max(rad, Math.hypot(p.x - cen.x, p.y - cen.y)); });
      const o = editSel.origin, u = editSel.u, v = editSel.v, n = editSel.n;
      // Editing always yields a polygon (a dragged rectangle is no longer axis-aligned).
      useStudio.getState().updateSurfaceTag(editSel.id, {
        kind: "poly", area: Math.round(area * 10) / 10,
        geo: { origin: [o.x, o.y, o.z], u: [u.x, u.y, u.z], v: [v.x, v.y, v.z], n: [n.x, n.y, n.z], outline: pts.map((p) => [p.x, p.y]), center: [centerW.x, centerW.y, centerW.z], normal: [n.x, n.y, n.z], radius: rad, faceOutline: editSel.faceOutline || null },
      });
      editSel.kind = "poly";
    }
    function deleteSelectedTag() {
      if (!editSel.id) return;
      useStudio.getState().removeSurfaceTag(editSel.id);
      clearEditSel();
      onNotifyRef.current?.("Region deleted.");
    }
    function commitClassifyPoly() {
      const pts = classifyPoly.pts;
      const cat = classifyCatRef.current;
      if (!pts || pts.length < 3 || cat === "auto") { cancelClassifyPoly(); return; }
      const area = polygonArea(pts);
      const cen = polygonCentroid(pts);
      const { origin: o, u, v, n } = classifyPoly;
      const centerW = o.clone().addScaledVector(u, cen.x).addScaledVector(v, cen.y);
      let rad = 0; pts.forEach((p) => { rad = Math.max(rad, Math.hypot(p.x - cen.x, p.y - cen.y)); });
      // Cache the underlying face boundary (in this tag's u,v) so region-edit can snap to it later.
      const faceOutline = (classifyPoly.snapW && classifyPoly.snapW.length >= 3)
        ? classifyPoly.snapW.map((w) => { const d = w.clone().sub(o); return [d.dot(u), d.dot(v)]; })
        : null;
      if (area > 0.2) {
        const pbucket = bucketFromNormal(n, o, apiRef.current?.modelBounds, northOffsetRef.current);
        const pcls = classifyLayerRef.current === "material"
          ? { category: BUCKET_TO_BASE[pbucket] || "exterior", material: classifyMaterialRef.current }
          : { category: cat, material: null };
        useStudio.getState().addSurfaceTag({
          ...pcls, kind: "poly", area: Math.round(area * 10) / 10, autoBucket: pbucket,
          geo: { origin: [o.x, o.y, o.z], u: [u.x, u.y, u.z], v: [v.x, v.y, v.z], n: [n.x, n.y, n.z], outline: pts.map((p) => [p.x, p.y]), center: [centerW.x, centerW.y, centerW.z], normal: [n.x, n.y, n.z], radius: rad, faceOutline },
        });
        onNotifyRef.current?.(`Region tagged ${pcls.material || pcls.category} · ${Math.round(area)} m²`);
      }
      cancelClassifyPoly();
    }

    // Reusable ring-themed hover highlight (world-space geometry, identity transform)
    const hoverOverlay = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({ color: RING_HEX.locations, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 })
    );
    hoverOverlay.matrixAutoUpdate = false;
    hoverOverlay.renderOrder = 3;
    hoverOverlay.raycast = () => {};
    scene.add(hoverOverlay);

    // Add the draft-window overlay (fill + outline) for on-surface 2D area drafting
    // Draft window — a real PlaneGeometry mounted flush to the target face (Section: strict coplanar anchoring)
    const draftPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ color: RING_HEX.systems, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 })
    );
    draftPlane.renderOrder = 4; draftPlane.raycast = () => {};
    const draftEdge = new THREE.LineSegments(
      new THREE.EdgesGeometry(draftPlane.geometry),
      new THREE.LineBasicMaterial({ color: RING_HEX.systems, transparent: true, opacity: 0 })
    );
    draftEdge.raycast = () => {};
    draftPlane.add(draftEdge); // inherits the flush transform
    scene.add(draftPlane);

    // Persistent ALLOCATION ZONE overlay (Location-ring color) — painted where the user drafts the
    // allocated sub-area, so the following System Footprint is visibly drawn "inside" it.
    const allocZone = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ color: RING_HEX.locations, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1.5, polygonOffsetUnits: -1.5 })
    );
    allocZone.renderOrder = 3; allocZone.raycast = () => {};
    const allocZoneEdge = new THREE.LineSegments(new THREE.EdgesGeometry(allocZone.geometry), new THREE.LineBasicMaterial({ color: RING_HEX.locations, transparent: true, opacity: 0 }));
    allocZoneEdge.raycast = () => {};
    allocZone.add(allocZoneEdge);
    scene.add(allocZone);
    const paintAllocZone = (p0, u, v, n, aC, bC) => {
      const eps = Math.max(modelScaleRef.current * 0.0012, 0.0016);
      allocZone.position.copy(p0).addScaledVector(u, aC / 2).addScaledVector(v, bC / 2).addScaledVector(n, eps);
      _basis.makeBasis(u, v, n); allocZone.quaternion.setFromRotationMatrix(_basis);
      allocZone.scale.set(Math.max(Math.abs(aC), 0.001), Math.max(Math.abs(bC), 0.001), 1);
      allocZone.material.opacity = 0.16; allocZoneEdge.material.opacity = 0.9;
    };
    const hideAllocZone = () => { allocZone.material.opacity = 0; allocZoneEdge.material.opacity = 0; if (allocZonePoly) { allocZonePoly.parent?.remove(allocZonePoly); allocZonePoly.geometry?.dispose?.(); allocZonePoly.material?.dispose?.(); allocZonePoly = null; } };
    // Polygon allocation-zone fill (irregular zones): a flat filled mesh of the drawn outline.
    let allocZonePoly = null;
    const paintAllocZonePoly = (o, u, v, n, outline, color) => {
      hideAllocZone();
      const pts2 = outline.map((p) => (p.isVector2 ? p.clone() : new THREE.Vector2(p[0], p[1])));
      const g = polyGeomWorld(pts2, o, u, v);
      if (!g) return;
      const m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.16, side: THREE.DoubleSide, depthWrite: false }));
      m.renderOrder = 2; m.raycast = () => {};
      content.add(m); allocZonePoly = m;
    };

    // ZONE-FIRST LOCK (issue #4) — geometric bounds of the active allocation zone, normalized to a
    // min-corner origin with positive extents so system footprints can be clamped inside it.
    let activeZoneMeta = null;
    const setActiveZoneMeta = (meta) => {
      if (!meta) { activeZoneMeta = null; return; }
      if (meta.outline && meta.outline.length >= 3) {
        // Polygon zone — clamp initial placement to its bounding box (MOVE is poly-exact via zoneGeoms).
        const xs = meta.outline.map((p) => (p.isVector2 ? p.x : p[0])), ys = meta.outline.map((p) => (p.isVector2 ? p.y : p[1]));
        const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
        const zo = meta.p0.clone().addScaledVector(meta.u, minx).addScaledVector(meta.v, miny);
        activeZoneMeta = { p0: zo, u: meta.u.clone(), v: meta.v.clone(), n: meta.n.clone(), aC: Math.max(maxx - minx, 0.001), bC: Math.max(maxy - miny, 0.001) };
        return;
      }
      let za = meta.aC, zb = meta.bC;
      const zo = meta.p0.clone();
      if (za < 0) { zo.addScaledVector(meta.u, za); za = -za; }
      if (zb < 0) { zo.addScaledVector(meta.v, zb); zb = -zb; }
      activeZoneMeta = { p0: zo, u: meta.u.clone(), v: meta.v.clone(), n: meta.n.clone(), aC: za, bC: zb };
    };
    // Clamp a drafted footprint rectangle {p0,u,v,n,aC,bC} to sit fully inside the active zone.
    const clampToZone = (rect) => {
      const z = activeZoneMeta;
      if (!z) return rect;
      const rel = rect.p0.clone().sub(z.p0);
      let a0 = rel.dot(z.u), b0 = rel.dot(z.v);
      let aC = rect.aC, bC = rect.bC;
      if (aC < 0) { a0 += aC; aC = -aC; }
      if (bC < 0) { b0 += bC; bC = -bC; }
      aC = Math.min(aC, z.aC); bC = Math.min(bC, z.bC);
      a0 = Math.max(0, Math.min(a0, z.aC - aC));
      b0 = Math.max(0, Math.min(b0, z.bC - bC));
      const p0 = z.p0.clone().addScaledVector(z.u, a0).addScaledVector(z.v, b0);
      return { p0, u: z.u.clone(), v: z.v.clone(), n: z.n.clone(), aC, bC };
    };
    const clearActiveZoneVisual = () => { activeZoneMeta = null; hideAllocZone(); };

    // TransformControls (scale) for volumetric equipment blocks
    const tcontrols = new TransformControls(camera, renderer.domElement);
    tcontrols.setMode("scale");
    tcontrols.setSize(0.85);
    scene.add(tcontrols.getHelper());
    tcontrols.addEventListener("dragging-changed", (ev) => { controls.enabled = !ev.value; });
    tcontrols.addEventListener("objectChange", () => {
      const box = tcontrols.object;
      if (!box) return;
      const base = box.userData.base;
      const l = +(base.length * box.scale.x).toFixed(2);
      const w = +(base.width * box.scale.z).toFixed(2);
      const h = +(base.height * box.scale.y).toFixed(2);
      box.position.y = box.userData.surfaceY + h / 2; // keep the base resting on the surface
      const volume = +(l * w * h).toFixed(2);
      const capacity = +(volume * (base.yieldPerM3 || 1)).toFixed(1);
      useStudio.getState().setResourceBlock(box.userData.resourceId, { length: l, width: w, height: h, volume, capacity, unit: base.unit });
    });
    const equipment = { tcontrols, blocks: {} };

    const disposeMesh = (m) => { m.traverse?.((o) => { o.geometry?.dispose?.(); if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((mm) => mm.dispose?.()); }); };
    const clearEquipment = () => {
      tcontrols.detach();
      Object.values(equipment.blocks).forEach((m) => { m.parent?.remove(m); disposeMesh(m); });
      equipment.blocks = {};
    };

    // Spawn / replace a semi-transparent equipment block at a surface point
    const spawnBlock = (resourceId, dims, point) => {
      if (equipment.blocks[resourceId]) { const old = equipment.blocks[resourceId]; old.parent?.remove(old); disposeMesh(old); }
      const geo = new THREE.BoxGeometry(dims.length, dims.height, dims.width);
      const box = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: RING_HEX.resources, transparent: true, opacity: 0.5, roughness: 0.5, metalness: 0.1 }));
      const surfaceY = point.y;
      box.position.set(point.x, surfaceY + dims.height / 2, point.z);
      box.castShadow = true;
      const edge = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: 0x0a7d78 }));
      edge.raycast = () => {};
      box.add(edge);
      box.userData = {
        resourceId,
        surfaceY,
        base: { length: dims.length, width: dims.width, height: dims.height, unit: dims.recovered_unit || "", yieldPerM3: dims.typical_yield || 1 },
      };
      content.add(box);
      equipment.blocks[resourceId] = box;
      tcontrols.attach(box);
      const volume = +(dims.length * dims.width * dims.height).toFixed(2);
      useStudio.getState().setResourceBlock(resourceId, {
        length: dims.length, width: dims.width, height: dims.height, volume,
        capacity: +(volume * (dims.typical_yield || 1)).toFixed(1), unit: dims.recovered_unit || "",
      });
    };

    // ---- Analysis Mode: ring-synced viability heatmap (shared ShaderMaterial) ----
    const heatMat = new THREE.ShaderMaterial({
      uniforms: {
        uRingColor: { value: new THREE.Color(RING_HEX.locations) },
        uLow: { value: new THREE.Color(0xa8b0b8) },
        uMode: { value: 0 },
        uDliSite: { value: 20 },
        uDliThreshold: { value: 12 },
        uAreaThreshold: { value: 40 },
        uTime: { value: 0 },
      },
      vertexShader: HEAT_VERT,
      fragmentShader: HEAT_FRAG,
    });
    const PILLAR_MODE = { locations: 0, systems: 1, crops: 2, resources: 3 };
    const bakeKey = () => `${seasonRef.current}|${Math.round(northOffsetRef.current)}|${Math.round(geoLatRef.current ?? 45)}`;
    const heatRc = new THREE.Raycaster();
    // Bake a per-vertex solar exposure (sun-angle × self-shadow occlusion, averaged over
    // the day's sun path). Recomputed only when the season / north / site changes.
    const bakeExposure = () => {
      const meshes = apiRef.current?.buildingMeshes || [];
      if (!meshes.length) return;
      content.updateMatrixWorld(true);
      const lat = geoLatRef.current ?? 45;
      const samples = daySunSamples(lat, seasonRef.current, northOffsetRef.current);
      const dirs = samples.map((s) => new THREE.Vector3(s.d[0], s.d[1], s.d[2]));
      const weights = samples.map((s) => s.w); // clear-sky beam intensity (sin altitude) per sample
      const wsum = weights.reduce((a, b) => a + b, 0) || 1; // polar night → wsum 1, sum stays 0 → grey
      let total = 0;
      meshes.forEach((m) => { total += m.geometry.attributes.position.count; });
      const doOcc = total <= 6000; // cap raycast cost on very dense imports
      const eps = Math.max(modelScaleRef.current * 0.004, 0.02);
      const wp = new THREE.Vector3(), wn = new THREE.Vector3(), origin = new THREE.Vector3();
      meshes.forEach((mesh) => {
        const g = mesh.geometry;
        if (!g.attributes.normal) g.computeVertexNormals();
        const pos = g.attributes.position, nor = g.attributes.normal, count = pos.count;
        const exp = new Float32Array(count);
        const nmat = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
        for (let i = 0; i < count; i++) {
          wp.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
          wn.fromBufferAttribute(nor, i).applyMatrix3(nmat).normalize();
          let sum = 0;
          dirs.forEach((sd, k) => {
            const d = wn.dot(sd);
            if (d <= 0) return; // faces away from the sun
            let vis = 1;
            if (doOcc) {
              origin.copy(wp).addScaledVector(wn, eps);
              heatRc.set(origin, sd);
              const hits = heatRc.intersectObjects(content.children, true);
              if (hits.length && hits[0].distance > eps) vis = 0; // self-shadowed (incl. context masses)
            }
            sum += d * vis * weights[k]; // weight noon-height sun above low-angle morning/evening
          });
          exp[i] = Math.min(1, sum / wsum);
        }
        g.setAttribute("aExposure", new THREE.BufferAttribute(exp, 1));
      });
      if (apiRef.current) apiRef.current.heatBakedKey = bakeKey();
    };
    // Real contextual overshadowing: the sun-weighted fraction of the building envelope that is
    // shaded SPECIFICALLY by imported CONTEXT masses, traced across the site's sun path (location +
    // True North aware). No context geometry → honest 0. Zero fake math — feeds the opportunity area.
    const shadeRc = new THREE.Raycaster();
    const computeContextShade = () => {
      const meshes = apiRef.current?.buildingMeshes || [];
      const envelope = meshes.filter((m) => m.userData?.layerRole !== "context" && m.geometry?.attributes?.position);
      let hasContext = false;
      content.traverse?.((c) => { if (c.userData?.layerRole === "context") hasContext = true; });
      if (!envelope.length) { useStudio.getState().setContextShade(null); return; }
      if (!hasContext) { useStudio.getState().setContextShade(0); return; } // no neighbours → no context shade
      content.updateMatrixWorld(true);
      const lat = geoLatRef.current ?? 45;
      const samples = daySunSamples(lat, seasonRef.current, northOffsetRef.current);
      const dirs = samples.map((s) => new THREE.Vector3(s.d[0], s.d[1], s.d[2]));
      const weights = samples.map((s) => s.w);
      const eps = Math.max(modelScaleRef.current * 0.004, 0.02);
      const wp = new THREE.Vector3(), wn = new THREE.Vector3(), origin = new THREE.Vector3();
      let totalV = 0; envelope.forEach((m) => { totalV += m.geometry.attributes.position.count; });
      const stride = Math.max(1, Math.round(totalV / 1200)); // cap sample points for responsiveness
      let litW = 0, ctxShadeW = 0;
      envelope.forEach((mesh) => {
        const g = mesh.geometry;
        if (!g.attributes.normal) g.computeVertexNormals();
        const pos = g.attributes.position, nor = g.attributes.normal, count = pos.count;
        const nmat = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
        for (let i = 0; i < count; i += stride) {
          wp.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
          wn.fromBufferAttribute(nor, i).applyMatrix3(nmat).normalize();
          dirs.forEach((sd, k) => {
            const d = wn.dot(sd);
            if (d <= 0) return; // faces away from the sun this sample — not illuminable
            const w = d * weights[k];
            litW += w;
            origin.copy(wp).addScaledVector(wn, eps);
            shadeRc.set(origin, sd);
            const hits = shadeRc.intersectObjects(content.children, true);
            const first = hits.find((h) => h.distance > eps);
            if (first && first.object?.userData?.layerRole === "context") ctxShadeW += w;
          });
        }
      });
      const frac = litW > 0 ? Math.min(0.95, ctxShadeW / litW) : 0;
      useStudio.getState().setContextShade(Math.round(frac * 1000) / 1000);
    };
    const updateHeatUniforms = () => {
      const p = rootPillarRef.current;
      heatMat.uniforms.uRingColor.value.setHex(RING_HEX[p] || RING_HEX.locations);
      heatMat.uniforms.uMode.value = PILLAR_MODE[p] ?? 0;
      // Site DLI: manual override (zero-fake-math input) → real per-season DLI → annual DLI → 0.
      // 0 means "no climate data": crop-viability renders grey instead of an invented number.
      const w = weatherRef.current;
      const seasonal = w?.seasonalDli?.[seasonRef.current];
      const site = siteDliOverrideRef.current ?? (seasonal != null ? seasonal : (w?.dli != null ? w.dli : 0));
      heatMat.uniforms.uDliSite.value = site;
      heatMat.uniforms.uDliThreshold.value = dliThreshRef.current;
      heatMat.uniforms.uAreaThreshold.value = areaThreshRef.current;
    };
    const applyAnalysis = () => {
      const meshes = apiRef.current?.buildingMeshes || [];
      if (analysisRef.current) {
        if (apiRef.current.heatBakedKey !== bakeKey()) bakeExposure();
        updateHeatUniforms();
        // Scope guard: under "building" scope, context masses stay in their neutral clay so the
        // heatmap reads only on the Building/BIA surfaces — but they still cast shadows (the bake
        // occlusion raycasts against every mesh, including context). Neighborhood scope colorizes all.
        const scopeAll = useStudio.getState().scope === "neighborhood";
        meshes.forEach((m) => {
          if (m.material !== heatMat && !m.userData.clayMat) m.userData.clayMat = m.material;
          const colorize = scopeAll || m.userData.layerRole !== "context";
          m.material = colorize ? heatMat : (m.userData.clayMat || m.material);
        });
        controls.autoRotate = false;
      } else {
        meshes.forEach((m) => { if (m.userData.clayMat) m.material = m.userData.clayMat; });
      }
    };
    const rebakeAnalysis = () => { if (analysisRef.current) { bakeExposure(); updateHeatUniforms(); } };

    // ---- Section 7/8: drafted BIA system blocks (parametric extrusion + height arrow) ----
    const sysBlocks = {}; // blockId -> mesh
    // Section 11 — keep the explicit "biaVolumeMeshes" raycast set in sync with the blocks
    const refreshVolumeMeshes = () => { if (apiRef.current) apiRef.current.biaVolumeMeshes = Object.values(sysBlocks); };
    const heightControl = new TransformControls(camera, renderer.domElement);
    heightControl.setMode("translate");
    heightControl.showX = false; heightControl.showZ = false;
    heightControl.setSpace("local");
    heightControl.setSize(0.8);
    scene.add(heightControl.getHelper());
    const heightHandle = new THREE.Object3D();
    scene.add(heightHandle);
    heightControl.addEventListener("dragging-changed", (ev) => { controls.enabled = !ev.value; });
    heightControl.addEventListener("objectChange", () => {
      const id = apiRef.current?.activeBlockId3D;
      const mesh = id && sysBlocks[id];
      if (!mesh) return;
      const ud = mesh.userData;
      const height = Math.max(0.05, heightHandle.position.clone().sub(ud.baseCenter).dot(ud.n));
      mesh.scale.z = height / ud.depth0; // extrude runs 0..depth0 along local +Z (=normal) → base stays put
      heightHandle.position.copy(ud.baseCenter).addScaledVector(ud.n, height);
      useStudio.getState().updateSystemBlock(id, { height: +height.toFixed(2), volume: +(ud.area * height).toFixed(2) });
    });

    // ---- Direct manipulation: MOVE (drag in the surface plane) + RESIZE (push/pull footprint) ----
    // A single extra control switches between translate/scale so only one gizmo is ever attached to
    // the active block → no picking conflict with the height arrow above.
    const moveHandle = new THREE.Object3D();
    scene.add(moveHandle);
    const moveControl = new TransformControls(camera, renderer.domElement);
    moveControl.setMode("translate");
    moveControl.setSpace("local");
    moveControl.showZ = false; // local Z = surface normal → keep the footprint ON the surface; move in X(u)+Y(v)
    moveControl.setSize(0.9);
    scene.add(moveControl.getHelper());
    const scaleControl = new TransformControls(camera, renderer.domElement);
    scaleControl.setMode("scale");
    scaleControl.setSpace("local"); // X = length, Y = width, Z = height (push/pull up)
    scaleControl.setSize(0.9);
    scene.add(scaleControl.getHelper());

    // Locate the allocation-zone polygon this block must stay inside for a Move. Prefers the block's
    // OWN persisted zone polygon (multi-zone, by zoneId); falls back to any drawn location footprint
    // whose plane contains the block's centre. Returns null when the block isn't bound to a zone.
    const zonePolyForBlock = (id, mesh) => {
      const b = id ? useStudio.getState().systemBlocks.find((x) => x.id === id) : null;
      const zid = b ? (b.zoneId || b.locationId) : null;
      const g = zid ? useStudio.getState().zoneGeoms?.[zid] : null;
      if (g && g.outline && g.outline.length >= 3) {
        return {
          origin: new THREE.Vector3().fromArray(g.origin),
          u: new THREE.Vector3().fromArray(g.u),
          v: new THREE.Vector3().fromArray(g.v),
          outline: g.outline.map(([a, bb]) => new THREE.Vector2(a, bb)),
        };
      }
      return findZonePolyFor(mesh);
    };
    const findZonePolyFor = (mesh) => {
      const ud = mesh.userData;
      const base = ud.baseCenter;
      let best = null;
      scenarioFaceMeshes.forEach((rec) => {
        if ((rec.pillar || "locations") !== "locations") return;
        if (!rec.outline || rec.outline.length < 3 || !rec.n) return;
        if (Math.abs(rec.n.dot(ud.n)) < 0.98) return; // must be the same surface orientation
        const d = base.clone().sub(rec.origin);
        if (Math.abs(d.dot(rec.n)) > Math.max((modelScaleRef.current || 20) * 0.08, 1)) return; // ~coplanar
        if (pointInPoly2D(d.dot(rec.u), d.dot(rec.v), rec.outline)) best = { origin: rec.origin.clone(), u: rec.u.clone(), v: rec.v.clone(), outline: rec.outline };
      });
      return best;
    };
    let moveAnchor = null;
    moveControl.addEventListener("dragging-changed", (ev) => {
      controls.enabled = !ev.value;
      const id = apiRef.current?.activeBlockId3D;
      const mesh = id && sysBlocks[id];
      if (ev.value && mesh) {
        const zone = zonePolyForBlock(id, mesh);
        // Only enforce the zone constraint when the block actually STARTS inside it — guards against
        // a mis-registered polygon silently freezing all movement (the reported "gumball won't move").
        let zoneOk = false;
        if (zone) {
          const d0 = mesh.userData.baseCenter.clone().sub(zone.origin);
          zoneOk = pointInPoly2D(d0.dot(zone.u), d0.dot(zone.v), zone.outline);
        }
        moveAnchor = {
          handle0: moveHandle.position.clone(), origin0: mesh.position.clone(),
          base0: mesh.userData.baseCenter.clone(), zone: zoneOk ? zone : null,
          lastDelta: new THREE.Vector3(),
        };
      } else if (mesh && moveAnchor) {
        const b = useStudio.getState().systemBlocks.find((x) => x.id === id);
        if (b?.geom) useStudio.getState().updateSystemBlock(id, { geom: { ...b.geom, origin: mesh.position.toArray() } });
        moveAnchor = null;
      }
    });
    moveControl.addEventListener("objectChange", () => {
      const id = apiRef.current?.activeBlockId3D;
      const mesh = id && sysBlocks[id];
      if (!mesh || !moveAnchor) return;
      const ud = mesh.userData;
      const delta = moveHandle.position.clone().sub(moveAnchor.handle0);
      delta.addScaledVector(ud.n, -delta.dot(ud.n)); // project onto the surface plane (stays on-plane)
      const proposedBase = moveAnchor.base0.clone().add(delta);
      // Zone-bound: keep the system's centre inside its drawn allocation-zone polygon. When it would
      // leave the zone, clamp the gizmo back to the last valid spot (so it stays glued to the mesh)
      // instead of freezing the mesh while the gizmo drifts away.
      const z = moveAnchor.zone;
      if (z) {
        const d2 = proposedBase.clone().sub(z.origin);
        if (!pointInPoly2D(d2.dot(z.u), d2.dot(z.v), z.outline)) {
          moveHandle.position.copy(moveAnchor.handle0).add(moveAnchor.lastDelta);
          return;
        }
      }
      moveAnchor.lastDelta.copy(delta);
      mesh.position.copy(moveAnchor.origin0).add(delta);
      ud.baseCenter.copy(proposedBase);
      heightHandle.position.copy(ud.baseCenter).addScaledVector(ud.n, mesh.scale.z * ud.depth0);
    });

    // Live footprint dimensions from a block's stored geom (rect = aC/bC, poly = outline bounds).
    const footprintLW = (g) => {
      if (g.shape.type === "rect") return { L: g.shape.aC, W: g.shape.bC };
      const xs = g.shape.outline.map((p) => p[0]), ys = g.shape.outline.map((p) => p[1]);
      return { L: Math.max(...xs) - Math.min(...xs), W: Math.max(...ys) - Math.min(...ys) };
    };
    scaleControl.addEventListener("dragging-changed", (ev) => {
      controls.enabled = !ev.value;
      const id = apiRef.current?.activeBlockId3D;
      const mesh = id && sysBlocks[id];
      if (!ev.value && mesh) {
        const b = useStudio.getState().systemBlocks.find((x) => x.id === id);
        if (b?.geom) {
          const { L, W } = footprintLW(b.geom);
          // resizeBlock rebuilds the volume centered on baseCenter and resets mesh scale → clean commit.
          // X→length, Y→width, Z→height (scale.z already encodes height/depth0, so ×depth0 gives metres).
          resizeBlock({
            id,
            length: +(L * mesh.scale.x).toFixed(2),
            width: +(W * mesh.scale.y).toFixed(2),
            height: +Math.max(mesh.scale.z * mesh.userData.depth0, 0.3).toFixed(2),
          });
        }
      }
    });

    const disposeSys = (m) => { m.geometry?.dispose?.(); if (m.material) (Array.isArray(m.material) ? m.material : [m.material]).forEach((mm) => mm.dispose?.()); m.children?.forEach((c) => { c.geometry?.dispose?.(); c.material?.dispose?.(); }); };

    // Attach the gizmo that matches the current transform mode to the active block (or detach all).
    const attachGizmo = (mesh) => {
      heightControl.detach(); moveControl.detach(); scaleControl.detach();
      const volOn = rootPillarRef.current === "systems";
      if (!mesh || !volOn) return;
      const ud = mesh.userData;
      const mode = blockXformModeRef.current === "resize" ? "resize" : "move";
      if (mode === "move") {
        moveHandle.position.copy(ud.baseCenter);
        moveHandle.quaternion.copy(mesh.quaternion); // local X = u (length), Y = v (width), Z = n (normal, hidden)
        moveControl.attach(moveHandle);
      } else {
        mesh.scale.x = 1; mesh.scale.y = 1; // keep scale.z (current height) so the Z handle push/pulls height
        scaleControl.attach(mesh);
      }
    };
    const applyXformMode = () => attachGizmo(apiRef.current?.activeBlockId3D ? sysBlocks[apiRef.current.activeBlockId3D] : null);

    const setActiveBlock3D = (id) => {
      if (apiRef.current) apiRef.current.activeBlockId3D = id;
      // Every drafted volume stays VISIBLE: the active instance renders in its true Systems colour,
      // all deselected instances read as faded grey ghosts. Clicking a faded ghost re-activates it
      // as the main draft (see the volume-select handler on pointer-up).
      Object.entries(sysBlocks).forEach(([bid, mesh]) => {
        const on = bid === id;
        mesh.material.color.setHex(on ? RING_HEX.systems : 0x8b95a1);
        mesh.material.opacity = on ? 0.62 : 0.12;
        if (mesh.userData.edge) { mesh.userData.edge.material.color.setHex(on ? RING_HEX.systems : 0x8b95a1); mesh.userData.edge.material.opacity = on ? 1 : 0.3; }
        mesh.visible = true;
      });
      attachGizmo(id && sysBlocks[id]);
    };

    // "Pick allocation zone" hover: brighten the system volumes that already live in the hovered
    // zone (its location) in the Locations ring colour + fade the rest, so users visually confirm
    // which surface they'd nest into. Passing null restores the normal active/ghost styling.
    const highlightZone = (locationId) => {
      const activeId = apiRef.current?.activeBlockId3D;
      if (!locationId) { setActiveBlock3D(activeId); return; }
      const blocks = useStudio.getState().systemBlocks || [];
      const inZone = new Set(blocks.filter((b) => b.locationId === locationId).map((b) => b.id));
      if (inZone.size === 0) { setActiveBlock3D(activeId); return; }
      Object.entries(sysBlocks).forEach(([bid, mesh]) => {
        const on = inZone.has(bid);
        mesh.material.color.setHex(on ? RING_HEX.locations : 0x8b95a1);
        mesh.material.opacity = on ? 0.85 : 0.06;
        mesh.visible = true;
        if (mesh.userData.edge) {
          mesh.userData.edge.material.color.setHex(on ? RING_HEX.locations : 0x8b95a1);
          mesh.userData.edge.material.opacity = on ? 1 : 0.15;
        }
      });
    };


    // Ring-scoped visibility (Fix #2): face highlights/footprints show only under their own ring;
    // drafted volumes are Systems-ring instances. Hidden meshes are also non-interactive (raycast
    // sets filter on `.visible`), so switching rings declutters the viewport contextually.
    const applyRingScope = (active) => {
      scenarioFaceMeshes.forEach((rec) => {
        const on = (rec.pillar || "locations") === active;
        rec.overlay.visible = on;
        rec.footprint.visible = on && !rec.extruded;
        rec.edge.visible = on && !rec.extruded;
      });
      const activeId = apiRef.current?.activeBlockId3D;
      // Drafted volumes are ALWAYS visible (active = true colour, others faded ghosts) regardless of
      // the active ring — deselected instances no longer vanish. Refresh fade + gizmo for the ring.
      Object.values(sysBlocks).forEach((m) => { m.visible = true; });
      setActiveBlock3D(activeId);
    };

    // Low-level: mount a volume mesh into the scene with a KNOWN id (used by both fresh
    // spawns and .dsymbia rehydration). Does NOT touch the store.
    const mountBlockMesh = (id, systemId, geo, meshOrient, position, area, baseCenter, n, handleOrient, height0, scaleZ = 1) => {
      const hex = RING_HEX.systems;
      const mat = new THREE.MeshPhysicalMaterial({ color: hex, transparent: true, opacity: 0.5, roughness: 0.12, metalness: 0, transmission: 0.6, thickness: 1.2, ior: 1.2, envMapIntensity: 1, side: THREE.DoubleSide });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.quaternion.copy(meshOrient);
      mesh.position.copy(position);
      mesh.scale.z = scaleZ;
      mesh.castShadow = true;
      mesh.renderOrder = 5;
      const edge = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: hex, transparent: true, opacity: 1 }));
      edge.raycast = () => {};
      mesh.add(edge);
      mesh.userData = { blockId: id, systemId, isSystemBlock: true, ringId: "systems", area, depth0: height0, n: n.clone(), baseCenter, handleOrient, edge };
      content.add(mesh);
      sysBlocks[id] = mesh;
      return mesh;
    };

    // Shared: register a NEW extruded volume (rect or exact n-gon) into the scene + store.
    // `geomMeta` is the serializable placement persisted for .dsymbia re-draw.
    const finalizeBlock = (systemId, geo, meshOrient, position, area, baseCenter, n, handleOrient, height0, sys, geomMeta, locationId, opts = {}) => {
      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `blk-${Date.now()}`;
      mountBlockMesh(id, systemId, geo, meshOrient, position, area, baseCenter, n, handleOrient, height0, 1);
      refreshVolumeMeshes();
      // PHASE 3 — a draft/volume created inside a pairing carries the anchored location so the
      // instance is a single Location+System object (auto-completes the pair via addSystemBlock).
      const locId = locationId ?? useStudio.getState().pairDraft?.locationId ?? null;
      const loc = locId ? useStudio.getState().kb?.locations?.find((l) => l.id === locId) : null;
      // 3-tier funnel: carry the in-progress pairing's drawn/typed Allocation Area onto the block.
      // Default when open is origin-aware: LOCATION-anchored → full picked surface; SYSTEM-anchored →
      // the footprint itself. Falls back to footprint if the surface area is unknown.
      const pd = useStudio.getState().pairDraft;
      const draftAlloc = pd?.allocatedArea;
      const surf = locationSurfaceArea(useStudio.getState().grossAreas, locId);
      const openDefault = pd?.origin === "locations" ? (surf > 0 ? +surf.toFixed(1) : area) : area;
      // MULTI-ZONE identity: nest into the active zone ONLY when this footprint is placed inside it
      // (activeZoneMeta present + same location); an explicit picked/stacked zone (pairDraft.zoneId)
      // also nests; otherwise this volume opens its OWN new independent zone (id). No stale lock.
      const azone = useStudio.getState().activeZone;
      const nestInActive = !!activeZoneMeta && azone && azone.locationId === locId;
      const zoneId = nestInActive ? azone.zoneId : (pd?.zoneId || id);
      const zoneCap = nestInActive ? +Number(azone.area).toFixed(1)
        : (pd?.zoneId ? (Number(useStudio.getState().zoneAllocations?.[pd.zoneId]) || 0) : 0);
      const allocatedArea = zoneCap > 0
        ? zoneCap
        : (Number(draftAlloc) > 0 ? +Number(draftAlloc).toFixed(1) : openDefault);
      // Carry the draft's edited "queried database metrics" onto the drawn instance + push to global.
      const FIN_KEYS = ["capex_per_m2", "opex_per_m2_yr"];
      const baseOv = {}, finOv = {};
      Object.entries(pd?.overrides || {}).forEach(([k, v]) => { if (v == null || v === "") return; (FIN_KEYS.includes(k) ? finOv : baseOv)[k] = Number(v); });
      useStudio.getState().addSystemBlock({
        id, zoneId, systemId, systemLabel: sys?.label || systemId, dbSystems: sys?.db_systems || [], ringId: "systems",
        locationId: locId, locationLabel: loc?.label || (locId || null),
        area, allocatedArea, height: +height0.toFixed(2), volume: +(area * height0).toFixed(2), allocations: {}, geom: geomMeta,
        overrides: null, metricOverrides: Object.keys(baseOv).length ? baseOv : null, fromBia: !!opts.fromBia, existing: !!opts.fromBia,
      });
      const st = useStudio.getState();
      if (Object.keys(finOv).length) useStudio.setState({ financialOverrides: { ...st.financialOverrides, [id]: finOv } });
      if (baseOv.structural != null) useStudio.setState({ systemWeightOverride: { ...st.systemWeightOverride, [systemId]: baseOv.structural } });
      setActiveBlock3D(id);
      // Volumes are Systems-ring instances — surface the Systems ring so the new volume is
      // immediately visible (and not hidden by ring-scoping) regardless of the source face's ring.
      if (rootPillarRef.current !== "systems") useStudio.getState().setRoot("systems");
      applyRingScope(rootPillarRef.current);
    };

    // Build & register an extruded volume from a free-hand drafted rectangle {p0,u,v,n,aC,bC}
    const spawnSystemBlock = (systemId, d) => {
      if (!systemId) return;
      const kb = useStudio.getState().kb;
      const sys = kb?.systems?.find((s) => s.id === systemId);
      // Proprietary systems (e.g. P&W | QT POD) enforce a hardcoded footprint & height.
      const ff = sys?.fixed_footprint;
      if (ff) { d = { ...d, aC: ff.length, bC: ff.width }; }
      const height0 = (ff && ff.height) || sys?.default_height || 1;
      const area = Math.max(0.1, Math.round(Math.abs(d.aC * d.bC) * 10) / 10);
      const shape = new THREE.Shape();
      shape.moveTo(0, 0); shape.lineTo(d.aC, 0); shape.lineTo(d.aC, d.bC); shape.lineTo(0, d.bC); shape.closePath();
      const geo = new THREE.ExtrudeGeometry(shape, { depth: height0, bevelEnabled: false });
      const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(d.u, d.v, d.n));
      const w = new THREE.Vector3().crossVectors(d.u, d.n).normalize();
      const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(d.u, d.n, w));
      const baseCenter = d.p0.clone().addScaledVector(d.u, d.aC / 2).addScaledVector(d.v, d.bC / 2);
      const geomMeta = { origin: d.p0.toArray(), u: d.u.toArray(), v: d.v.toArray(), n: d.n.toArray(), shape: { type: "rect", aC: d.aC, bC: d.bC }, height0 };
      finalizeBlock(systemId, geo, meshOrient, d.p0.clone(), area, baseCenter, d.n, handleOrient, height0, sys, geomMeta);
    };

    // Fix #1: extrude the EXACT architectural n-gon. `d.outline` = Vector2[] in (a,b) coords
    // relative to `d.origin` along (u,v); the extrusion follows the true face shape, not a bbox.
    const spawnPolyBlock = (systemId, d) => {
      if (!systemId || !d.outline || d.outline.length < 3) return;
      const kb = useStudio.getState().kb;
      const sys = kb?.systems?.find((s) => s.id === systemId);
      // Proprietary systems ignore the captured n-gon and place their fixed footprint at the centroid.
      const ff = sys?.fixed_footprint;
      if (ff) {
        const c = polygonCentroid(d.outline);
        const p0 = d.origin.clone().addScaledVector(d.u, c.x - ff.length / 2).addScaledVector(d.v, c.y - ff.width / 2);
        spawnSystemBlock(systemId, { p0, u: d.u, v: d.v, n: d.n, aC: ff.length, bC: ff.width });
        return;
      }
      const height0 = sys?.default_height || 1;
      const shape = new THREE.Shape(d.outline);
      const geo = new THREE.ExtrudeGeometry(shape, { depth: height0, bevelEnabled: false });
      const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(d.u, d.v, d.n));
      const w = new THREE.Vector3().crossVectors(d.u, d.n).normalize();
      const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(d.u, d.n, w));
      const cen = polygonCentroid(d.outline);
      const baseCenter = d.origin.clone().addScaledVector(d.u, cen.x).addScaledVector(d.v, cen.y);
      const area = Math.max(0.1, Math.round((d.area || polygonArea(d.outline)) * 10) / 10);
      const geomMeta = { origin: d.origin.toArray(), u: d.u.toArray(), v: d.v.toArray(), n: d.n.toArray(), shape: { type: "poly", outline: d.outline.map((p) => [p.x, p.y]) }, height0 };
      finalizeBlock(systemId, geo, meshOrient, d.origin.clone(), area, baseCenter, d.n, handleOrient, height0, sys, geomMeta);
    };

    // .dsymbia re-draw: reconstruct a volume mesh from a stored block's `geom` (no store write).
    const redrawBlock = (b) => {
      const g = b.geom;
      if (!g || !g.shape || sysBlocks[b.id]) return;
      const origin = new THREE.Vector3().fromArray(g.origin);
      const u = new THREE.Vector3().fromArray(g.u), v = new THREE.Vector3().fromArray(g.v), n = new THREE.Vector3().fromArray(g.n);
      // Mesh type — the saved WORLD-space triangle soup of a tagged folded/irregular solid.
      if (g.shape.type === "mesh" && g.shape.positions?.length) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(g.shape.positions, 3));
        geo.computeVertexNormals();
        geo.computeBoundingBox();
        const bb = geo.boundingBox;
        const baseCenter = new THREE.Vector3((bb.min.x + bb.max.x) / 2, bb.min.y, (bb.min.z + bb.max.z) / 2);
        const w = new THREE.Vector3().crossVectors(u, n).normalize();
        const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, n, w));
        mountBlockMesh(b.id, b.systemId, geo, new THREE.Quaternion(), new THREE.Vector3(0, 0, 0), b.area, baseCenter, n, handleOrient, g.height0, 1);
        return;
      }
      let shape, cen;
      if (g.shape.type === "poly") {
        const pts = g.shape.outline.map((p) => new THREE.Vector2(p[0], p[1]));
        shape = new THREE.Shape(pts);
        cen = polygonCentroid(pts);
      } else {
        shape = new THREE.Shape();
        shape.moveTo(0, 0); shape.lineTo(g.shape.aC, 0); shape.lineTo(g.shape.aC, g.shape.bC); shape.lineTo(0, g.shape.bC); shape.closePath();
        cen = new THREE.Vector2(g.shape.aC / 2, g.shape.bC / 2);
      }
      const geo = new THREE.ExtrudeGeometry(shape, { depth: g.height0, bevelEnabled: false });
      const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, v, n));
      const w = new THREE.Vector3().crossVectors(u, n).normalize();
      const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, n, w));
      const baseCenter = origin.clone().addScaledVector(u, cen.x).addScaledVector(v, cen.y);
      const scaleZ = (b.height || g.height0) / g.height0;
      mountBlockMesh(b.id, b.systemId, geo, meshOrient, origin, b.area, baseCenter, n, handleOrient, g.height0, scaleZ);
    };


    // Phase 2 — rebuild a drafted block as a rectangle L×W×H (manual override or existing-volume tag).
    const resizeBlock = ({ id, length, width, height }) => {
      const b = useStudio.getState().systemBlocks.find((x) => x.id === id);
      if (!b || !b.geom) return;
      const g = b.geom;
      if (g.shape.type === "mesh") return; // folded/irregular solids keep their real geometry — no box resize
      const u = new THREE.Vector3().fromArray(g.u), v = new THREE.Vector3().fromArray(g.v);
      const old = sysBlocks[id];
      const L = Math.max(0.2, +length || 0.2), W = Math.max(0.2, +width || 0.2), H = Math.max(0.2, +height || 0.2);
      let baseCenter;
      if (old) baseCenter = old.userData.baseCenter.clone();
      else {
        const origin = new THREE.Vector3().fromArray(g.origin);
        if (g.shape.type === "poly") { const c = polygonCentroid(g.shape.outline.map((p) => new THREE.Vector2(p[0], p[1]))); baseCenter = origin.clone().addScaledVector(u, c.x).addScaledVector(v, c.y); }
        else baseCenter = origin.clone().addScaledVector(u, g.shape.aC / 2).addScaledVector(v, g.shape.bC / 2);
      }
      let newGeom, area;
      if (g.shape.type === "poly") {
        // Scale the polygon so its bounding box matches the target L×W (keeps the shape), about its centroid.
        const out = g.shape.outline.map((p) => new THREE.Vector2(p[0], p[1]));
        const xs = out.map((p) => p.x), ys = out.map((p) => p.y);
        const curL = Math.max(Math.max(...xs) - Math.min(...xs), 1e-3), curW = Math.max(Math.max(...ys) - Math.min(...ys), 1e-3);
        const cen = polygonCentroid(out);
        const scaled = out.map((p) => new THREE.Vector2(cen.x + (p.x - cen.x) * (L / curL), cen.y + (p.y - cen.y) * (W / curW)));
        const cenNew = polygonCentroid(scaled);
        const newOrigin = baseCenter.clone().addScaledVector(u, -cenNew.x).addScaledVector(v, -cenNew.y);
        newGeom = { origin: newOrigin.toArray(), u: g.u, v: g.v, n: g.n, shape: { type: "poly", outline: scaled.map((p) => [p.x, p.y]) }, height0: H };
        area = +polygonArea(scaled).toFixed(1);
      } else {
        const newOrigin = baseCenter.clone().addScaledVector(u, -L / 2).addScaledVector(v, -W / 2);
        newGeom = { origin: newOrigin.toArray(), u: g.u, v: g.v, n: g.n, shape: { type: "rect", aC: L, bC: W }, height0: H };
        area = +(L * W).toFixed(1);
      }
      if (old) { if (apiRef.current?.activeBlockId3D === id) heightControl.detach(); old.parent?.remove(old); disposeSys(old); delete sysBlocks[id]; }
      const updated = { ...b, area, height: +H.toFixed(2), volume: +(area * H).toFixed(2), geom: newGeom };
      redrawBlock(updated);
      refreshVolumeMeshes();
      useStudio.getState().updateSystemBlock(id, { area: updated.area, height: updated.height, volume: updated.volume, geom: newGeom });
      setActiveBlock3D(id);
      applyRingScope(rootPillarRef.current);
    };

    // Rotate a drafted rect/poly volume in-plane about its surface normal, keeping it centred. Accepts
    // an absolute target `angle` (deg) or a `delta` (deg). Any 5° step is supported (not just 90°).
    const rotateBlock = ({ id, angle, delta }) => {
      const b = useStudio.getState().systemBlocks.find((x) => x.id === id);
      if (!b) return;
      const cur = ((b.rotation || 0) % 360 + 360) % 360;
      const target = angle != null ? ((+angle % 360) + 360) % 360 : ((cur + (delta ?? 90)) % 360 + 360) % 360;
      const step = ((target - cur) % 360 + 360) % 360; // positive in-plane rotation to apply
      // Unplaced (numeric-entry) instance: no 3D mesh — store rotation; swap footprint L/W when the
      // net orientation crosses a 90° parity (so the card dims still reflect a quarter-turn).
      if (!b.geom) {
        const patch = { rotation: target };
        const fp = b.footprint;
        if (fp && (Math.round(cur / 90) % 2) !== (Math.round(target / 90) % 2)) patch.footprint = { length: fp.width, width: fp.length };
        useStudio.getState().updateSystemBlock(id, patch);
        return;
      }
      if (Math.abs(step) < 1e-6) { useStudio.getState().updateSystemBlock(id, { rotation: target }); return; }
      const g = b.geom;
      const u = new THREE.Vector3().fromArray(g.u), v = new THREE.Vector3().fromArray(g.v), n = new THREE.Vector3().fromArray(g.n);
      const old = sysBlocks[id];
      let baseCenter, cen;
      if (g.shape.type === "poly") cen = polygonCentroid(g.shape.outline.map((p) => new THREE.Vector2(p[0], p[1])));
      else if (g.shape.type === "mesh") cen = new THREE.Vector2(0, 0);
      else cen = new THREE.Vector2(g.shape.aC / 2, g.shape.bC / 2);
      if (old) baseCenter = old.userData.baseCenter.clone();
      else {
        const origin = new THREE.Vector3().fromArray(g.origin);
        baseCenter = origin.clone().addScaledVector(u, cen.x).addScaledVector(v, cen.y);
      }
      const rad = step * Math.PI / 180;
      const nu = u.clone().applyAxisAngle(n, rad);
      const nv = v.clone().applyAxisAngle(n, rad);
      const newOrigin = baseCenter.clone().addScaledVector(nu, -cen.x).addScaledVector(nv, -cen.y);
      const newGeom = { origin: newOrigin.toArray(), u: nu.toArray(), v: nv.toArray(), n: g.n, shape: g.shape, height0: g.height0 };
      if (old) { if (apiRef.current?.activeBlockId3D === id) heightControl.detach(); old.parent?.remove(old); disposeSys(old); delete sysBlocks[id]; }
      redrawBlock({ ...b, geom: newGeom, rotation: target });
      refreshVolumeMeshes();
      useStudio.getState().updateSystemBlock(id, { geom: newGeom, rotation: target });
      setActiveBlock3D(id);
      applyRingScope(rootPillarRef.current);
    };

    // Phase 2/3 — tag an existing imported mesh as the armed BIA system: build a box volume that
    // ACCURATELY wraps the mesh's world AABB (min→max on all axes), extruded to its true height in
    // one shot (no default-height + resize dance that distorted imported/surface meshes). Carries
    // the pairing's anchored location so the instance is a single Location+System object.
    // Hotfix 5.0 §3e — tag the CLICKED element as a volume that hugs its real geometry: isolate the
    // connected component under the clicked face (so merged neighbours aren't swallowed) and fit an
    // ORIENTED footprint box (PCA on the XZ projection) so it no longer deforms around a world AABB.
    // Falls back to the whole-object world AABB if a component/face isn't available.
    const tagExistingVolume = (systemId, mesh, locationId, face) => {
      if (!systemId || !mesh) return;
      const kb = useStudio.getState().kb;
      const sys = kb?.systems?.find((s) => s.id === systemId);

      // Preserve the ACTUAL picked solid (folded / sloped / irregular) by cloning its real triangle
      // geometry — not a convex-hull prism — so the tagged volume matches the massing. Footprint
      // area comes from the ground-projection hull; height is the volume-equivalent (true volume /
      // footprint) so the stored volume is honest even on a folded form.
      const comp = (!sys?.fixed_footprint && face) ? componentWorldTris(mesh, face) : null;
      if (comp && comp.verts.length >= 3 && comp.positions.length <= 120000) {
        const n = new THREE.Vector3(0, 1, 0);
        const u = new THREE.Vector3(1, 0, 0);
        const v = new THREE.Vector3().crossVectors(n, u).normalize();
        const hull = convexHullXZ(comp.verts);
        if (hull.length >= 3) {
          let minX = Infinity, maxZ = -Infinity;
          hull.forEach((p) => { if (p.x < minX) minX = p.x; if (p.y > maxZ) maxZ = p.y; });
          const outline = hull.map((p) => new THREE.Vector2(p.x - minX, maxZ - p.y));
          const area = Math.max(0.1, polygonArea(outline));
          const cen = polygonCentroid(outline);
          const H = Math.max(0.2, comp.yMax - comp.yMin);
          const vol = comp.volume > 0.01 ? comp.volume : area * H;
          const height0 = Math.max(0.2, vol / area); // volume-equivalent height (honest volume)
          // The geometry is already in WORLD space → mount at identity (origin 0, no rotation).
          const geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.Float32BufferAttribute(comp.positions, 3));
          geo.computeVertexNormals();
          const baseCenter = new THREE.Vector3(minX, comp.yMin, maxZ).addScaledVector(u, cen.x).addScaledVector(v, cen.y);
          const w = new THREE.Vector3().crossVectors(u, n).normalize();
          const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, n, w));
          const geomMeta = { origin: [0, 0, 0], u: u.toArray(), v: v.toArray(), n: n.toArray(), shape: { type: "mesh", positions: Array.from(comp.positions) }, height0 };
          finalizeBlock(systemId, geo, new THREE.Quaternion(), new THREE.Vector3(0, 0, 0), +area.toFixed(1), baseCenter, n, handleOrient, height0, sys, geomMeta, locationId);
          onNotifyRef.current?.(`Volume tagged: ${Math.round(area)} m² footprint · ${Math.round(vol)} m³`);
          return;
        }
      }

      const verts = componentWorldVerts(mesh, face);
      if (verts && verts.length >= 3) {
        // Fallback (very dense mesh or proprietary fixed-footprint system): extrude the convex-hull
        // footprint of the picked component's ground projection from its true base to its top height.
        const n = new THREE.Vector3(0, 1, 0);
        const u = new THREE.Vector3(1, 0, 0);
        const v = new THREE.Vector3().crossVectors(n, u).normalize(); // (0,0,-1) — right-handed {u,v,n} so the extrude maps base→up, not inverted
        let yMin = Infinity, yMax = -Infinity;
        verts.forEach((p) => { if (p.y < yMin) yMin = p.y; if (p.y > yMax) yMax = p.y; });
        const H = Math.max(0.2, yMax - yMin);
        const hull = convexHullXZ(verts); // 2D hull points: { x: worldX, y: worldZ }
        if (hull.length < 3) return;
        let minX = Infinity, maxZ = -Infinity;
        hull.forEach((p) => { if (p.x < minX) minX = p.x; if (p.y > maxZ) maxZ = p.y; });
        // Project each hull vertex into the (u,v) frame: a = worldX·u = worldX-minX; b = worldZ·v = maxZ-worldZ (v points −Z).
        const outline = hull.map((p) => new THREE.Vector2(p.x - minX, maxZ - p.y));
        const area = polygonArea(outline);
        const cen = polygonCentroid(outline);
        const p0 = new THREE.Vector3(minX, yMin, maxZ);
        const shape = new THREE.Shape(outline);
        const geo = new THREE.ExtrudeGeometry(shape, { depth: H, bevelEnabled: false });
        const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, v, n));
        const w = new THREE.Vector3().crossVectors(u, n).normalize();
        const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, n, w));
        const baseCenter = p0.clone().addScaledVector(u, cen.x).addScaledVector(v, cen.y);
        const geomMeta = { origin: p0.toArray(), u: u.toArray(), v: v.toArray(), n: n.toArray(), shape: { type: "poly", outline: outline.map((p) => [p.x, p.y]) }, height0: H };
        finalizeBlock(systemId, geo, meshOrient, p0.clone(), +area.toFixed(1), baseCenter, n, handleOrient, H, sys, geomMeta, locationId);
        onNotifyRef.current?.(`Volume tagged: ${Math.round(area)} m² · ${Math.round(H)} m tall`);
        return;
      }
      // Fallback: whole-object world AABB (rectangle) when a component/face isn't resolvable.
      const box = new THREE.Box3().setFromObject(mesh);
      if (box.isEmpty()) return;
      const size = new THREE.Vector3(); box.getSize(size);
      const center = new THREE.Vector3(); box.getCenter(center);
      const u = new THREE.Vector3(1, 0, 0), n = new THREE.Vector3(0, 1, 0);
      const v = new THREE.Vector3().crossVectors(n, u).normalize(); // (0,0,-1) — right-handed {u,v,n}
      const L = Math.max(0.2, size.x), W = Math.max(0.2, size.z), H = Math.max(0.2, size.y);
      const p0 = new THREE.Vector3(center.x - L / 2, box.min.y, center.z + W / 2);
      const shape = new THREE.Shape();
      shape.moveTo(0, 0); shape.lineTo(L, 0); shape.lineTo(L, W); shape.lineTo(0, W); shape.closePath();
      const geo = new THREE.ExtrudeGeometry(shape, { depth: H, bevelEnabled: false });
      const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, v, n));
      const w = new THREE.Vector3().crossVectors(u, n).normalize();
      const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, n, w));
      const baseCenter = p0.clone().addScaledVector(u, L / 2).addScaledVector(v, W / 2);
      const geomMeta = { origin: p0.toArray(), u: u.toArray(), v: v.toArray(), n: n.toArray(), shape: { type: "rect", aC: L, bC: W }, height0: H };
      finalizeBlock(systemId, geo, meshOrient, p0.clone(), +(L * W).toFixed(1), baseCenter, n, handleOrient, H, sys, geomMeta, locationId);
      onNotifyRef.current?.(`Volume tagged: ${Math.round(L * W)} m² · ${Math.round(H)} m tall`);
    };

    // Optimization mode — convert every mesh tagged "BIA System" into a MEASURED but UNASSIGNED
    // draft card (real footprint/height/volume from its geometry). Locations are inferred from the
    // block's base height (on ground plane → ground, elevated → rooftop). The user then defines the
    // system, allocation & crops per card. Returns the number of blocks created.
    const spawnBiaBlocksFromLayer = () => {
      const all = apiRef.current?.buildingMeshes || [];
      const meshes = all.filter((m) => m && m.userData?.layerRole === "bia" && m.geometry?.attributes?.position);
      if (!meshes.length) { onNotifyRef.current?.("No BIA System layer to measure."); return 0; }
      const kb = useStudio.getState().kb;
      // Site ground reference — lowest point across all non-context meshes.
      let groundY = Infinity;
      all.forEach((m) => {
        if (!m || m.userData?.layerRole === "context") return;
        const bb = new THREE.Box3().setFromObject(m); if (!bb.isEmpty()) groundY = Math.min(groundY, bb.min.y);
      });
      if (!isFinite(groundY)) groundY = 0;
      const gtol = Math.max((modelScaleRef.current || 20) * 0.03, 0.5);
      let count = 0;
      meshes.forEach((mesh) => {
        mesh.updateWorldMatrix(true, false);
        const pos = mesh.geometry.attributes.position, mw = mesh.matrixWorld;
        const verts = []; const tmp = new THREE.Vector3();
        for (let i = 0; i < pos.count; i++) verts.push(tmp.fromBufferAttribute(pos, i).clone().applyMatrix4(mw));
        if (verts.length < 3) return;
        const axes = principalAxesXZ(verts);
        const u = axes.u, n = new THREE.Vector3(0, 1, 0);
        const v = new THREE.Vector3().crossVectors(n, u).normalize(); // right-handed {u,v,n} — avoids inverted (base-up) extrusion
        const c = new THREE.Vector3(); verts.forEach((p) => c.add(p)); c.multiplyScalar(1 / verts.length);
        let uMin = Infinity, uMax = -Infinity, wMin = Infinity, wMax = -Infinity, yMin = Infinity, yMax = -Infinity;
        verts.forEach((p) => {
          const d = p.clone().sub(c); const du = d.dot(u), dw = d.dot(v);
          if (du < uMin) uMin = du; if (du > uMax) uMax = du;
          if (dw < wMin) wMin = dw; if (dw > wMax) wMax = dw;
          if (p.y < yMin) yMin = p.y; if (p.y > yMax) yMax = p.y;
        });
        const L = Math.max(0.2, uMax - uMin), W = Math.max(0.2, wMax - wMin), H = Math.max(0.2, yMax - yMin);
        const footCenter = c.clone().addScaledVector(u, (uMin + uMax) / 2).addScaledVector(v, (wMin + wMax) / 2);
        footCenter.y = yMin;
        const p0 = footCenter.clone().addScaledVector(u, -L / 2).addScaledVector(v, -W / 2);
        const shape = new THREE.Shape();
        shape.moveTo(0, 0); shape.lineTo(L, 0); shape.lineTo(L, W); shape.lineTo(0, W); shape.closePath();
        const geo = new THREE.ExtrudeGeometry(shape, { depth: H, bevelEnabled: false });
        const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, v, n));
        const w2 = new THREE.Vector3().crossVectors(u, n).normalize();
        const handleOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, n, w2));
        const baseCenter = p0.clone().addScaledVector(u, L / 2).addScaledVector(v, W / 2);
        const geomMeta = { origin: p0.toArray(), u: u.toArray(), v: v.toArray(), n: n.toArray(), shape: { type: "rect", aC: L, bC: W }, height0: H };
        const locId = (yMin - groundY) <= gtol ? "ground" : "rooftop";
        const placeholder = { label: "Existing system (define)", db_systems: [] };
        finalizeBlock(null, geo, meshOrient, p0.clone(), +(L * W).toFixed(1), baseCenter, n, handleOrient, H, placeholder, geomMeta, locId, { fromBia: true });
        count++;
      });
      if (count) onNotifyRef.current?.(`Optimization mode — measured ${count} existing system${count === 1 ? "" : "s"} into draft cards`);
      return count;
    };

    // Phase 2 — Section Box: cfg = { active, x, y, z } with x/y/z ∈ [0..1] (1 = no cut on that axis).
    const setSectionBox = (cfg) => {
      section.active = !!cfg.active;
      const box = new THREE.Box3();
      (apiRef.current?.buildingMeshes || []).forEach((m) => { if (m.userData?.layerRole !== "context") box.expandByObject(m); });
      const mats = [];
      (apiRef.current?.buildingMeshes || []).forEach((m) => { (Array.isArray(m.material) ? m.material : [m.material]).forEach((mm) => mm && mats.push(mm)); });
      if (!section.active || box.isEmpty()) {
        mats.forEach((mm) => { mm.clippingPlanes = null; mm.needsUpdate = true; });
        return;
      }
      const size = new THREE.Vector3(); box.getSize(size);
      // Axis mapping to match the CAD/architectural convention shown in the UI (Z = up/height).
      // three.js is Y-up, so the "Z" slider drives the vertical plane and "Y" drives plan depth.
      clipPlanes[0].constant = box.min.x + Math.max(0.001, cfg.x ?? 1) * size.x; // X — plan width
      clipPlanes[2].constant = box.min.z + Math.max(0.001, cfg.y ?? 1) * size.z; // Y slider — plan depth (three.js Z)
      clipPlanes[1].constant = box.min.y + Math.max(0.001, cfg.z ?? 1) * size.y; // Z slider — height (three.js Y)
      mats.forEach((mm) => { mm.clippingPlanes = clipPlanes; mm.clipIntersection = false; mm.needsUpdate = true; });
    };

    // Reconcile scene meshes with the store's block list (handles deletes from the panel)
    const syncSystemBlocks = (blocks) => {
      const ids = new Set(blocks.map((b) => b.id));
      Object.keys(sysBlocks).forEach((bid) => {
        if (!ids.has(bid)) {
          const m = sysBlocks[bid];
          if (apiRef.current?.activeBlockId3D === bid) heightControl.detach();
          m.parent?.remove(m); disposeSys(m); delete sysBlocks[bid];
        }
      });
      refreshVolumeMeshes();
      applyRingScope(rootPillarRef.current);
    };

    const clearSystemBlocks3D = () => {
      heightControl.detach(); moveControl.detach(); scaleControl.detach();
      Object.values(sysBlocks).forEach((m) => { m.parent?.remove(m); disposeSys(m); });
      Object.keys(sysBlocks).forEach((k) => delete sysBlocks[k]);
      refreshVolumeMeshes();
    };

    const clock = new THREE.Clock();
    let raf;
    const render = () => {
      const t = clock.getElapsedTime();
      heatMat.uniforms.uTime.value = t;
      const pulse = 0.45 + Math.sin(t * 2.2) * 0.25;
      const hs = highlightRef.current;
      const hm = apiRef.current?.highlightMeshes || {};
      // Fix #2: legacy per-location highlights are Location-ring geometry — only pulse them
      // while the Location ring is active, so they hide when the user switches rings.
      const locActive = rootPillarRef.current === "locations";
      Object.entries(hm).forEach(([k, mesh]) => { if (mesh) mesh.material.opacity = (locActive && hs.includes(k)) ? pulse : 0; });
      // gentle pulse on the hover overlay so the targeted surface is unmistakable
      if (apiRef.current?.hoverOverlay && hoveringRef.current && pickToolRef.current?.kind !== "volume") {
        apiRef.current.hoverOverlay.material.opacity = 0.6 + Math.sin(t * 3) * 0.12;
      }
      // auto-rotate only after 60s idle; suppressed while picking/drafting, hovering, analysing or measuring
      const idleRotate = (performance.now() - lastInteract) > IDLE_ROTATE_MS;
      const classifyDrag = classifyModeRef.current && (classifyDrawRef.current === "rect" || editDrag >= 0);
      controls.autoRotate = idleRotate && !areaFocusRef.current && !allocDrawRef.current && !hoveringRef.current && !analysisRef.current && !measureRef.current && !classifyModeRef.current;
      controls.enableRotate = !areaFocusRef.current && !allocDrawRef.current && !classifyDrag;
      controls.enablePan = !classifyDrag;
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(render);
    };
    render();

    const handleResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      if (!width || !height) return;
      const asp = width / height;
      const half = apiRef.current?.fitHalf || frustum;
      camera.left = -half * asp;
      camera.right = half * asp;
      camera.top = half;
      camera.bottom = -half;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    const ro = new ResizeObserver(handleResize);
    ro.observe(mount);
    window.addEventListener("resize", handleResize);

    // Surface pick / hover / draft raycasting
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const nMat = new THREE.Matrix3();
    const plane = new THREE.Plane();
    const tmpP = new THREE.Vector3();
    // Item 3 — "Sky-Check": a vertical raycaster that blocks dragging a footprint under an upper mass.
    const skyRay = new THREE.Raycaster();
    const skyOrigin = new THREE.Vector3();
    const SKY_UP = new THREE.Vector3(0, 1, 0);
    let downXY = null;
    const draft = { active: false, p0: null, u: null, v: null, n: null, aC: 0, bC: 0, hitMesh: null };
    // Multi-point polygon sketch for drafting a SYSTEM footprint or an ALLOCATION ZONE (mirrors the
    // classify polygon tool). alloc=true → zone; alloc=false → system.
    const draftPoly = { active: false, alloc: false, pts: [], plane: null, u: null, v: null, n: null, origin: null, hitMesh: null };
    function updateDraftPolyPreview(cursorW) {
      const { origin: o, u, v } = draftPoly;
      if (!o) return;
      const col = draftPoly.alloc ? RING_HEX.locations : RING_HEX.systems;
      const vpts = draftPoly.pts.map((p) => o.clone().addScaledVector(u, p.x).addScaledVector(v, p.y));
      const linePts = vpts.slice();
      if (cursorW) linePts.push(cursorW.clone());
      const outline = draftPoly.pts.map((p) => p.clone());
      if (cursorW) { const d = cursorW.clone().sub(o); outline.push(new THREE.Vector2(d.dot(u), d.dot(v))); }
      const g = polyGeomWorld(outline, o, u, v);
      const old = classifyPreview.geometry;
      if (g) { classifyPreview.geometry = g; if (old && old !== g) old.dispose?.(); classifyPreview.material.color.setHex(col); classifyPreview.material.opacity = 0.3; }
      else { classifyPreview.material.opacity = 0; }
      clearPolyGuides();
      if (linePts.length >= 2) {
        const lg = new THREE.BufferGeometry().setFromPoints(linePts);
        classifyPolyGroup.add(new THREE.Line(lg, new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.95, depthTest: false })));
      }
      const dotR = Math.max((modelScaleRef.current || 20) * 0.012, 0.15);
      vpts.forEach((p, i) => {
        const first = i === 0;
        const dot = new THREE.Mesh(new THREE.SphereGeometry(first ? dotR * 1.9 : dotR, 12, 12), new THREE.MeshBasicMaterial({ color: first ? 0xffffff : col, depthTest: false }));
        dot.position.copy(p); dot.renderOrder = 7; dot.raycast = () => {};
        classifyPolyGroup.add(dot);
      });
    }
    const cancelDraftPoly = () => {
      draftPoly.active = false; draftPoly.pts = []; draftPoly.plane = null; draftPoly.origin = null;
      classifyPreview.material.opacity = 0; clearPolyGuides();
    };
    const commitDraftPoly = () => {
      if (!draftPoly.active || draftPoly.pts.length < 3) { cancelDraftPoly(); return; }
      const o = draftPoly.origin.clone(), u = draftPoly.u.clone(), v = draftPoly.v.clone(), n = draftPoly.n.clone();
      const outline = draftPoly.pts.map((p) => p.clone());
      const area = Math.round(polygonArea(outline) * 10) / 10;
      const alloc = draftPoly.alloc;
      cancelDraftPoly();
      if (area <= 0.2) return;
      if (alloc) {
        const meta = { p0: o, u, v, n, outline: outline.map((p) => [p.x, p.y]), poly: true, aC: Math.sqrt(area), bC: Math.sqrt(area) };
        paintAllocZonePoly(o, u, v, n, outline, RING_HEX.locations);
        setActiveZoneMeta(meta);
        const st = useStudio.getState();
        const _locId = st.pairDraft?.locationId || st.systemBlocks.find((b) => b.id === st.activeBlockId)?.locationId;
        const _hostZid = st.activeBlockId ? (st.systemBlocks.find((b) => b.id === st.activeBlockId)?.zoneId || st.systemBlocks.find((b) => b.id === st.activeBlockId)?.locationId) : null;
        const _zid = _hostZid || ((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `zone-${Date.now()}`);
        if (st.pairDraft) st.setPairField({ allocatedArea: area, zoneId: _zid });
        else if (st.activeBlockId) st.setBlockAllocated(st.activeBlockId, area);
        st.setZoneAllocation(_zid, area);
        st.setZoneGeom(_zid, { origin: o.toArray(), u: u.toArray(), v: v.toArray(), n: n.toArray(), outline: outline.map((p) => [p.x, p.y]) });
        const _loc = _locId ? st.kb?.locations?.find((l) => l.id === _locId) : null;
        st.setActiveZone({ zoneId: _zid, locationId: _locId || null, area, label: _loc?.label || _locId || "Zone" });
        onAllocAreaRef.current?.(area);
        onNotifyRef.current?.(`Zone set · ${area.toLocaleString()} m². Now place systems inside it (Esc to unlock)`);
        useStudio.getState().setAllocDraw(null);
      } else {
        const sysId = areaFocusRef.current;
        if (sysId) spawnPolyBlock(sysId, { origin: o, u, v, n, outline, area });
        useStudio.getState().setAreaFocus(null);
      }
    };

    const setNdc = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };
    const firstMeshHit = () => {
      raycaster.setFromCamera(ndc, camera);
      return raycaster.intersectObjects(content.children, true).find((h) => h.object.isMesh && h.face);
    };
    // Phase 2 — Section Box clipping planes (X/Y/Z). Visual slice + clip-aware raycasting so the
    // user can click interior floors/walls exposed by the cut.
    const clipPlanes = [
      new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0),
      new THREE.Plane(new THREE.Vector3(0, -1, 0), 0),
      new THREE.Plane(new THREE.Vector3(0, 0, -1), 0),
    ];
    const section = { active: false };
    const clipOK = (p) => !section.active || clipPlanes.every((pl) => pl.distanceToPoint(p) >= -1e-4);

    // Section 11 — explicit two-set raycasting for the interaction state machine.
    // Drafting Mode intersects Building surfaces only; Selection Mode intersects drafted BIA volumes.
    const hitBuildings = () => {
      raycaster.setFromCamera(ndc, camera);
      return raycaster.intersectObjects(apiRef.current?.buildingMeshes || [], false).filter((h) => h.face && h.object.visible !== false && clipOK(h.point));
    };
    const hitVolumes = () => {
      raycaster.setFromCamera(ndc, camera);
      return raycaster.intersectObjects(apiRef.current?.biaVolumeMeshes || [], true).filter((h) => h.face && h.object.visible !== false && clipOK(h.point));
    };
    const worldNormalOf = (hit) => hit.face.normal.clone().applyMatrix3(nMat.getNormalMatrix(hit.object.matrixWorld)).normalize();

    // ---- Measure tool: spot-check import accuracy; click two points, snaps to nearest vertex ----
    const measure = { active: false, pts: [] };
    const snapSphere = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 20), new THREE.MeshBasicMaterial({ color: 0x0a84ff, depthTest: false, transparent: true, opacity: 0.9 }));
    snapSphere.visible = false; snapSphere.renderOrder = 30; snapSphere.raycast = () => {}; scene.add(snapSphere);
    const measureMarkers = [0, 1].map(() => {
      const s = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 20), new THREE.MeshBasicMaterial({ color: 0xff3b30, depthTest: false }));
      s.visible = false; s.renderOrder = 31; s.raycast = () => {}; scene.add(s); return s;
    });
    const measureLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]), new THREE.LineBasicMaterial({ color: 0xff3b30, depthTest: false, transparent: true, opacity: 0.95 }));
    measureLine.visible = false; measureLine.renderOrder = 30; measureLine.raycast = () => {}; scene.add(measureLine);
    const markerScale = () => Math.max(modelScaleRef.current * 0.011, 0.05);
    const measureHits = () => {
      raycaster.setFromCamera(ndc, camera);
      const objs = [...(apiRef.current?.buildingMeshes || []), ...Object.values(sysBlocks)];
      return raycaster.intersectObjects(objs, false).filter((h) => h.face && h.object.visible !== false);
    };
    // Snap the hit to the nearest of the hit face's 3 vertices (corner snap) within ~16px, else raw hit
    const projPx = (p) => { const v = p.clone().project(camera); return new THREE.Vector2((v.x * 0.5 + 0.5) * renderer.domElement.clientWidth, (-v.y * 0.5 + 0.5) * renderer.domElement.clientHeight); };
    // Snap-to-edges: snap a world point (already on the working plane) to the nearest boundary VERTEX
    // (corners + opening corners) or EDGE of a cached world-space outline. Holding Alt disables it.
    const SNAP_VERT_PX = 15, SNAP_EDGE_PX = 10;
    const snapToOutlineW = (pW, outlineW, disabled) => {
      if (disabled || !outlineW || outlineW.length < 2) return { point: pW, snapped: false, kind: null };
      const pPx = projPx(pW);
      let bestV = null, bestVD = SNAP_VERT_PX;
      outlineW.forEach((w) => { const d = projPx(w).distanceTo(pPx); if (d < bestVD) { bestVD = d; bestV = w; } });
      if (bestV) return { point: bestV.clone(), snapped: true, kind: "vertex" };
      let bestE = null, bestED = SNAP_EDGE_PX;
      for (let i = 0; i < outlineW.length; i++) {
        const a = outlineW[i], b = outlineW[(i + 1) % outlineW.length];
        const ab = b.clone().sub(a); const len2 = ab.lengthSq() || 1;
        const t = Math.max(0, Math.min(1, pW.clone().sub(a).dot(ab) / len2));
        const proj = a.clone().addScaledVector(ab, t);
        const d = projPx(proj).distanceTo(pPx);
        if (d < bestED) { bestED = d; bestE = proj; }
      }
      if (bestE) return { point: bestE, snapped: true, kind: "edge" };
      return { point: pW, snapped: false, kind: null };
    };
    // Build the world-space boundary loop of the coplanar face under a hit (for snapping).
    const faceOutlineWorld = (mesh, normal, point) => {
      const cf = coplanarFace(mesh, normal, point, modelScaleRef.current);
      if (!cf || !cf.outline || cf.outline.length < 3) return null;
      return cf.outline.map(([a, b]) => cf.origin.clone().addScaledVector(cf.u, a).addScaledVector(cf.v, b));
    };
    // Dedicated snap indicator (green = snapping to an edge/corner) for the classify + region tools.
    const snapDot = new THREE.Mesh(new THREE.SphereGeometry(1, 18, 18), new THREE.MeshBasicMaterial({ color: 0x34c759, depthTest: false, transparent: true, opacity: 0.95 }));
    snapDot.visible = false; snapDot.renderOrder = 32; snapDot.raycast = () => {}; scene.add(snapDot);
    const showSnapDot = (w) => { snapDot.position.copy(w); snapDot.scale.setScalar(Math.max(modelScaleRef.current * 0.014, 0.06)); snapDot.visible = true; };
    const hideSnapDot = () => { snapDot.visible = false; };
    const snapPoint = (hit) => {
      const pos = hit.object.geometry.attributes.position, f = hit.face;
      const hitPx = projPx(hit.point);
      let best = hit.point.clone(), bestD = 16, snapped = false;
      [f.a, f.b, f.c].forEach((i) => {
        const w = new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(hit.object.matrixWorld);
        const d = projPx(w).distanceTo(hitPx);
        if (d < bestD) { bestD = d; best = w; snapped = true; }
      });
      return { point: best, snapped };
    };
    const setMeasureActive = (on) => { measure.active = on; if (!on) snapSphere.visible = false; };
    const clearMeasure = () => { measure.pts = []; snapSphere.visible = false; measureMarkers.forEach((m) => (m.visible = false)); measureLine.visible = false; setMeasureInfo(null); };
    const measureHover = () => {
      const hit = measureHits()[0];
      if (!hit) { snapSphere.visible = false; return; }
      const { point, snapped } = snapPoint(hit);
      snapSphere.position.copy(point); snapSphere.scale.setScalar(markerScale() * (snapped ? 1.25 : 0.8));
      snapSphere.material.color.setHex(snapped ? 0x0a84ff : 0x86868b); snapSphere.visible = true;
    };
    const measureClick = () => {
      const hit = measureHits()[0];
      if (!hit) return;
      const { point } = snapPoint(hit);
      if (measure.pts.length >= 2) { measure.pts = []; measureMarkers.forEach((m) => (m.visible = false)); measureLine.visible = false; }
      measure.pts.push(point.clone());
      const s = markerScale();
      measure.pts.forEach((p, i) => { measureMarkers[i].position.copy(p); measureMarkers[i].scale.setScalar(s); measureMarkers[i].visible = true; });
      if (measure.pts.length === 2) { measureLine.geometry.setFromPoints(measure.pts); measureLine.visible = true; setMeasureInfo({ dist: measure.pts[0].distanceTo(measure.pts[1]) }); }
      else setMeasureInfo({ dist: null });
    };

    // ---- §10/§11 Scenario faces: persistent selection + performance gradient + parametric footprint ----
    const scenarioFaceMeshes = new Map(); // id -> { overlay, footprint, edge, u, v, n, origin, W, H, totalArea, cur }
    const _fb = new THREE.Matrix4();
    const faceDrag = { active: false, id: null };
    const RING_HEXOF = (p) => RING_HEX[p] || RING_HEX.systems;
    const faceKey = (mesh, n, p) => `${mesh.uuid}:${n.x.toFixed(2)},${n.y.toFixed(2)},${n.z.toFixed(2)}:${n.dot(p).toFixed(1)}`;

    function footprintDims(rec, utilized) {
      const k = Math.sqrt(Math.max(0, Math.min(1, utilized / (rec.W * rec.H))));
      return { w: Math.max(rec.W * k, 0.01), h: Math.max(rec.H * k, 0.01) };
    }
    function updateScenarioFace3D(id, { utilizedArea, offset, pillar, score, active, extruded }) {
      const rec = scenarioFaceMeshes.get(id); if (!rec) return;
      const hex = RING_HEXOF(pillar);
      const perf = Math.max(0, Math.min(1, score ?? 1)); // §10 gradient: opacity ∝ live ring viability
      rec.pillar = pillar; rec.extruded = !!extruded;
      rec.overlay.material.color.setHex(hex);
      rec.overlay.material.opacity = extruded ? 0.14 : (0.2 + 0.3 * perf);
      rec.footprint.material.color.setHex(hex);
      rec.footprint.material.opacity = extruded ? 0 : (0.3 + 0.4 * perf);
      rec.footprint.visible = !extruded; rec.edge.visible = !extruded;
      rec.edge.material.color.setHex(hex); rec.edge.material.opacity = active ? 1 : 0.5;
      const eps = Math.max(modelScaleRef.current * 0.0018, 0.003);
      _fb.makeBasis(rec.u, rec.v, rec.n);
      rec.footprint.quaternion.setFromRotationMatrix(_fb);
      const util = utilizedArea == null ? rec.totalArea : utilizedArea;
      if (rec.hasPoly) {
        // Fix #1: exact-shape footprint — scale the true face polygon about its centroid.
        const k = Math.sqrt(Math.max(0, Math.min(1, util / rec.totalArea)));
        const cx = offset ? offset.a : rec.centroid.x;
        const cy = offset ? offset.b : rec.centroid.y;
        const a = Math.max(0, Math.min(rec.W, cx));
        const b = Math.max(0, Math.min(rec.H, cy));
        rec.footprint.position.copy(rec.origin).addScaledVector(rec.u, a).addScaledVector(rec.v, b).addScaledVector(rec.n, eps * 1.4);
        rec.footprint.scale.set(k, k, 1);
        const poly = rec.outline.map((p) => new THREE.Vector2(a + (p.x - rec.centroid.x) * k, b + (p.y - rec.centroid.y) * k));
        rec.cur = { poly, area: rec.totalArea * k * k, a, b };
      } else {
        const { w, h } = footprintDims(rec, util);
        const off = offset || { a: (rec.W - w) / 2, b: (rec.H - h) / 2 };
        const a = Math.max(0, Math.min(rec.W - w, off.a));
        const b = Math.max(0, Math.min(rec.H - h, off.b));
        rec.footprint.position.copy(rec.origin).addScaledVector(rec.u, a + w / 2).addScaledVector(rec.v, b + h / 2).addScaledVector(rec.n, eps * 1.4);
        rec.footprint.scale.set(w, h, 1);
        rec.cur = { w, h, a, b };
      }
    }
    const addScenarioFace3D = (mesh, worldNormal, worldPoint, pillar) => {
      const cf = coplanarFace(mesh, worldNormal, worldPoint, modelScaleRef.current);
      if (!cf) return null;
      const { built, n, u, v, origin, W, H, outline, centroid, holes } = cf;
      const hex = RING_HEXOF(pillar);
      const hatch = hatchTexture().clone(); hatch.needsUpdate = true; hatch.repeat.set(Math.max(W * 0.5, 1), Math.max(H * 0.5, 1));
      const overlay = new THREE.Mesh(built.geometry, new THREE.MeshBasicMaterial({ color: hex, map: hatch, transparent: true, opacity: 0.4, depthTest: true, depthWrite: false, side: THREE.DoubleSide }));
      overlay.renderOrder = 8; overlay.raycast = () => {}; overlay.userData.ringId = pillar; content.add(overlay);
      // Footprint geometry = the EXACT (clipped) face polygon, centered at its centroid so it
      // scales/drags cleanly. Holes (from occlusion clipping) are preserved. Falls back to a rect.
      const hasPoly = !!(outline && centroid);
      let fpGeo;
      if (hasPoly) {
        const shp = new THREE.Shape(outline.map((p) => p.clone().sub(centroid)));
        (holes || []).forEach((ring) => shp.holes.push(new THREE.Path(ring.map((p) => p.clone().sub(centroid)))));
        fpGeo = new THREE.ShapeGeometry(shp);
      } else {
        fpGeo = new THREE.PlaneGeometry(1, 1);
      }
      const footprint = new THREE.Mesh(fpGeo, new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide }));
      footprint.renderOrder = 10; footprint.userData.ringId = pillar;
      const edge = new THREE.LineSegments(new THREE.EdgesGeometry(fpGeo), new THREE.LineBasicMaterial({ color: hex, transparent: true, opacity: 0.95 }));
      edge.renderOrder = 11; edge.raycast = () => {}; footprint.add(edge); content.add(footprint);
      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `face-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const info = semanticTag(worldNormal, worldPoint, apiRef.current?.modelBounds);
      scenarioFaceMeshes.set(id, { overlay, footprint, edge, u, v, n, origin, W, H, totalArea: built.area, cur: null, hasPoly, outline, centroid, pillar, extruded: false });
      updateScenarioFace3D(id, { utilizedArea: built.area, offset: null, pillar, score: 1, active: true });
      applyRingScope(rootPillarRef.current);
      // Persist placement for .dsymbia re-draw
      const geom = { origin: origin.toArray(), u: u.toArray(), v: v.toArray(), n: n.toArray(), W, H, outline: outline ? outline.map((p) => [p.x, p.y]) : null, centroid: centroid ? [centroid.x, centroid.y] : null, holes: holes ? holes.map((r) => r.map((p) => [p.x, p.y])) : null };
      return { id, pillar, tag: info.tag, totalArea: Math.round(built.area * 10) / 10, utilizedArea: Math.round(built.area * 10) / 10, offset: null, systemId: null, extruded: false, geom };
    };
    // .dsymbia re-draw: rebuild a scenario-face overlay/footprint from a stored face's `geom`.
    const redrawFace = (f) => {
      const g = f.geom;
      if (!g || scenarioFaceMeshes.has(f.id)) return;
      const origin = new THREE.Vector3().fromArray(g.origin);
      const u = new THREE.Vector3().fromArray(g.u), v = new THREE.Vector3().fromArray(g.v), n = new THREE.Vector3().fromArray(g.n);
      const W = g.W, H = g.H;
      const hasPoly = !!(g.outline && g.outline.length >= 3 && g.centroid);
      const outline = hasPoly ? g.outline.map((p) => new THREE.Vector2(p[0], p[1])) : null;
      const centroid = hasPoly ? new THREE.Vector2(g.centroid[0], g.centroid[1]) : null;
      const holes = hasPoly && g.holes ? g.holes.map((r) => r.map((p) => new THREE.Vector2(p[0], p[1]))) : [];
      const hex = RING_HEXOF(f.pillar);
      _fb.makeBasis(u, v, n);
      const orient = new THREE.Quaternion().setFromRotationMatrix(_fb);
      const eps = Math.max(modelScaleRef.current * 0.0018, 0.003);
      let ovShape;
      if (hasPoly) { ovShape = new THREE.Shape(outline); holes.forEach((r) => ovShape.holes.push(new THREE.Path(r))); }
      else ovShape = rectShape(W, H);
      const hatch = hatchTexture().clone(); hatch.needsUpdate = true; hatch.repeat.set(Math.max(W * 0.5, 1), Math.max(H * 0.5, 1));
      const overlay = new THREE.Mesh(new THREE.ShapeGeometry(ovShape), new THREE.MeshBasicMaterial({ color: hex, map: hatch, transparent: true, opacity: 0.4, depthTest: true, depthWrite: false, side: THREE.DoubleSide }));
      overlay.quaternion.copy(orient); overlay.position.copy(origin).addScaledVector(n, eps); overlay.renderOrder = 8; overlay.raycast = () => {}; overlay.userData.ringId = f.pillar; content.add(overlay);
      let fpGeo;
      if (hasPoly) { const shp = new THREE.Shape(outline.map((p) => p.clone().sub(centroid))); holes.forEach((r) => shp.holes.push(new THREE.Path(r.map((p) => p.clone().sub(centroid))))); fpGeo = new THREE.ShapeGeometry(shp); }
      else fpGeo = new THREE.PlaneGeometry(1, 1);
      const footprint = new THREE.Mesh(fpGeo, new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide }));
      footprint.renderOrder = 10; footprint.userData.ringId = f.pillar;
      const edge = new THREE.LineSegments(new THREE.EdgesGeometry(fpGeo), new THREE.LineBasicMaterial({ color: hex, transparent: true, opacity: 0.95 }));
      edge.renderOrder = 11; edge.raycast = () => {}; footprint.add(edge); content.add(footprint);
      scenarioFaceMeshes.set(f.id, { overlay, footprint, edge, u, v, n, origin, W, H, totalArea: f.totalArea, cur: null, hasPoly, outline, centroid, pillar: f.pillar, extruded: f.extruded });
    };
    // Rehydrate the whole scene from the rehydrated store (called once geometry is ready).
    const rehydrateScene = () => {
      const st = useStudio.getState();
      (st.systemBlocks || []).forEach((b) => redrawBlock(b));
      (st.scenarioFaces || []).forEach((f) => redrawFace(f));
      refreshVolumeMeshes();
      syncScenarioFaces3D(st.scenarioFaces || [], faceScoresRef.current, st.activeFaceId);
      if (st.activeBlockId && sysBlocks[st.activeBlockId]) setActiveBlock3D(st.activeBlockId);
      applyRingScope(rootPillarRef.current);
    };
    const removeScenarioFace3D = (id) => {
      const rec = scenarioFaceMeshes.get(id); if (!rec) return;
      [rec.overlay, rec.footprint, rec.edge].forEach((m) => { m.parent?.remove(m); m.geometry?.dispose?.(); m.material?.map?.dispose?.(); m.material?.dispose?.(); });
      scenarioFaceMeshes.delete(id);
    };
    const clearScenarioFaces3D = () => { [...scenarioFaceMeshes.keys()].forEach(removeScenarioFace3D); faceDrag.active = false; };
    const syncScenarioFaces3D = (list, scores, activeId) => {
      const ids = new Set(list.map((f) => f.id));
      [...scenarioFaceMeshes.keys()].forEach((id) => { if (!ids.has(id)) removeScenarioFace3D(id); });
      list.forEach((f) => updateScenarioFace3D(f.id, { utilizedArea: f.utilizedArea, offset: f.offset, pillar: f.pillar, score: scores?.[f.pillar] ?? 1, active: f.id === activeId, extruded: f.extruded }));
      applyRingScope(rootPillarRef.current);
    };
    const extrudeFace = (id, systemId) => {
      const rec = scenarioFaceMeshes.get(id); if (!rec || !rec.cur || !systemId) return false;
      if (rec.hasPoly && rec.cur.poly && rec.cur.poly.length >= 3) {
        spawnPolyBlock(systemId, { outline: rec.cur.poly, origin: rec.origin, u: rec.u, v: rec.v, n: rec.n, area: rec.cur.area });
      } else {
        const { w, h, a, b } = rec.cur;
        const p0 = rec.origin.clone().addScaledVector(rec.u, a).addScaledVector(rec.v, b);
        spawnSystemBlock(systemId, { p0, u: rec.u, v: rec.v, n: rec.n, aC: w, bC: h });
      }
      return true;
    };
    const footprintHit = (id) => { const rec = scenarioFaceMeshes.get(id); if (!rec || !rec.footprint.visible) return null; raycaster.setFromCamera(ndc, camera); return raycaster.intersectObject(rec.footprint, false)[0] || null; };

    const clearHover = () => {
      volumeHoverBox.visible = false;
      if (!hoveringRef.current) return;
      hoveringRef.current = false;
      hoverKeyRef.current = null;
      hoverTagRef.current = null;
      hoverOverlay.material.opacity = 0;
      setHoverInfo(null);
    };

    // Flush-mount the draft PlaneGeometry to the target face from in-plane extents aC × bC
    const _basis = new THREE.Matrix4();
    const updateDraftOverlay = () => {
      const { p0, u, v, n, aC, bC } = draft;
      const eps = Math.max(modelScaleRef.current * 0.0015, 0.002); // scale-relative offset so it never floats
      // center of the rectangle, nudged along the normal so it paints onto the surface
      draftPlane.position.copy(p0).addScaledVector(u, aC / 2).addScaledVector(v, bC / 2).addScaledVector(n, eps);
      // orient flush: local X->u, Y->v, Z->face normal (generalises quaternion.setFromUnitVectors(z, normal))
      _basis.makeBasis(u, v, n);
      draftPlane.quaternion.setFromRotationMatrix(_basis);
      draftPlane.scale.set(Math.max(Math.abs(aC), 0.001), Math.max(Math.abs(bC), 0.001), 1);
      const hex = draft.alloc ? RING_HEX.locations : (RING_HEX[rootPillarRef.current] || RING_HEX.systems);
      draftPlane.material.color.setHex(hex); draftPlane.material.opacity = 0.34;
      draftEdge.material.color.setHex(hex); draftEdge.material.opacity = 0.95;
    };
    const hideDraftOverlay = () => { draftPlane.material.opacity = 0; draftEdge.material.opacity = 0; };

    // Item 2 — "Stamp" mechanic for fixed-dimension systems (e.g. P&W | QT POD). A semi-transparent
    // preview volume follows the cursor; a click stamps it; R / Space rotates the footprint 90°.
    const stampPreview = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: RING_HEX.systems, transparent: true, opacity: 0.35, depthWrite: false })
    );
    stampPreview.visible = false; stampPreview.renderOrder = 6; stampPreview.raycast = () => {};
    stampPreview.add(new THREE.LineSegments(new THREE.EdgesGeometry(stampPreview.geometry), new THREE.LineBasicMaterial({ color: RING_HEX.systems })));
    scene.add(stampPreview);
    // Phase 3 — "Select Existing Volume" hover: a wireframe box that snaps to the whole mesh's
    // AABB so the user clearly sees the SOLID they'll tag (not a surface). Ring-themed.
    const volumeHoverBox = new THREE.Box3Helper(new THREE.Box3(new THREE.Vector3(), new THREE.Vector3(1, 1, 1)), RING_HEX.systems);
    volumeHoverBox.visible = false;
    volumeHoverBox.renderOrder = 12;
    if (volumeHoverBox.material) { volumeHoverBox.material.depthTest = false; volumeHoverBox.material.transparent = true; }
    volumeHoverBox.raycast = () => {};
    scene.add(volumeHoverBox);
    let stampRot = 0; // radians (0 / 90° / …), toggled by R or Space while hovering
    const armedFixed = () => {
      const sid = areaFocusRef.current;
      if (!sid) return null;
      const sys = useStudio.getState().kb?.systems?.find((s) => s.id === sid);
      return sys?.fixed_footprint ? { sys, ff: sys.fixed_footprint } : null;
    };
    const stampBasis = (n) => {
      const up = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      let u = new THREE.Vector3().crossVectors(up, n).normalize();
      let v = new THREE.Vector3().crossVectors(n, u).normalize();
      if (stampRot) {
        const c = Math.cos(stampRot), s = Math.sin(stampRot);
        const u2 = u.clone().multiplyScalar(c).addScaledVector(v, s);
        const v2 = v.clone().multiplyScalar(c).addScaledVector(u, -s);
        u = u2.normalize(); v = v2.normalize();
      }
      return { u, v };
    };
    const updateStampPreview = (ff, hitPoint, n) => {
      const { u, v } = stampBasis(n);
      _basis.makeBasis(u, n, v); // box local X->u (length), Y->n (height), Z->v (width)
      stampPreview.quaternion.setFromRotationMatrix(_basis);
      stampPreview.scale.set(ff.length, ff.height, ff.width);
      stampPreview.position.copy(hitPoint).addScaledVector(n, ff.height / 2);
      stampPreview.visible = true;
      return { u, v };
    };
    const hideStamp = () => { stampPreview.visible = false; };

    // Precision face hover -> semantic tag + ring-themed coplanar highlight
    const onMove = (e) => {
      if (modelStatusRef.current !== "ready") { clearHover(); return; }
      // Region editing: drag a selected corner along the tag plane.
      if (editDrag >= 0 && editSel.id) {
        setNdc(e); raycaster.setFromCamera(ndc, camera);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(editSel.n, editSel.origin);
        const cp = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(plane, cp)) {
          // Snap-to-edges: pull the dragged corner onto the underlying face vertex/edge (Alt = free).
          const snap = snapToOutlineW(cp.clone(), editSel.snapW, e.altKey);
          if (snap.snapped) showSnapDot(snap.point); else hideSnapDot();
          const d = snap.point.clone().sub(editSel.origin);
          editSel.outline[editDrag] = new THREE.Vector2(d.dot(editSel.u), d.dot(editSel.v));
          renderEditHandles();
          const g = polyGeomWorld(editSel.outline, editSel.origin, editSel.u, editSel.v);
          if (g) { const old = classifyPreview.geometry; classifyPreview.geometry = g; if (old && old !== g) old.dispose?.(); classifyPreview.material.color.setHex(hexInt(editSel.category)); classifyPreview.material.opacity = 0.35; }
        }
        return;
      }
      // Manual classification: rectangle-drag preview, or whole-face hover highlight.
      if (classifyPoly.active) {
        setNdc(e); raycaster.setFromCamera(ndc, camera);
        const cp = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(classifyPoly.plane, cp)) {
          const snap = snapToOutlineW(cp.clone(), classifyPoly.snapW, e.altKey);
          if (snap.snapped) showSnapDot(snap.point); else hideSnapDot();
          updateClassifyPolyPreview(snap.point);
        }
        return;
      }
      // Drafting polygon (system/zone) rubber-band preview to the cursor on the locked plane.
      if (draftPoly.active) {
        setNdc(e); raycaster.setFromCamera(ndc, camera);
        const cp = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(draftPoly.plane, cp)) updateDraftPolyPreview(cp);
        return;
      }
      if (classifyRect.active) {
        setNdc(e);
        raycaster.setFromCamera(ndc, camera);
        const cp = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(classifyRect.plane, cp)) updateClassifyPreview(classifyRect.p0, cp, classifyRect.u, classifyRect.v, activeCatColorId());
        return;
      }
      if (classifyModeRef.current) {
        setIdentify(null);
        if (e.buttons !== 0 || classifyDrawRef.current !== "face") { clearClassifyHover(); return; }
        setNdc(e);
        const chit = hitBuildings()[0];
        if (!chit || chit.object.userData.layerRole === "context") { hoverOverlay.material.opacity = 0; return; }
        const cn = worldNormalOf(chit);
        const cf = coplanarFace(chit.object, cn, chit.point, modelScaleRef.current);
        if (cf) paintClassifyHover(cf, activeCatColorId());
        return;
      }
      if (measureRef.current) { setNdc(e); measureHover(); clearHover(); return; } // Measure tool owns the surface
      if (analysisRef.current) { clearHover(); return; } // heatmap owns the surface in Analysis Mode
      if (equipment.tcontrols.dragging || moveControl.dragging || scaleControl.dragging) { clearHover(); return; } // manipulating a block

      // §11 footprint kinematic drag — slide the utilised footprint flush along the face, clamped to bounds
      if (faceDrag.active) {
        setNdc(e);
        const rec = scenarioFaceMeshes.get(faceDrag.id);
        if (rec && rec.cur) {
          raycaster.setFromCamera(ndc, camera);
          plane.setFromNormalAndCoplanarPoint(rec.n, rec.origin);
          if (raycaster.ray.intersectPlane(plane, tmpP)) {
            tmpP.sub(rec.origin);
            // poly footprint drags by centroid position; rectangle by bottom-left corner
            const a = rec.hasPoly ? tmpP.dot(rec.u) : tmpP.dot(rec.u) - rec.cur.w / 2;
            const b = rec.hasPoly ? tmpP.dot(rec.v) : tmpP.dot(rec.v) - rec.cur.h / 2;
            // Item 3 — Sky-Check: on an up-facing surface, fire a ray straight UP from the footprint
            // centroid; if it hits any building mass above, block this move so the user can only
            // draft in the truly exposed / outdoor leftover space (no sliding under upper floors).
            if (rec.n.y > 0.6) {
              const ca = rec.hasPoly ? a : a + rec.cur.w / 2;
              const cb = rec.hasPoly ? b : b + rec.cur.h / 2;
              const lift = Math.max(modelScaleRef.current * 0.02, 0.2);
              skyOrigin.copy(rec.origin).addScaledVector(rec.u, ca).addScaledVector(rec.v, cb).addScaledVector(rec.n, lift);
              skyRay.set(skyOrigin, SKY_UP);
              const above = skyRay.intersectObjects(apiRef.current?.buildingMeshes || [], false);
              if (above.some((h) => h.object?.userData?.layerRole !== "context" && h.distance > 0.05)) return; // occluded — keep prior position
            }
            const f = useStudio.getState().scenarioFaces.find((x) => x.id === faceDrag.id);
            updateScenarioFace3D(faceDrag.id, { utilizedArea: f?.utilizedArea, offset: { a, b }, pillar: f?.pillar, score: faceScoresRef.current?.[f?.pillar] ?? 1, active: true, extruded: f?.extruded });
          }
        }
        return;
      }

      // Active drafting: project the cursor onto the target face plane and size the rectangle
      if (draft.active) {
        setNdc(e);
        raycaster.setFromCamera(ndc, camera);
        plane.setFromNormalAndCoplanarPoint(draft.n, draft.p0);
        if (raycaster.ray.intersectPlane(plane, tmpP)) {
          tmpP.sub(draft.p0);
          draft.aC = tmpP.dot(draft.u);
          draft.bC = tmpP.dot(draft.v);
          updateDraftOverlay();
          setHoverInfo({ tag: "Drafting window", kind: "draft", area: Math.round(Math.abs(draft.aC * draft.bC) * 10) / 10 });
        }
        return;
      }

      // Item 2 — fixed-footprint stamp preview follows the cursor over valid flat surfaces
      const fixed = armedFixed();
      if (fixed) {
        setNdc(e);
        const fh = hitBuildings()[0];
        if (fh && fh.object.userData.layerRole !== "context") {
          const fn = worldNormalOf(fh);
          if (fn.y > 0.5) {
            updateStampPreview(fixed.ff, fh.point, fn);
            setHoverInfo({ tag: `${fixed.sys.label} · ${Math.round(fixed.ff.length * 10) / 10}×${Math.round(fixed.ff.width * 10) / 10} m · click to stamp, R to rotate`, kind: "stamp", area: Math.round(fixed.ff.length * fixed.ff.width * 10) / 10 });
            clearHover();
            return;
          }
        }
        hideStamp(); clearHover(); setHoverInfo(null); return;
      }
      hideStamp();

      // Phase 3 — "Select Existing" (Systems or Locations). Volume mode highlights the WHOLE hovered
      // mesh (its AABB); Surface mode highlights just the coplanar face under the cursor.
      if (pickToolRef.current?.kind === "volume") {
        setNdc(e);
        const vhit = hitBuildings()[0];
        if (vhit && vhit.object.userData.layerRole !== "context") {
          if (pickToolRef.current.mode === "surface") {
            const sn = worldNormalOf(vhit);
            const cf = coplanarFace(vhit.object, sn, vhit.point, modelScaleRef.current);
            volumeHoverBox.visible = false;
            if (cf) {
              hoverOverlay.geometry = cf.built.geometry;
              hoverOverlay.material.color.setHex(RING_HEX.systems);
              hoverOverlay.material.opacity = 0.34;
              hoverOverlay.matrix.identity();
              setHoverInfo({ tag: "Tag this surface", kind: "surface", area: Math.round(cf.area * 10) / 10 });
              hoveringRef.current = true;
              return;
            }
          } else {
            const box = new THREE.Box3().setFromObject(vhit.object);
            if (!box.isEmpty()) {
              volumeHoverBox.box.copy(box);
              volumeHoverBox.visible = true;
              hoverOverlay.material.opacity = 0;
              volumeHoverBox.material.color.setHex(RING_HEX.systems);
              const sz = new THREE.Vector3(); box.getSize(sz);
              setHoverInfo({ tag: "Tag this volume", kind: "volume", area: Math.round(sz.x * sz.z * 10) / 10 });
              hoveringRef.current = true;
              return;
            }
          }
        }
        volumeHoverBox.visible = false;
        clearHover();
        return;
      }

      // Phase 2 (item 1) — the building is INERT by default. When NO tool is armed we run a passive
      // "Identify Face" hover: name the surface (compass orientation / roof / ground) in a small
      // cursor popup WITHOUT selecting the whole face. Suppressed while orbiting (a button is held)
      // or while an allocation-draw / resource-placement is armed.
      if (!areaFocusRef.current && !pickToolRef.current) {
        clearHover();
        if (e.buttons !== 0 || allocDrawRef.current || placeResourceRef.current) { setIdentify(null); return; }
        setNdc(e);
        const ih = hitBuildings()[0];
        if (!ih || ih.object.userData.layerRole === "context") { setIdentify((p) => (p ? null : p)); return; }
        const inWn = worldNormalOf(ih);
        const oi = orientationLabel(inWn, ih.point, apiRef.current?.modelBounds, northOffsetRef.current);
        // Reflect a manual re-classification if this surface has one.
        let tagLabel = oi.tag, tagKind = oi.kind, tagHex = null;
        const manual = manualTagAt(ih.point, inWn);
        if (manual) {
          const cat = CLASSIFY_CATS.find((c) => c.id === manual.category);
          tagLabel = `${cat ? cat.label : manual.category} · classified`;
          tagKind = manual.category;
          tagHex = CLASSIFY_HEX[manual.category];
        }
        const irect = renderer.domElement.getBoundingClientRect();
        const nx = Math.round(e.clientX - irect.left), ny = Math.round(e.clientY - irect.top);
        setIdentify((prev) =>
          prev && prev.tag === tagLabel && Math.abs(prev.x - nx) < 2 && Math.abs(prev.y - ny) < 2
            ? prev
            : { tag: tagLabel, kind: tagKind, hex: tagHex, x: nx, y: ny }
        );
        return;
      }

      setNdc(e);
      // Drafting a flexible footprint (a system is armed): do NOT highlight the whole coplanar face
      // — that reads as "selecting the entire surface" (user request). The user drags a rectangle
      // to define the footprint, or taps to drop a default footprint. Show no face fill on hover.
      if (areaFocusRef.current) { clearHover(); setHoverInfo(null); return; }
      // Selection Mode hover — highlight only real Building surfaces (the buildingMeshes set,
      // which excludes overlays & drafted volumes by construction).
      const hit = hitBuildings()[0];
      if (!hit || hit.object.userData.layerRole === "context") { clearHover(); return; }
      const mesh = hit.object;
      const worldNormal = worldNormalOf(hit);
      if (worldNormal.y < -0.5) { clearHover(); return; } // underside — ignore
      const info = semanticTag(worldNormal, hit.point, apiRef.current?.modelBounds);
      const key = `${mesh.uuid}:${hit.faceIndex}`;
      if (key !== hoverKeyRef.current) {
        const built = buildCoplanarHighlight(mesh, worldNormal, hit.point, modelScaleRef.current);
        if (!built) { clearHover(); return; }
        hoverOverlay.geometry.dispose();
        hoverOverlay.geometry = built.geometry;
        hoverKeyRef.current = key;
        hoverTagRef.current = { ...info, area: Math.round(built.area * 10) / 10 };
        setHoverInfo({ tag: info.tag, kind: info.kind, area: Math.round(built.area * 10) / 10 });
      }
      hoverOverlay.material.color.setHex(RING_HEX[rootPillarRef.current] || 0x0a84ff);
      hoveringRef.current = true;
    };

    const onLeave = () => { clearHover(); setHoverInfo(null); setIdentify(null); };

    const onDown = (e) => {
      downXY = [e.clientX, e.clientY];
      // Manual classification — rectangle-subdivide starts a drag; whole-face select is a click (onUp).
      if (classifyModeRef.current) {
        // Region editing: grab a corner handle of the selected region to start dragging it.
        if (editSel.id && editSel.outline.length) {
          setNdc(e); raycaster.setFromCamera(ndc, camera);
          const hh = raycaster.intersectObjects(editGroup.children, false).find((h) => h.object.userData.cornerIndex != null);
          if (hh) { editDrag = hh.object.userData.cornerIndex; controls.enabled = false; return; }
        }
        if (e.metaKey || e.ctrlKey || classifyDrawRef.current !== "rect") return;
        setNdc(e);
        const chit = hitBuildings()[0];
        if (!chit || chit.object.userData.layerRole === "context") return;
        const cn = worldNormalOf(chit);
        const cup = Math.abs(cn.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
        classifyRect.u = new THREE.Vector3().crossVectors(cup, cn).normalize();
        classifyRect.v = new THREE.Vector3().crossVectors(cn, classifyRect.u).normalize();
        classifyRect.n = cn; classifyRect.p0 = chit.point.clone();
        classifyRect.plane = new THREE.Plane().setFromNormalAndCoplanarPoint(cn, chit.point);
        classifyRect.active = true;
        clearHover();
        return;
      }
      if (measureRef.current) return;   // Measure tool handles clicks on pointerup
      if (analysisRef.current) return; // Analysis Mode owns the surface
      // Fix #3: Ctrl/Cmd is a delete gesture — never start a drag/draft; onUp performs the purge.
      if (e.metaKey || e.ctrlKey) return;
      // §11 kinematic footprint drag — grab the active scenario face's footprint (Selection Mode)
      if (!areaFocusRef.current && !placeResourceRef.current) {
        const activeId = useStudio.getState().activeFaceId;
        if (activeId) { setNdc(e); if (footprintHit(activeId)) { faceDrag.active = true; faceDrag.id = activeId; clearHover(); return; } }
      }
      // Begin a draft when a system draft target OR the allocation-zone draw is armed.
      if (!areaFocusRef.current && !allocDrawRef.current) return;
      if (areaFocusRef.current && armedFixed()) return; // fixed-footprint systems use click-to-stamp (handled on pointerup)
      setNdc(e);
      // Drafting Mode raycasts the Building set only; a Context-role surface is rejected with a toast.
      const hit = hitBuildings()[0];
      if (!hit) return;
      if (hit.object.userData.layerRole === "context") { onNotifyRef.current?.("Drafting restricted to primary Building surfaces."); return; }
      const n = worldNormalOf(hit);
      const up = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const u = new THREE.Vector3().crossVectors(up, n).normalize();
      const v = new THREE.Vector3().crossVectors(n, u).normalize();
      // POLYGON draw mode — click to drop corners on the locked surface plane; close to commit.
      if (draftModeRef.current === "poly") {
        raycaster.setFromCamera(ndc, camera);
        if (!draftPoly.active) {
          draftPoly.active = true;
          draftPoly.alloc = !areaFocusRef.current && !!allocDrawRef.current;
          draftPoly.u = u; draftPoly.v = v; draftPoly.n = n; draftPoly.origin = hit.point.clone();
          draftPoly.plane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, hit.point);
          draftPoly.pts = [new THREE.Vector2(0, 0)];
          draftPoly.hitMesh = hit.object;
          clearHover();
          updateDraftPolyPreview(null);
          onNotifyRef.current?.(`${draftPoly.alloc ? "Zone" : "System"} polygon started — click to add corners. Close by clicking the white first dot or pressing Enter (Esc cancels).`);
          return;
        }
        const pp = new THREE.Vector3();
        if (!raycaster.ray.intersectPlane(draftPoly.plane, pp)) return;
        if (draftPoly.pts.length >= 3) {
          const scr = worldToScreen(draftPoly.origin);
          if (Math.hypot(e.clientX - scr.x, e.clientY - scr.y) < 14) { commitDraftPoly(); return; }
        }
        const d = pp.clone().sub(draftPoly.origin);
        draftPoly.pts.push(new THREE.Vector2(d.dot(draftPoly.u), d.dot(draftPoly.v)));
        updateDraftPolyPreview(pp);
        return;
      }
      draft.active = true; draft.alloc = !areaFocusRef.current && !!allocDrawRef.current;
      draft.p0 = hit.point.clone(); draft.u = u; draft.v = v; draft.n = n; draft.aC = 0; draft.bC = 0; draft.hitMesh = hit.object;
      clearHover();
    };

    const onUp = (e) => {
      const dist = downXY ? Math.hypot(e.clientX - downXY[0], e.clientY - downXY[1]) : 0;

      // Region editing: finish a corner drag → persist the reshaped region.
      if (editDrag >= 0) {
        persistEditedTag();
        editDrag = -1;
        classifyPreview.material.opacity = 0;
        controls.enabled = true;
        renderEditHandles();
        hideSnapDot();
        return;
      }

      // Manual classification commit: finalize a rectangle sub-region, or tag a whole face on click.
      if (classifyRect.active) {
        classifyRect.active = false;
        classifyPreview.material.opacity = 0;
        setNdc(e);
        raycaster.setFromCamera(ndc, camera);
        const cp = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(classifyRect.plane, cp)) {
          const d = cp.clone().sub(classifyRect.p0);
          let a = d.dot(classifyRect.u), b = d.dot(classifyRect.v);
          const u = classifyRect.u.clone(), v = classifyRect.v.clone();
          const o = classifyRect.p0.clone();
          if (a < 0) { o.addScaledVector(u, a); a = -a; }
          if (b < 0) { o.addScaledVector(v, b); b = -b; }
          const cat = classifyCatRef.current;
          if (a > 0.2 && b > 0.2 && cat !== "auto") {
            const center = o.clone().addScaledVector(u, a / 2).addScaledVector(v, b / 2);
            const rbucket = bucketFromNormal(classifyRect.n, classifyRect.p0, apiRef.current?.modelBounds, northOffsetRef.current);
            const rcls = classifyLayerRef.current === "material"
              ? { category: BUCKET_TO_BASE[rbucket] || "exterior", material: classifyMaterialRef.current }
              : { category: cat, material: null };
            useStudio.getState().addSurfaceTag({
              ...rcls, kind: "rect", area: Math.round(a * b * 10) / 10, autoBucket: rbucket,
              geo: { origin: [o.x, o.y, o.z], u: [u.x, u.y, u.z], v: [v.x, v.y, v.z], w: a, h: b, center: [center.x, center.y, center.z], normal: [classifyRect.n.x, classifyRect.n.y, classifyRect.n.z], radius: 0.5 * Math.hypot(a, b) },
            });
            onNotifyRef.current?.(`Region tagged ${rcls.material || rcls.category} (${Math.round(a * b)} m²)`);
          }
        }
        return;
      }
      if (classifyModeRef.current && dist < 5) {
        setNdc(e);
        raycaster.setFromCamera(ndc, camera);
        // Free-form polygon region: first click locks the face plane; each click drops a corner;
        // clicking near the first point (or pressing Enter) closes and tags the enclosed area. Points
        // ride the locked plane, so you can trace AROUND objects sitting on the surface.
        if (classifyDrawRef.current === "poly" && classifyCatRef.current !== "auto") {
          if (!classifyPoly.active) {
            const chit0 = hitBuildings()[0];
            if (!chit0 || chit0.object.userData.layerRole === "context") return;
            const cn0 = worldNormalOf(chit0);
            // Snap-to-edges: cache the face boundary loop and snap the first corner to it.
            const snapW0 = faceOutlineWorld(chit0.object, cn0, chit0.point);
            const startPt = snapToOutlineW(chit0.point.clone(), snapW0, e.altKey).point;
            const cup = Math.abs(cn0.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
            classifyPoly.u = new THREE.Vector3().crossVectors(cup, cn0).normalize();
            classifyPoly.v = new THREE.Vector3().crossVectors(cn0, classifyPoly.u).normalize();
            classifyPoly.n = cn0; classifyPoly.origin = startPt.clone();
            classifyPoly.plane = new THREE.Plane().setFromNormalAndCoplanarPoint(cn0, startPt);
            classifyPoly.pts = [new THREE.Vector2(0, 0)];
            classifyPoly.snapW = snapW0;
            classifyPoly.active = true;
            clearHover();
            hideSnapDot();
            updateClassifyPolyPreview(null); // show the first vertex dot right away
            onNotifyRef.current?.("Polygon started — click to add corners (snaps to surface edges; hold Alt to free-place). Close by clicking the white first dot or pressing Enter (Esc cancels).");
            return;
          }
          const cpp = new THREE.Vector3();
          if (!raycaster.ray.intersectPlane(classifyPoly.plane, cpp)) return;
          // Close by clicking the first vertex (screen-space → zoom-independent) once it's a triangle+.
          if (classifyPoly.pts.length >= 3) {
            const scr = worldToScreen(classifyPoly.origin); // first vertex sits at the plane origin (0,0)
            if (Math.hypot(e.clientX - scr.x, e.clientY - scr.y) < 14) { commitClassifyPoly(); return; }
          }
          // Snap-to-edges: pull the dropped corner onto the nearest face vertex/edge (Alt = free-place).
          const snappedC = snapToOutlineW(cpp.clone(), classifyPoly.snapW, e.altKey).point;
          const dd = snappedC.clone().sub(classifyPoly.origin);
          classifyPoly.pts.push(new THREE.Vector2(dd.dot(classifyPoly.u), dd.dot(classifyPoly.v)));
          updateClassifyPolyPreview(snappedC);
          return;
        }
        const chit = hitBuildings()[0];
        if (!chit || chit.object.userData.layerRole === "context") return;
        const cn = worldNormalOf(chit);
        const cat = classifyCatRef.current;
        if (cat === "auto") {
          // Eraser — remove the nearest classification to the click point.
          const tags = useStudio.getState().surfaceTags;
          let best = null, bestD = Infinity;
          tags.forEach((t) => {
            const c = t.geo?.center; if (!c) return;
            const dd = chit.point.distanceTo(new THREE.Vector3(c[0], c[1], c[2]));
            if (dd < bestD) { bestD = dd; best = t; }
          });
          const tol = Math.max((modelScaleRef.current || 20) * 0.5, 3);
          if (best && bestD < tol) { useStudio.getState().removeSurfaceTag(best.id); onNotifyRef.current?.("Classification removed"); }
          return;
        }
        const cf = coplanarFace(chit.object, cn, chit.point, modelScaleRef.current);
        if (!cf) return;
        if (classifyDrawRef.current !== "face") return; // rectangle draws via drag; ignore stray clicks
        // Region (rect/poly) tags are still click-to-edit (drag corners / Delete). Whole-face tags
        // toggle instead, so selecting & deselecting a surface is one click.
        const existingRegion = manualTagAt(chit.point, cn);
        if (existingRegion && existingRegion.kind !== "face") { selectTagForEdit(existingRegion); return; }
        clearEditSel();
        const positions = Array.from(cf.built.geometry.attributes.position.array);
        // centroid + bounding radius for precise hover matching later
        let cx = 0, cy = 0, cz = 0; const nv = positions.length / 3;
        for (let i = 0; i < positions.length; i += 3) { cx += positions[i]; cy += positions[i + 1]; cz += positions[i + 2]; }
        cx /= nv; cy /= nv; cz /= nv;
        let rad = 0;
        for (let i = 0; i < positions.length; i += 3) rad = Math.max(rad, Math.hypot(positions[i] - cx, positions[i + 1] - cy, positions[i + 2] - cz));
        // The single face tag already covering this surface (match by centroid proximity + shared normal).
        const dupTol = Math.max((modelScaleRef.current || 20) * 0.15, 0.75);
        const dup = (useStudio.getState().surfaceTags || []).find((t) => t.kind === "face" && t.geo?.center && t.geo?.normal
          && Math.hypot(t.geo.center[0] - cx, t.geo.center[1] - cy, t.geo.center[2] - cz) < dupTol
          && (t.geo.normal[0] * cn.x + t.geo.normal[1] * cn.y + t.geo.normal[2] * cn.z) > 0.95);
        const autoBucket = bucketFromNormal(cn, chit.point, apiRef.current?.modelBounds, northOffsetRef.current);

        if (classifyLayerRef.current === "material") {
          // MATERIAL layer — toggle glazed/opaque independently of the base type.
          const mat = classifyMaterialRef.current;
          if (dup) {
            const curMat = tagIsGlazed(dup) ? "glazed" : (tagIsOpaque(dup) ? "opaque" : null);
            if (curMat === mat) { useStudio.getState().updateSurfaceTag(dup.id, { material: null }); onNotifyRef.current?.(`${mat[0].toUpperCase() + mat.slice(1)} removed`); }
            else { useStudio.getState().updateSurfaceTag(dup.id, { material: mat }); onNotifyRef.current?.(`Tagged ${mat} (${Math.round(cf.area)} m²)`); }
          } else {
            useStudio.getState().addSurfaceTag({
              category: BUCKET_TO_BASE[autoBucket] || "exterior", material: mat, kind: "face", area: Math.round(cf.area * 10) / 10,
              autoBucket, geo: { positions, center: [cx, cy, cz], normal: [cn.x, cn.y, cn.z], radius: rad },
            });
            onNotifyRef.current?.(`Tagged ${mat} (${Math.round(cf.area)} m²)`);
          }
          return;
        }

        // BASE layer — set / change / toggle-off the surface type (keeps any material layer).
        if (dup) {
          if (dup.category === cat && !dup.material) { useStudio.getState().removeSurfaceTag(dup.id); onNotifyRef.current?.("Surface deselected"); }
          else { useStudio.getState().updateSurfaceTag(dup.id, { category: cat }); onNotifyRef.current?.(`Surface set to ${cat} (${Math.round(cf.area)} m²)`); }
          return;
        }
        useStudio.getState().addSurfaceTag({
          category: cat, material: null, kind: "face", area: Math.round(cf.area * 10) / 10,
          autoBucket, geo: { positions, center: [cx, cy, cz], normal: [cn.x, cn.y, cn.z], radius: rad },
        });
        onNotifyRef.current?.(`Surface classified as ${cat} (${Math.round(cf.area)} m²)`);
        return;
      }

      // Measure tool: a tap (not an orbit-drag) places/toggles a snapped measure point
      if (measureRef.current) { if (dist < 5) { setNdc(e); measureClick(); } return; }

      // Phase 2/3 — "Select Existing Volume": tag the clicked modeled mesh as the armed system,
      // carrying the pairing's anchored location. If no system is armed yet, guide the user.
      if (pickToolRef.current?.kind === "volume" && dist < 5 && !(e.metaKey || e.ctrlKey)) {
        const pt = pickToolRef.current;
        if (!pt.systemId) { onNotifyRef.current?.("Select a system option below first, then tag a volume."); return; }
        setNdc(e);
        const vh = [...hitVolumes(), ...hitBuildings()].sort((a, b) => a.distance - b.distance)[0];
        const mesh = vh?.object;
        if (mesh && mesh.userData?.layerRole !== "context") {
          const locId = pt.locationId ?? useStudio.getState().pairDraft?.locationId ?? null;
          if (pt.mode === "surface") {
            // SURFACE pick — tag just the clicked face (e.g. a green roof) as the system footprint.
            const sn = worldNormalOf(vh);
            const cf = coplanarFace(mesh, sn, vh.point, modelScaleRef.current);
            const sysObj = useStudio.getState().kb?.systems?.find((s) => s.id === pt.systemId);
            const h0 = sysObj?.default_height || 0.6;
            if (cf && cf.outline && cf.outline.length >= 3) {
              spawnPolyBlock(pt.systemId, { origin: cf.origin, u: cf.u, v: cf.v, n: cf.n, outline: cf.outline, area: cf.area });
            } else if (cf) {
              const p0 = cf.origin.clone();
              spawnSystemBlock(pt.systemId, { p0, u: cf.u, v: cf.v, n: cf.n, aC: cf.W, bC: cf.H });
            } else { onNotifyRef.current?.("Couldn't read that surface — try a flatter face."); return; }
            onNotifyRef.current?.(`Surface tagged (${Math.round(cf.area)} m²)`);
          } else {
            tagExistingVolume(pt.systemId, mesh, locId, vh.face);
          }
          useStudio.getState().setPickTool(null);
        } else {
          onNotifyRef.current?.(pt.mode === "surface" ? "Click a modeled surface to tag it." : "Click an existing modeled volume to tag it.");
        }
        return;
      }

      // Fix #3: Ctrl/Cmd + click on an existing volume OR captured face → delete that instance.
      // Store removal auto-destroys the mesh (sync) and recomputes the ring completion %.
      if (dist < 5 && (e.metaKey || e.ctrlKey) && !areaFocusRef.current && !placeResourceRef.current) {
        setNdc(e);
        const vh = hitVolumes()[0];
        if (vh?.object?.userData?.blockId) {
          useStudio.getState().removeSystemBlock(vh.object.userData.blockId);
          onNotifyRef.current?.("Instance deleted");
          return;
        }
        const fh = hitBuildings()[0];
        if (fh && fh.object.userData.layerRole !== "context") {
          const wn = worldNormalOf(fh);
          const key = faceKey(fh.object, wn, fh.point);
          const existing = useStudio.getState().scenarioFaces.find((x) => x._key === key);
          if (existing) {
            removeScenarioFace3D(existing.id);
            useStudio.getState().removeScenarioFace(existing.id);
            onNotifyRef.current?.("Selection deleted");
          }
        }
        return;
      }

      // §11 end footprint drag → persist the placement offset
      if (faceDrag.active) {
        const rec = scenarioFaceMeshes.get(faceDrag.id);
        if (rec && rec.cur) useStudio.getState().updateScenarioFace(faceDrag.id, { offset: { a: rec.cur.a, b: rec.cur.b } });
        faceDrag.active = false; faceDrag.id = null;
        return;
      }

      // Item 2 — click stamps a fixed-footprint volume at the preview location
      const fixed = armedFixed();
      if (fixed && dist < 5) {
        setNdc(e);
        const fh = hitBuildings()[0];
        if (fh && fh.object.userData.layerRole !== "context") {
          const fn = worldNormalOf(fh);
          if (fn.y > 0.5) {
            const { u, v } = stampBasis(fn);
            const ff = fixed.ff;
            const p0 = fh.point.clone().addScaledVector(u, -ff.length / 2).addScaledVector(v, -ff.width / 2);
            spawnSystemBlock(fixed.sys.id, { p0, u, v, n: fn, aC: ff.length, bC: ff.width });
            hideStamp();
            useStudio.getState().setAreaFocus(null);
            onNotifyRef.current?.(`${fixed.sys.label} stamped (${ff.length}×${ff.width}×${ff.height} m)`);
            return;
          }
        }
        onNotifyRef.current?.("Hover a flat building surface to stamp the pod.");
        return;
      }

      // (A) commit an on-surface draft → extrude a parametric BIA system block
      if (draft.active) {
        draft.active = false;
        hideDraftOverlay();
        setHoverInfo(null);
        // (A0) Allocation-zone draft: record the drawn sub-area as the funnel's ALLOCATED AREA
        // (on the in-progress pairing, else on the active committed instance) and paint the zone.
        // No system volume is spawned — the footprint is drawn next, "inside" this zone.
        if (draft.alloc) {
          draft.alloc = false;
          let area = 0, meta = null;
          if (dist < 5) {
            const cf = coplanarFace(draft.hitMesh, draft.n, draft.p0, modelScaleRef.current);
            area = Math.round((cf?.area || 0) * 10) / 10;
            if (cf?.origin) meta = { p0: cf.origin, u: cf.u, v: cf.v, n: cf.n, aC: Math.sqrt(cf.area), bC: Math.sqrt(cf.area) };
          } else if (Math.abs(draft.aC * draft.bC) > 0.2) {
            area = Math.round(Math.abs(draft.aC * draft.bC) * 10) / 10;
            meta = { p0: draft.p0.clone(), u: draft.u.clone(), v: draft.v.clone(), n: draft.n.clone(), aC: draft.aC, bC: draft.bC };
          }
          if (area > 0) {
            if (meta) { paintAllocZone(meta.p0, meta.u, meta.v, meta.n, meta.aC, meta.bC); setActiveZoneMeta(meta); }
            const st = useStudio.getState();
            const _locId = st.pairDraft?.locationId || st.systemBlocks.find((b) => b.id === st.activeBlockId)?.locationId;
            // Each drawn zone is INDEPENDENT: mint a fresh zoneId (reusing the active block's zone when
            // re-zoning around an existing volume) so multiple zones can coexist on the same location.
            const _hostZid = st.activeBlockId ? (st.systemBlocks.find((b) => b.id === st.activeBlockId)?.zoneId || st.systemBlocks.find((b) => b.id === st.activeBlockId)?.locationId) : null;
            const _zid = _hostZid || ((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `zone-${Date.now()}`);
            if (st.pairDraft) st.setPairField({ allocatedArea: area, zoneId: _zid });
            else if (st.activeBlockId) st.setBlockAllocated(st.activeBlockId, area);
            // Persist the drawn zone by zoneId so future systems inherit it.
            st.setZoneAllocation(_zid, area);
            // Persist the zone polygon so a system's MOVE can be clamped inside THIS zone later.
            if (meta) {
              st.setZoneGeom(_zid, {
                origin: meta.p0.toArray(), u: meta.u.toArray(), v: meta.v.toArray(), n: meta.n.toArray(),
                outline: [[0, 0], [meta.aC, 0], [meta.aC, meta.bC], [0, meta.bC]],
              });
            }
            // ZONE LOCK: register this as the active zone — every system placed inside it next is
            // hard-bound to this zoneId and clamped inside its bounds until Escape / release.
            const _loc = _locId ? st.kb?.locations?.find((l) => l.id === _locId) : null;
            st.setActiveZone({ zoneId: _zid, locationId: _locId || null, area, label: _loc?.label || _locId || "Zone" });
            onAllocAreaRef.current?.(area); // bridge the drawn zone up to the native shell
            onNotifyRef.current?.(`Zone set · ${area.toLocaleString()} m². Now place systems inside it (Esc to unlock the zone)`);
          }
          useStudio.getState().setAllocDraw(null);
          return;
        }
        const sysId = areaFocusRef.current;
        // ZONE-FIRST LOCK: when a zone is active, the system is placed INSIDE the zone bounds.
        // A tap stamps a default footprint clamped into the zone (never the whole face); a drag is
        // clamped to fit within the zone rectangle.
        if (activeZoneMeta) {
          const sysObj = useStudio.getState().kb?.systems?.find((x) => x.id === sysId);
          const ff = sysObj?.fixed_footprint;
          let rect;
          if (dist < 5) {
            const side = ff ? Math.max(ff.length, ff.width) : Math.max(0.5, Math.sqrt(Math.min(activeZoneMeta.aC * activeZoneMeta.bC, 4)));
            const aC = ff ? ff.length : side, bC = ff ? ff.width : side;
            const p0 = draft.p0.clone().addScaledVector(draft.u, -aC / 2).addScaledVector(draft.v, -bC / 2);
            rect = clampToZone({ p0, u: draft.u, v: draft.v, n: draft.n, aC, bC });
          } else if (Math.abs(draft.aC * draft.bC) > 0.2) {
            rect = clampToZone({ p0: draft.p0, u: draft.u, v: draft.v, n: draft.n, aC: draft.aC, bC: draft.bC });
          }
          if (rect) spawnSystemBlock(sysId, rect);
          useStudio.getState().setAreaFocus(null);
          return;
        }
        if (dist < 5) {
          // Tap drops a DEFAULT footprint at the cursor — never the whole surface (user request:
          // remove whole-surface selection). Proprietary systems use their fixed footprint; flexible
          // systems get a modest 4×4 m starter the user can resize. Drag instead for a custom size.
          const sysObj = useStudio.getState().kb?.systems?.find((x) => x.id === sysId);
          const ff = sysObj?.fixed_footprint;
          const aC = ff ? ff.length : 4, bC = ff ? ff.width : 4;
          const p0 = draft.p0.clone().addScaledVector(draft.u, -aC / 2).addScaledVector(draft.v, -bC / 2);
          spawnSystemBlock(sysId, { p0, u: draft.u, v: draft.v, n: draft.n, aC, bC });
        } else if (Math.abs(draft.aC * draft.bC) > 0.2) {
          spawnSystemBlock(sysId, { p0: draft.p0, u: draft.u, v: draft.v, n: draft.n, aC: draft.aC, bC: draft.bC });
        }
        // Auto-revert to Selection Mode the instant the volume is generated (directive #2)
        useStudio.getState().setAreaFocus(null);
        return;
      }

      // (B) select an existing drafted block to edit its height / crop allocation
      if (dist < 5 && !analysisRef.current && !areaFocusRef.current && !placeResourceRef.current && !heightControl.dragging && !moveControl.dragging && !scaleControl.dragging) {
        setNdc(e);
        const bh = hitVolumes()[0]; // Selection Mode → drafted BIA volumes only
        if (bh) { useStudio.getState().setActiveBlock(bh.object.userData.blockId); return; }
      }

      // (B2) §10/§11 — tap a building face to add/remove it as a persistent scenario face
      // (item 1) only when the surface pick tool ("Draw on Building") is armed.
      if (dist < 5 && pickToolRef.current?.kind === "surface" && !areaFocusRef.current && !placeResourceRef.current && !heightControl.dragging) {
        setNdc(e);
        const fhit = hitBuildings()[0];
        if (fhit && fhit.object.userData.layerRole !== "context") {
          const wn = worldNormalOf(fhit);
          if (wn.y >= -0.5) {
            const key = faceKey(fhit.object, wn, fhit.point);
            const existing = useStudio.getState().scenarioFaces.find((x) => x._key === key);
            if (existing) { removeScenarioFace3D(existing.id); useStudio.getState().removeScenarioFace(existing.id); onNotifyRef.current?.("Surface released"); return; }
            const pillar = rootPillarRef.current;
            const desc = addScenarioFace3D(fhit.object, wn, fhit.point, pillar);
            if (desc) {
              desc._key = key;
              const st = semanticTag(wn, fhit.point, apiRef.current?.modelBounds);
              desc.locationId = st.locationId; // for contextual nesting under the location option
              useStudio.getState().addScenarioFace(desc);
              if (pillar === "locations") useStudio.getState().activateLocation(st.locationId);
              onSurfaceTagRef.current?.({ tag: desc.tag, locationId: st.locationId, area: desc.totalArea });
              return;
            }
          }
        }
      }

      // (C) bi-directional selection — a tap on a surface while the Location ring is active
      if (dist < 5 && pickToolRef.current?.kind === "surface" && !areaFocusRef.current && rootPillarRef.current === "locations" && hoveringRef.current && hoverTagRef.current) {
        const t = hoverTagRef.current;
        useStudio.getState().activateLocation(t.locationId);
        onSurfaceTagRef.current?.({ tag: t.tag, locationId: t.locationId, area: t.area });
        return;
      }

      // (D) volumetric placement — a tap on a surface while a resource is armed for placement
      if (dist < 5 && placeResourceRef.current && !equipment.tcontrols.dragging) {
        setNdc(e);
        const hit = firstMeshHit();
        if (!hit) return;
        const { id, dims } = placeResourceRef.current;
        if (dims) spawnBlock(id, dims, hit.point.clone());
      }
    };

    // Section 11 — Escape exits Drafting Mode (escape hatch back to Selection Mode)
    const onKeyDown = (e) => {
      // Classify Undo — Ctrl/Cmd+Z reverts the last classification while in classify mode.
      if ((e.key === "z" || e.key === "Z") && (e.metaKey || e.ctrlKey) && classifyModeRef.current) {
        e.preventDefault();
        if (useStudio.getState().surfaceTagsPast.length) { useStudio.getState().undoSurfaceTags(); onNotifyRef.current?.("Reverted last classification"); }
        return;
      }
      // Close an in-progress classification polygon with Enter.
      if ((e.key === "Enter") && classifyPoly.active) { e.preventDefault(); commitClassifyPoly(); return; }
      // Close an in-progress drafting (system/zone) polygon with Enter.
      if ((e.key === "Enter") && draftPoly.active) { e.preventDefault(); commitDraftPoly(); return; }
      // Item 2 — rotate the fixed-footprint stamp preview 90° (R or Spacebar)
      if ((e.key === "r" || e.key === "R" || e.key === " ") && armedFixed()) {
        e.preventDefault();
        stampRot = (stampRot + Math.PI / 2) % (Math.PI * 2);
        onNotifyRef.current?.("Rotated 90°. Click to stamp");
        return;
      }
      if (e.key !== "Escape") return;
      let acted = false;
      if (classifyPoly.active) { cancelClassifyPoly(); acted = true; }
      if (draftPoly.active) { cancelDraftPoly(); acted = true; }
      if (draft.active) { draft.active = false; draft.alloc = false; hideDraftOverlay(); setHoverInfo(null); acted = true; }
      if (armedFixed()) { hideStamp(); stampRot = 0; }
      if (areaFocusRef.current) { useStudio.getState().setAreaFocus(null); acted = true; }
      if (allocDrawRef.current) { useStudio.getState().setAllocDraw(null); acted = true; }
      // ZONE-FIRST LOCK: Escape frees the active zone so the user can draft anywhere again.
      if (activeZoneMeta || useStudio.getState().activeZone) { clearActiveZoneVisual(); useStudio.getState().clearActiveZone(); acted = true; }
      if (pickToolRef.current) { useStudio.getState().setPickTool(null); acted = true; }
      if (acted) { clearHover(); onNotifyRef.current?.("Tool disarmed. Back to Selection Mode"); }
    };
    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("pointerleave", onLeave);
    renderer.domElement.addEventListener("pointerup", onUp);
    window.addEventListener("keydown", onKeyDown);

    // Sum per-face gross areas of the BUILDING-layer meshes (excludes context), classified
    // roof / base / facade with facade bucketed by true-north compass orientation (Section 5).
    const computeGross = () => {
      const res = { roof: 0, base: 0, N: 0, S: 0, E: 0, W: 0 };
      let hiddenArea = 0; // buried / shared / interior faces — excluded from the exterior envelope (re-counted under CEA)
      const northDeg = northOffsetRef.current || 0;
      const scopeAll = useStudio.getState().scope === "neighborhood";
      const allMeshes = apiRef.current?.buildingMeshes || [];
      const meshes = allMeshes.filter((m) => m && (scopeAll || m.userData?.layerRole === "building" || m.userData?.layerRole === "bia") && m.geometry?.attributes?.position);

      // Per-face visibility is O(tris · masses); on very dense meshes fall back to the lightweight
      // AABB roof heuristic so the UI stays responsive.
      let totalTris = 0;
      meshes.forEach((m) => { const g = m.geometry; totalTris += g.index ? g.index.count / 3 : g.attributes.position.count / 3; });
      const rayMode = totalTris <= 12000 && meshes.length > 0;

      const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), ab = new THREE.Vector3(), ac = new THREE.Vector3(), nr = new THREE.Vector3(), cen = new THREE.Vector3(), org = new THREE.Vector3();
      const ray = new THREE.Raycaster();
      const DOWN = new THREE.Vector3(0, -1, 0);
      const PROBE = new THREE.Vector3(0.5773, 0.5773, 0.5773); // arbitrary non-axis dir for point-in-solid parity test
      const CLEAR_M = 1.0;  // >1 m of clear space in front = an exposed / growable surface (user rule)
      const EPS = Math.max((modelScaleRef.current || 20) * 0.0006, 0.008);
      const boxes = rayMode ? meshes.map((m) => new THREE.Box3().setFromObject(m)) : [];

      // Parity point-in-solid needs BOTH front & back crossings; default materials cull back faces,
      // so flip the raycast targets to DoubleSide for the duration of the measurement, then restore.
      const savedSides = [];
      if (rayMode) meshes.forEach((m, k) => { const mats = Array.isArray(m.material) ? m.material : [m.material]; savedSides[k] = mats.map((mm) => (mm ? mm.side : null)); mats.forEach((mm) => { if (mm) mm.side = THREE.DoubleSide; }); });

      // True if point p sits inside any closed building solid (buried/shared face → hidden).
      const insideAnySolid = (p) => {
        for (let k = 0; k < meshes.length; k++) {
          if (!boxes[k].containsPoint(p)) continue;
          ray.set(p, PROBE); ray.near = 0; ray.far = 1e9;
          const hits = ray.intersectObject(meshes[k], false);
          if (hits.length % 2 === 1) return true; // odd crossings → inside this solid
        }
        return false;
      };

      const classify = (n, area) => {
        if (n.y > 0.6) res.roof += area;
        else if (n.y < -0.6) res.base += area;
        else {
          let heading = (Math.atan2(n.x, -n.z) * 180) / Math.PI + northDeg;
          heading = ((heading % 360) + 360) % 360;
          const bucket = heading < 45 || heading >= 315 ? "N" : heading < 135 ? "E" : heading < 225 ? "S" : "W";
          res[bucket] += area;
        }
      };

      meshes.forEach((m) => {
        const geo = m.geometry, pos = geo?.attributes?.position;
        if (!pos) return;
        m.updateWorldMatrix(true, false);
        const mat = m.matrixWorld, idx = geo.index;
        const tris = idx ? idx.count / 3 : pos.count / 3;
        for (let t = 0; t < tris; t++) {
          const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
          a.fromBufferAttribute(pos, i0).applyMatrix4(mat); b.fromBufferAttribute(pos, i1).applyMatrix4(mat); c.fromBufferAttribute(pos, i2).applyMatrix4(mat);
          ab.subVectors(b, a); ac.subVectors(c, a); nr.crossVectors(ab, ac);
          const area = nr.length() / 2;
          if (area < 1e-9) continue;
          nr.normalize();
          if (rayMode) {
            cen.copy(a).add(b).add(c).multiplyScalar(1 / 3);
            org.copy(cen).addScaledVector(nr, EPS);
            // 1) Buried / shared / interior — a point just outside the face lies inside another solid.
            if (insideAnySolid(org)) { hiddenArea += area; continue; }
            // 2) Underside faces are only growable with > 1 m clearance below (ground OR the mass beneath).
            if (nr.y < -0.6) {
              let clr = cen.y; // clearance to grade (model min-Y is placed at 0)
              ray.set(org, nr); ray.near = 0; ray.far = CLEAR_M + EPS * 2;
              const hits = ray.intersectObjects(meshes, false);
              for (let h = 0; h < hits.length; h++) { if (hits[h].distance > EPS * 1.5) { clr = Math.min(clr, hits[h].distance); break; } }
              if (clr <= CLEAR_M) { hiddenArea += area; continue; }
            }
          }
          classify(nr, area);
        }
      });

      if (rayMode) meshes.forEach((m, k) => { const mats = Array.isArray(m.material) ? m.material : [m.material]; mats.forEach((mm, i2) => { if (mm && savedSides[k]) mm.side = savedSides[k][i2]; }); });

      if (!rayMode && meshes.length) {
        // Fallback: AABB roof occlusion (boxes stacked on boxes) when the mesh is too dense to ray-test.
        const fboxes = meshes.map((m) => new THREE.Box3().setFromObject(m));
        const tol = Math.max((modelScaleRef.current || 20) * 0.01, 0.15);
        let occluded = 0;
        for (let i = 0; i < fboxes.length; i++) {
          const A = fboxes[i];
          for (let j = 0; j < fboxes.length; j++) {
            if (i === j) continue;
            const B = fboxes[j];
            if (B.max.y > A.max.y + tol && B.min.y <= A.max.y + tol) {
              const ox = Math.max(0, Math.min(A.max.x, B.max.x) - Math.max(A.min.x, B.min.x));
              const oz = Math.max(0, Math.min(A.max.z, B.max.z) - Math.max(A.min.z, B.min.z));
              occluded += ox * oz;
            }
          }
        }
        hiddenArea += Math.min(res.roof, occluded);
        res.roof = Math.max(0, res.roof - occluded);
      }

      // Manual surface-classification overrides (2a): move each tagged area out of its auto bucket
      // and into the user-chosen role. "interior" is excluded from all exterior growable buckets;
      // "exterior" keeps the auto detection; "podium" is treated as growable roof-deck.
      const _tags = useStudio.getState().surfaceTags || [];
      let interiorArea = 0, podiumArea = 0;
      _tags.forEach((tg) => {
        const ar = tg.area || 0;
        // "exterior" keeps auto detection; material tags (opaque/glazed) are visual-only → no bucket effect.
        if (!ar || tg.category === "exterior" || tg.category === "opaque" || tg.category === "glazed") return;
        const from = tg.autoBucket;
        if (from && res[from] != null) res[from] = Math.max(0, res[from] - ar);
        if (tg.category === "roof") res.roof += ar;
        else if (tg.category === "ground") res.base += ar;
        else if (tg.category === "podium") { res.roof += ar; podiumArea += ar; }
        else if (tg.category === "interior") { interiorArea += ar; }
      });
      const facadeTotal = res.N + res.S + res.E + res.W;
      const interior = +(hiddenArea + interiorArea).toFixed(1);
      return { roof: +res.roof.toFixed(1), base: +res.base.toFixed(1), facade: { total: +facadeTotal.toFixed(1), N: +res.N.toFixed(1), S: +res.S.toFixed(1), E: +res.E.toFixed(1), W: +res.W.toFixed(1) }, interior, podium: +podiumArea.toFixed(1) };
    };

    // Find the largest upward-facing (roof) plane across the building-layer meshes.
    // Groups coplanar triangles by (normal, plane offset), accumulates area and an
    // area-weighted centroid, and returns the winning plane's seed for auto-placement.
    const largestUpFace = () => {
      const meshes = apiRef.current?.buildingMeshes || [];
      const groups = new Map();
      const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), ab = new THREE.Vector3(), ac = new THREE.Vector3(), nr = new THREE.Vector3();
      meshes.forEach((m) => {
        if (!m || (useStudio.getState().scope !== "neighborhood" && m.userData?.layerRole !== "building" && m.userData?.layerRole !== "bia")) return;
        const geo = m.geometry, pos = geo?.attributes?.position;
        if (!pos) return;
        m.updateWorldMatrix(true, false);
        const mat = m.matrixWorld, idx = geo.index;
        const tris = idx ? idx.count / 3 : pos.count / 3;
        for (let t = 0; t < tris; t++) {
          const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
          a.fromBufferAttribute(pos, i0).applyMatrix4(mat); b.fromBufferAttribute(pos, i1).applyMatrix4(mat); c.fromBufferAttribute(pos, i2).applyMatrix4(mat);
          ab.subVectors(b, a); ac.subVectors(c, a); nr.crossVectors(ab, ac);
          const len = nr.length();
          if (len < 1e-9) continue;
          const area = len / 2;
          nr.multiplyScalar(1 / len);
          if (nr.y <= 0.6) continue; // roof / up-facing only
          const d = nr.dot(a);
          const key = `${Math.round(nr.x * 12)}_${Math.round(nr.y * 12)}_${Math.round(nr.z * 12)}_${Math.round(d * 3)}`;
          const cx = (a.x + b.x + c.x) / 3, cy = (a.y + b.y + c.y) / 3, cz = (a.z + b.z + c.z) / 3;
          let g = groups.get(key);
          if (!g) { g = { area: 0, cx: 0, cy: 0, cz: 0, nx: nr.x, ny: nr.y, nz: nr.z, mesh: m }; groups.set(key, g); }
          g.area += area; g.cx += cx * area; g.cy += cy * area; g.cz += cz * area;
        }
      });
      let best = null;
      groups.forEach((g) => { if (!best || g.area > best.area) best = g; });
      if (!best || best.area < 0.5) return null;
      return {
        mesh: best.mesh,
        normal: new THREE.Vector3(best.nx, best.ny, best.nz).normalize(),
        point: new THREE.Vector3(best.cx / best.area, best.cy / best.area, best.cz / best.area),
      };
    };

    // Auto-drop a square BIA volume of ~`targetArea` m² centered on the largest roof
    // face (General Entry "Apply"). Returns the placed area (m²) on success, else null.
    const autoPlaceBlock = (systemId, targetArea) => {
      if (!systemId) return null;
      const seed = largestUpFace();
      if (!seed) return null;
      const cf = coplanarFace(seed.mesh, seed.normal, seed.point, modelScaleRef.current);
      if (!cf) return null;
      let side = Math.sqrt(Math.max(0.5, targetArea || cf.area));
      const fit = Math.max(0.5, Math.min(cf.W, cf.H) * 0.95);
      if (side > fit) side = fit;
      const p0 = cf.origin.clone()
        .addScaledVector(cf.u, cf.W / 2 - side / 2)
        .addScaledVector(cf.v, cf.H / 2 - side / 2);
      spawnSystemBlock(systemId, { p0, u: cf.u, v: cf.v, n: cf.n, aC: side, bC: side });
      return +(side * side).toFixed(1);
    };

    // ---- Location-ring footprints: flat highlighted rectangles (not extruded volumes) ----
    const locBlocks = {}; // id -> { mesh }
    // Pick the best surface for a location: ground = up-face near minY; rooftop = up-face
    // above minY; facade_* = the largest vertical face whose cardinal orientation matches.
    const pickSurfaceForLocation = (locationId) => {
      const meshes = apiRef.current?.buildingMeshes || [];
      const bounds = apiRef.current?.modelBounds || { minY: 0, maxY: 1 };
      const tolY = Math.max((bounds.maxY - bounds.minY) * 0.04, 0.5);
      const groups = new Map();
      const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), ab = new THREE.Vector3(), ac = new THREE.Vector3(), nr = new THREE.Vector3();
      meshes.forEach((m) => {
        if (!m || (useStudio.getState().scope !== "neighborhood" && m.userData?.layerRole !== "building" && m.userData?.layerRole !== "bia")) return;
        const geo = m.geometry, pos = geo?.attributes?.position;
        if (!pos) return;
        m.updateWorldMatrix(true, false);
        const mat = m.matrixWorld, idx = geo.index;
        const tris = idx ? idx.count / 3 : pos.count / 3;
        for (let t = 0; t < tris; t++) {
          const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
          a.fromBufferAttribute(pos, i0).applyMatrix4(mat); b.fromBufferAttribute(pos, i1).applyMatrix4(mat); c.fromBufferAttribute(pos, i2).applyMatrix4(mat);
          ab.subVectors(b, a); ac.subVectors(c, a); nr.crossVectors(ab, ac);
          const len = nr.length(); if (len < 1e-9) continue;
          const area = len / 2; nr.multiplyScalar(1 / len);
          const cy = (a.y + b.y + c.y) / 3;
          let ok = false;
          if (locationId === "ground") ok = nr.y > 0.6 && Math.abs(cy - bounds.minY) <= tolY;
          else if (locationId === "rooftop") ok = nr.y > 0.6 && (cy - bounds.minY) > tolY;
          else { // facade — match cardinal orientation from the triangle normal
            if (Math.abs(nr.y) >= 0.5) ok = false;
            else ok = semanticTag(nr, null, bounds).locationId === locationId;
          }
          if (!ok) continue;
          const d = nr.dot(a);
          const key = `${Math.round(nr.x * 12)}_${Math.round(nr.y * 12)}_${Math.round(nr.z * 12)}_${Math.round(d * 3)}`;
          const cx = (a.x + b.x + c.x) / 3, cz = (a.z + b.z + c.z) / 3;
          let g = groups.get(key);
          if (!g) { g = { area: 0, cx: 0, cy: 0, cz: 0, nx: nr.x, ny: nr.y, nz: nr.z, mesh: m }; groups.set(key, g); }
          g.area += area; g.cx += cx * area; g.cy += cy * area; g.cz += cz * area;
        }
      });
      let best = null;
      groups.forEach((g) => { if (!best || g.area > best.area) best = g; });
      if (!best || best.area < 0.5) return null;
      return { mesh: best.mesh, normal: new THREE.Vector3(best.nx, best.ny, best.nz).normalize(), point: new THREE.Vector3(best.cx / best.area, best.cy / best.area, best.cz / best.area) };
    };

    const mountLocationFootprint = (id, cf, side, origin, meshOrient) => {
      const hex = RING_HEX.locations;
      const geo = new THREE.ShapeGeometry(rectShape(side, side));
      const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.42, depthWrite: false, side: THREE.DoubleSide }));
      mesh.quaternion.copy(meshOrient); mesh.position.copy(origin); mesh.renderOrder = 9; mesh.raycast = () => {};
      const edge = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: hex, transparent: true, opacity: 0.95 }));
      edge.raycast = () => {}; mesh.add(edge);
      mesh.userData = { locationBlockId: id, ringId: "locations" };
      content.add(mesh);
      locBlocks[id] = { mesh };
    };

    // Auto-drop a flat highlighted footprint of ~`targetArea` m² on a valid location surface.
    const groundPlaneFace = () => {
      const meshes = apiRef.current?.buildingMeshes || [];
      const bounds = apiRef.current?.modelBounds || { minY: 0, maxY: 1 };
      const bbox = new THREE.Box3();
      let has = false;
      meshes.forEach((m) => { if (m && m.userData?.layerRole !== "context") { bbox.expandByObject(m); has = true; } });
      if (!has) return null;
      const cx = (bbox.min.x + bbox.max.x) / 2, cz = (bbox.min.z + bbox.max.z) / 2;
      const W = Math.max(bbox.max.x - bbox.min.x, 2), H = Math.max(bbox.max.z - bbox.min.z, 2);
      const origin = new THREE.Vector3(cx - W / 2, bounds.minY, cz - H / 2);
      return { u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 0, 1), n: new THREE.Vector3(0, 1, 0), origin, W, H, area: W * H, outline: null };
    };
    const autoPlaceLocationFootprint = (locationId, targetArea) => {
      const seed = pickSurfaceForLocation(locationId);
      let cf = seed ? coplanarFace(seed.mesh, seed.normal, seed.point, modelScaleRef.current) : null;
      // Ground-Level rarely has a building up-face at grade → drop a footprint on the site plane.
      if (!cf && locationId === "ground") cf = groundPlaneFace();
      if (!cf) return null;
      let side = Math.sqrt(Math.max(0.5, targetArea || cf.area));
      const fit = Math.max(0.5, Math.min(cf.W, cf.H) * 0.95);
      if (side > fit) side = fit;
      const eps = Math.max(modelScaleRef.current * 0.002, 0.003);
      const origin = cf.origin.clone().addScaledVector(cf.u, cf.W / 2 - side / 2).addScaledVector(cf.v, cf.H / 2 - side / 2).addScaledVector(cf.n, eps);
      const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(cf.u, cf.v, cf.n));
      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `loc-${Date.now()}`;
      mountLocationFootprint(id, cf, side, origin, meshOrient);
      const kb = useStudio.getState().kb;
      const label = kb?.locations?.find((l) => l.id === locationId)?.label || locationId;
      const geomMeta = { origin: cf.origin.toArray(), u: cf.u.toArray(), v: cf.v.toArray(), n: cf.n.toArray(), side, W: cf.W, H: cf.H, eps };
      useStudio.getState().addLocationBlock({ id, locationId, locationLabel: label, area: +(side * side).toFixed(1), geom: geomMeta });
      return +(side * side).toFixed(1);
    };

    // Reconcile location footprints with store state (handles deletes from the panel).
    const clearLocationBlocks3D = () => {
      Object.keys(locBlocks).forEach((bid) => {
        const m = locBlocks[bid].mesh;
        m.parent?.remove(m); m.geometry.dispose(); m.material.dispose(); delete locBlocks[bid];
      });
    };
    const syncLocationBlocks = (blocks) => {
      const ids = new Set(blocks.map((b) => b.id));
      Object.keys(locBlocks).forEach((bid) => {
        if (!ids.has(bid)) {
          const m = locBlocks[bid].mesh;
          m.parent?.remove(m); m.geometry.dispose(); m.material.dispose(); delete locBlocks[bid];
        }
      });
      // Re-draw any block that isn't yet in the scene (e.g. after .dsymbia rehydration).
      blocks.forEach((b) => {
        if (locBlocks[b.id] || !b.geom) return;
        const g = b.geom;
        const cf = { origin: new THREE.Vector3().fromArray(g.origin), u: new THREE.Vector3().fromArray(g.u), v: new THREE.Vector3().fromArray(g.v), n: new THREE.Vector3().fromArray(g.n), W: g.W, H: g.H };
        const origin = cf.origin.clone().addScaledVector(cf.u, cf.W / 2 - g.side / 2).addScaledVector(cf.v, cf.H / 2 - g.side / 2).addScaledVector(cf.n, g.eps || 0.01);
        const meshOrient = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(cf.u, cf.v, cf.n));
        mountLocationFootprint(b.id, cf, g.side, origin, meshOrient);
      });
    };

    apiRef.current = { scene, camera, controls, content, highlightMeshes: {}, fitHalf: frustum, grid, ground, hoverOverlay, draftPlane, draftEdge, hideDraftOverlay, equipment, spawnBlock, clearEquipment, applyNorth, heatMat, applyAnalysis, updateHeatUniforms, rebakeAnalysis, buildingMeshes: [], biaVolumeMeshes: [], heatBakedKey: null, spawnSystemBlock, spawnPolyBlock, syncSystemBlocks, setActiveBlock3D, clearSystemBlocks3D, sysBlocks, activeBlockId3D: null, applyXformMode, layerRoles: {}, setMeasureActive, clearMeasure, syncScenarioFaces3D, clearScenarioFaces3D, extrudeFace, applyRingScope, rehydrateScene, computeGross, autoPlaceBlock, autoPlaceLocationFootprint, syncLocationBlocks, clearLocationBlocks3D, resizeBlock, rotateBlock, tagExistingVolume, spawnBiaBlocksFromLayer, setSectionBox, clearHoverNow: clearHover, syncSurfaceTags, clearClassifyHover, cancelClassifyPoly, cancelDraftPoly, clearActiveZoneVisual, highlightZone, computeContextShade };
    // Bridge for the PDF report: render on demand and hand back a PNG snapshot of the current 3D view.
    if (captureRef) captureRef.current = () => { try { renderer.render(scene, camera); return renderer.domElement.toDataURL("image/png"); } catch (e) { return null; } };
    syncSurfaceTags(useStudio.getState().surfaceTags);
    applyNorth(northOffsetRef.current);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("pointerleave", onLeave);
      renderer.domElement.removeEventListener("pointerup", onUp);
      window.removeEventListener("keydown", onKeyDown);
      ro.disconnect();
      clearEquipment();
      clearSystemBlocks3D();
      tcontrols.dispose();
      heightControl.dispose();
      moveControl.dispose();
      scaleControl.dispose();
      controls.dispose();
      renderer.dispose();
      pmrem.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [setMeasureInfo]);

  /* ---- Orthographic isometric fit to bounding box ---- */
  const fitIsometric = useCallback((box) => {
    const api = apiRef.current;
    if (!api) return;
    const { camera, controls } = api;
    const mount = mountRef.current;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    modelScaleRef.current = maxDim; // reference for scale-relative epsilon/tolerance
    const half = maxDim * 0.62;
    const asp = (mount.clientWidth || 1) / (mount.clientHeight || 1);
    camera.left = -half * asp;
    camera.right = half * asp;
    camera.top = half;
    camera.bottom = -half;
    const dir = new THREE.Vector3(1, 0.82, 1).normalize();
    camera.position.copy(center.clone().add(dir.multiplyScalar(maxDim * 3)));
    camera.near = -maxDim * 10;
    camera.far = maxDim * 20;
    camera.updateProjectionMatrix();
    controls.target.copy(center);
    controls.update();

    // Dynamically resize the floor grid + shadow-catcher ground to the model footprint
    const footprint = Math.max(size.x, size.z) || 20;
    const gridSize = Math.max(20, Math.ceil(footprint * 1.8));
    const divisions = Math.max(10, Math.min(120, Math.round(gridSize / 2)));
    if (api.grid) { api.scene.remove(api.grid); api.grid.geometry.dispose(); api.grid.material.dispose(); }
    const grid = new THREE.GridHelper(gridSize, divisions, 0xcfd2d6, 0xe4e6ea);
    grid.position.set(center.x, 0.01, center.z);
    grid.material.transparent = true;
    grid.material.opacity = 0.55;
    api.scene.add(grid);
    api.grid = grid;
    if (api.ground) { api.scene.remove(api.ground); api.ground.geometry.dispose(); api.ground.material.dispose(); }
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(gridSize * 0.62, 64),
      new THREE.MeshStandardMaterial({ color: 0xf3f4f7, roughness: 1, metalness: 0 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(center.x, 0, center.z);
    ground.receiveShadow = true;
    api.scene.add(ground);
    api.ground = ground;

    api.fitHalf = half;
  }, []);

  const buildSample = useCallback((content) => {
    const h = 14;
    const bm = [];
    const mk = (w, hh, d, x, y, z) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, hh, d), clayMaterial());
      m.position.set(x, y, z);
      m.castShadow = m.receiveShadow = true;
      m.userData.isBuilding = true; m.userData.layerRole = "building";
      addEdges(m);
      content.add(m);
      bm.push(m);
      return m;
    };
    mk(12, h, 12, 0, h / 2, 0);
    mk(20, 3, 16, 0, 1.5, 0);
    mk(6, 8, 6, -11, 4, -5);

    const ov = (geo, color) => new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false }));
    const roof = ov(new THREE.BoxGeometry(12.3, 0.3, 12.3), HIGHLIGHT_COLORS.rooftop); roof.position.y = h + 0.15; content.add(roof);
    const facade = ov(new THREE.PlaneGeometry(12, h), HIGHLIGHT_COLORS.facade); facade.position.set(6.06, h / 2, 0); facade.rotation.y = Math.PI / 2; content.add(facade);
    const groundPatch = ov(new THREE.PlaneGeometry(18, 14), HIGHLIGHT_COLORS.ground); groundPatch.rotation.x = -Math.PI / 2; groundPatch.position.y = 0.05; content.add(groundPatch);
    const interior = ov(new THREE.BoxGeometry(8, h - 4, 8), HIGHLIGHT_COLORS.interior); interior.material.wireframe = true; interior.position.y = h / 2; content.add(interior);
    apiRef.current.highlightMeshes = { rooftop: roof, facade, ground: groundPatch, interior };
    apiRef.current.buildingMeshes = bm;
    apiRef.current.modelBounds = buildingBoundsY(bm);
    apiRef.current.heatBakedKey = null;
    apiRef.current.applyAnalysis?.();
  }, []);

  const clearContent = useCallback((content) => {
    if (apiRef.current) { apiRef.current.highlightMeshes = {}; apiRef.current.layerGroups = {}; apiRef.current.layerRoles = {}; apiRef.current.buildingMeshes = []; apiRef.current.heatBakedKey = null; apiRef.current.hideDraftOverlay?.(); apiRef.current.clearEquipment?.(); apiRef.current.clearSystemBlocks3D?.(); apiRef.current.clearLocationBlocks3D?.(); }
    // Preserve the store's blocks/resources during a .dsymbia rehydration (they're re-drawn).
    if (!useStudio.getState().pendingRehydrate) {
      useStudio.getState().clearResourceBlocks();
      useStudio.getState().clearSystemBlocks();
    }
    selectedMeshRef.current = null;
    for (let i = content.children.length - 1; i >= 0; i--) {
      const c = content.children[i];
      content.remove(c);
      c.traverse?.((o) => {
        o.geometry?.dispose?.();
        if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose?.());
      });
    }
  }, []);

  const normalizeAndPlace = useCallback((root, content, unitScale, isRhino) => {
    if (isRhino) root.rotation.x = -Math.PI / 2; // Rhino Z-up -> three.js Y-up
    root.scale.setScalar(unitScale);
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root);
    const center = box.getCenter(new THREE.Vector3());
    root.position.x -= center.x;
    root.position.z -= center.z;
    root.position.y -= box.min.y;
    content.add(root);
    const finalBox = new THREE.Box3().setFromObject(content);
    const size = finalBox.getSize(new THREE.Vector3());
    fitIsometric(finalBox);
    return { x: +size.x.toFixed(2), y: +size.y.toFixed(2), z: +size.z.toFixed(2) };
  }, [fitIsometric]);

  // Ingest the Rhino layer table and group meshes by layer for the visibility + role overlay
  const buildLayerTable = useCallback((root, meshes, isRhino) => {
    const table = root.userData?.layers;
    if (!isRhino || !Array.isArray(table) || !table.length) {
      if (apiRef.current) { apiRef.current.layerGroups = {}; apiRef.current.layerRoles = {}; }
      setLayers([]);
      return;
    }
    const groups = {};
    meshes.forEach((m) => {
      const idx = m.userData?.attributes?.layerIndex;
      if (idx == null || idx < 0) return;
      (groups[idx] = groups[idx] || []).push(m);
    });
    if (apiRef.current) apiRef.current.layerGroups = groups;
    // Every imported mesh is draftable by default (deep-traversal draftableMeshes; no reliance on
    // hardcoded layer names — custom uploads must never fail). Users can still MANUALLY lock a layer
    // to Context via its role chip if they want it non-draftable.
    const roles = {};
    const list = Object.keys(groups).map((k) => {
      const idx = Number(k);
      const layer = table[idx] || {};
      const name = layer.name || layer.fullPath || `Layer ${idx}`;
      // Auto-classify obvious surrounding/site layers as Context (non-draftable, unchecked in the
      // site-anchor prompt) so they don't skew the Ground-vs-Roof datum. Users can still flip roles.
      const isContextName = /context|site|terrain|surround|neighbou?r|adjacent|existing/i.test(name);
      const role = isContextName ? "context" : "building";
      roles[idx] = role;
      groups[idx].forEach((m) => { m.userData.isBuilding = role === "building"; m.userData.layerRole = role; });
      return { index: idx, name, swatch: rhinoLayerColor(layer), visible: groups[idx].every((m) => m.visible), count: groups[idx].length, role };
    }).sort((a, b) => a.index - b.index);
    if (apiRef.current) apiRef.current.layerRoles = roles;
    setLayers(list);
  }, []);

  /* ---- Load model / sample ---- */
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    const { content } = api;
    let cancelled = false;
    clearContent(content);
    setLayers([]);
    apiRef.current?.clearMeasure?.();
    setMeasureMode(false);
    apiRef.current?.clearScenarioFaces3D?.();
    // Preserve stored faces during a .dsymbia rehydration (they're re-drawn once ready).
    if (!useStudio.getState().pendingRehydrate) useStudio.getState().clearScenarioFaces();

    if (modelFile) {
      setModel({ status: "loading", name: modelFile.name, error: null });
      const url = URL.createObjectURL(modelFile);
      const ext = modelFile.name.split(".").pop().toLowerCase();

      const finish = (root, unitScale = 1, isRhino = false) => {
        if (cancelled) return;
        const meshes = [];
        const toRemove = [];
        root.traverse((o) => {
          // Section 10 — keep only real render meshes (BREP faces produce these via
          // ON_Brep.Faces().GetMesh() inside Rhino3dmLoader). Empty/analytic geometry is dropped.
          if (o.isMesh && o.geometry?.attributes?.position?.count) {
            o.material = clayMaterial(); o.castShadow = o.receiveShadow = true;
            o.userData.isBuilding = true; o.userData.layerRole = "building";
            // Bulletproofing: guarantee valid vertex normals + bounds so the raycaster
            // (Selection / Drafting) and shading always have faces to hit.
            const g = o.geometry;
            if (!g.attributes.normal) g.computeVertexNormals();
            g.computeBoundingBox(); g.computeBoundingSphere();
            meshes.push(o);
          } else if (o.isMesh || o.isLine || o.isLineSegments || o.isPoints) toRemove.push(o); // curves / empty
        });
        toRemove.forEach((o) => o.parent && o.parent.remove(o));
        meshes.forEach(addEdges); // crisp architectural outlines (added after traverse so they survive)
        if (apiRef.current) { apiRef.current.buildingMeshes = meshes; apiRef.current.heatBakedKey = null; }
        URL.revokeObjectURL(url);
        if (!meshes.length) {
          // .3dm saved as wireframe / analytic surfaces (no render meshes on the BREPs) → nothing to raycast.
          onNotifyRef.current?.("No render meshes found. Re-save the .3dm from Rhino with render meshes enabled.");
          setModel({ status: "error", error: "This model has no render meshes (curves / BREPs only, likely a wireframe or ‘Save Small’ file). In Rhino, run the Mesh command or enable ‘Save render meshes’, then re-export as .3dm / .obj / .glb." });
          return;
        }
        const dims = normalizeAndPlace(root, content, unitScale, isRhino);
        buildLayerTable(root, meshes, isRhino);
        if (apiRef.current) apiRef.current.modelBounds = buildingBoundsY(meshes);
        useStudio.getState().setNorthOffset(isRhino ? deriveNorthDeg(root) : 0);
        apiRef.current?.applyAnalysis?.();
        setModel({ status: "ready", name: modelFile.name, dims, error: null });
      };
      const fail = (e) => {
        if (cancelled) return;
        warn("model load failed", e);
        URL.revokeObjectURL(url);
        setModel({ status: "error", error: "Could not parse this model. Try .3dm, .obj, .glb or .gltf." });
      };

      try {
        if (ext === "3dm") {
          const loader = new Rhino3dmLoader();
          loader.setLibraryPath("https://cdn.jsdelivr.net/npm/rhino3dm@8.4.0/");
          loader.load(url, (obj) => {
            const us = obj.userData?.settings?.modelUnitSystem;
            const scale = UNIT_TO_M[typeof us === "number" ? us : us?.value] ?? 1;
            finish(obj, scale, true);
          }, undefined, fail);
        } else if (ext === "obj") {
          new OBJLoader().load(url, (o) => finish(o, 1, false), undefined, fail);
        } else if (ext === "glb" || ext === "gltf") {
          new GLTFLoader().load(url, (g) => finish(g.scene, 1, false), undefined, fail);
        } else fail(new Error("unsupported"));
      } catch (e) { fail(e); }
    } else if (useSample) {
      buildSample(content);
      const box = new THREE.Box3().setFromObject(content);
      fitIsometric(box);
      useStudio.getState().setNorthOffset(0);
      setModel({ status: "ready", name: "Sample Building", dims: { x: 20, y: 14, z: 16 }, error: null });
    } else {
      setModel({ status: "empty", name: null, dims: null, error: null });
    }

    return () => { cancelled = true; };
  }, [modelFile, useSample, buildSample, clearContent, normalizeAndPlace, fitIsometric, setModel, buildLayerTable, setMeasureMode]);

  // .dsymbia rehydration: once the re-ingested geometry is ready, re-extrude the saved
  // volumes + re-draw the captured faces at their exact coordinates, then clear the flag.
  useEffect(() => {
    if (model.status !== "ready") return;
    if (!useStudio.getState().pendingRehydrate) return;
    apiRef.current?.rehydrateScene?.();
    apiRef.current?.syncLocationBlocks?.(useStudio.getState().locationBlocks);
    apiRef.current?.syncSurfaceTags?.(useStudio.getState().surfaceTags);
    useStudio.getState().clearPendingRehydrate();
  }, [model.status]);

  // Section 5: on model ready, sum the building-layer gross areas → store (feeds opportunity calc).
  useEffect(() => {
    if (model.status !== "ready") return;
    const g = apiRef.current?.computeGross?.();
    if (g) useStudio.getState().setGrossAreas(g);
  }, [model.status, northOffset]);

  // Item 3 — prompt for the site/ground-floor anchor once a multi-layer model is ready.
  useEffect(() => {
    if (model.status !== "ready") { setSiteAnchored(false); return; }
    if (!siteAnchored && layers.length > 1) {
      setAnchorSel(new Set(layers.filter((l) => l.role === "building").map((l) => l.index)));
      setShowAnchorPrompt(true);
    }
  }, [model.status, layers, siteAnchored]);

  const toggleAnchorSel = (idx) => setAnchorSel((s) => { const n = new Set(s); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  const confirmAnchor = () => {
    const groups = apiRef.current?.layerGroups || {};
    const box = new THREE.Box3(); let has = false;
    anchorSel.forEach((idx) => (groups[idx] || []).forEach((m) => { box.expandByObject(m); has = true; }));
    if (has && apiRef.current) {
      const prev = apiRef.current.modelBounds || {};
      apiRef.current.modelBounds = { minY: box.min.y, maxY: prev.maxY ?? box.max.y };
      const g = apiRef.current.computeGross?.();
      if (g) useStudio.getState().setGrossAreas(g);
    }
    setSiteAnchored(true); setShowAnchorPrompt(false);
    onNotify?.("Site anchored. Ground and rooftop faces re-calibrated");
  };
  const skipAnchor = () => { setSiteAnchored(true); setShowAnchorPrompt(false); };

  /* ---- Drag & drop ---- */
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "rcengine" || ext === "dsymbia") { onImportProject?.(file); return; }
    if (ext === "dwg") { setModel({ status: "error", error: "The .dwg format cannot be parsed in the browser. Export to .3dm, .obj, or .glb from your CAD tool." }); return; }
    if (MODEL_EXTS.includes(ext)) onPickModel(file);
    else setModel({ status: "error", error: "Unsupported format. Use .3dm, .obj, .glb, .gltf or a .rcengine project." });
  };

  const hasContent = model.status === "ready" || model.status === "loading";

  // Effective site DLI for the flexbar: manual override → real per-season DLI → annual DLI → none.
  const _seasonalDli = weather?.seasonalDli?.[season];
  const _autoDli = _seasonalDli != null ? _seasonalDli : (weather?.dli != null ? weather.dli : null);
  const effectiveDli = siteDliOverride != null ? siteDliOverride : _autoDli;
  const dliSource = siteDliOverride != null ? "manual" : (weather?.source || (_autoDli != null ? "estimate" : "none"));
  const dliSourceLabel = { api: "live climate", epw: "EPW file", estimate: "estimate", manual: "manual", none: "no data" }[dliSource] || dliSource;

  return (
    <div ref={mountRef} data-testid="viewport" data-mode={measureMode ? "measure" : areaFocusSystem ? "drafting" : "selection"}
      onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
      onDragLeave={() => setDragging(false)} onDrop={handleDrop}
      style={{ ...styles.mount, cursor: (areaFocusSystem || measureMode) ? "crosshair" : hoverInfo ? "pointer" : identify ? "help" : "default" }}>
      {dragging && <div style={styles.dragOverlay} />}

      {activeZone && !embedded && (
        <div data-testid="active-zone-chip"
          style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 40, display: "inline-flex", alignItems: "center", gap: 10, padding: "7px 8px 7px 14px", borderRadius: 999, background: "rgba(20,24,28,0.92)", color: "#fff", fontSize: 12.5, fontWeight: 600, boxShadow: "0 6px 20px rgba(0,0,0,0.28)", backdropFilter: "blur(8px)" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#34c759", flexShrink: 0 }} />
          <span>Placing in <b>{activeZone.label || "zone"}</b>{activeZone.area ? ` · ${Math.round(activeZone.area).toLocaleString()} m²` : ""}</span>
          <button data-testid="active-zone-release"
            onClick={() => { useStudio.getState().clearActiveZone(); apiRef.current?.clearActiveZoneVisual?.(); onNotify?.("Zone released — next system starts a new zone or free placement"); }}
            title="Release this zone so the next system starts fresh (or draw a new zone elsewhere)"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 14, lineHeight: 1, cursor: "pointer" }}>
            ✕
          </button>
        </div>
      )}


      {showAnchorPrompt && model.status === "ready" && (
        <div style={styles.anchorOverlay} data-testid="site-anchor-prompt">
          <div style={styles.anchorCard}>
            <div style={styles.anchorTitle}>Anchor your site</div>
            <div style={styles.anchorSub}>Select the layer(s) that form your building&apos;s <strong>ground floor / site slab</strong>. This sets the datum used to tell Ground-Level from Rooftop, so surrounding context masses don&apos;t skew it.</div>
            <div style={styles.anchorList}>
              {layers.map((l) => {
                const on = anchorSel.has(l.index);
                return (
                  <button key={l.index} data-testid={`anchor-layer-${l.index}`} onClick={() => toggleAnchorSel(l.index)}
                    style={{ ...styles.anchorRow, ...(on ? styles.anchorRowOn : {}) }}>
                    <span style={{ ...styles.anchorCheck, ...(on ? styles.anchorCheckOn : {}) }}>{on ? "✓" : ""}</span>
                    <span style={{ ...styles.swatch, background: l.swatch }} />
                    <span style={styles.anchorName}>{l.name}</span>
                    <span style={styles.anchorMeta}>{l.role === "building" ? "Building" : "Context"} · {l.count}</span>
                  </button>
                );
              })}
            </div>
            <div style={styles.anchorActions}>
              <button style={styles.anchorSkip} data-testid="anchor-skip" onClick={skipAnchor}>Skip (auto)</button>
              <button style={{ ...styles.anchorConfirm, ...(anchorSel.size ? {} : styles.anchorConfirmDisabled) }} data-testid="anchor-confirm" disabled={!anchorSel.size} onClick={confirmAnchor}>Anchor Site</button>
            </div>
          </div>
        </div>
      )}
      {areaFocusSystem && hasContent && <div style={styles.draftGlow} />}

      {hasContent && (toolsMin ? (
        <button style={styles.toolDockTab} data-testid="tooldock-expand" onClick={() => setToolsMin(false)} title="Show tools">
          <span style={styles.toolDockTabIcon}>⚒</span>
        </button>
      ) : (
        <div style={styles.toolDock} data-testid="tool-dock">
          <div style={styles.toolDockHead}>
            <span style={styles.toolDockTitle}>TOOLS</span>
            <button style={styles.toolDockMin} data-testid="tooldock-minimize" onClick={() => setToolsMin(true)} title="Minimize tools">‹</button>
          </div>
          {!analysisMode && (
            <button
              data-testid="measure-toggle"
              onClick={toggleMeasure}
              style={{ ...styles.dockBtn, ...(measureMode ? { background: "#51aafe", color: "#fff", borderColor: "transparent" } : {}) }}
            >
              <RulerGlyph on={measureMode} />
              {measureMode ? "Measuring · select two points" : "Measure"}
            </button>
          )}
          {!analysisMode && (
            <button
              data-testid="section-toggle"
              onClick={() => setSectionOn(!sectionOn)}
              style={{ ...styles.dockBtn, ...(sectionOn ? { background: "#5E5CE6", color: "#fff", borderColor: "transparent" } : {}) }}
            >
              {sectionOn ? "Section Box · ON" : "Section Box"}
            </button>
          )}
          {!analysisMode && !embedded && (
            <button
              data-testid="classify-toggle"
              onClick={() => { setClassifyMode(!classifyMode); if (measureMode) toggleMeasure(); }}
              style={{ ...styles.dockBtn, ...(classifyMode ? { background: "#34c759", color: "#fff", borderColor: "transparent" } : {}) }}
              title="Classify surfaces: tag faces or drawn regions as ground / roof / podium / interior / exterior"
            >
              {classifyMode ? "Classifying · ON" : "Classify Surfaces"}
            </button>
          )}
          {!analysisMode && <div style={styles.dockDivider} />}
          <button
            data-testid="btn-save-project"
            onClick={() => onSaveProject?.()}
            style={{ ...styles.dockBtn, background: "#51aafe", color: "#fff", borderColor: "transparent" }}
            title="Save Scenario"
          >
            {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "✓ Saved" : saveStatus === "error" ? "Retry Save" : "Save Scenario"}
          </button>
          <button
            data-testid="export-project-btn"
            onClick={() => onExportProject?.()}
            style={{ ...styles.dockBtn, background: colors.textPrimary, color: "#fff", borderColor: "transparent" }}
            title="Export the whole project (.rcengine) to your desktop"
          >
            Export Project
          </button>
          <button
            data-testid="generate-report-btn"
            onClick={() => onGenerateReport?.()}
            style={{ ...styles.dockBtn, color: "#2E86D9" }}
            title="Generate PDF Report"
          >
            Generate PDF
          </button>
          <button
            data-testid="report-details-btn"
            onClick={() => onReportDetails?.()}
            style={{ ...styles.dockBtn, color: colors.textSecondary }}
            title="Add a cover title, client name and studio logo to the PDF"
          >
            Report details…
          </button>
        </div>
      ))}

      {measureMode && hasContent && (
        <div style={styles.measurePanel} data-testid="measure-panel">
          <span style={styles.measureLabel}>MEASURE</span>
          {measureInfo?.dist != null ? (
            <span style={styles.measureVal} data-testid="measure-distance">{measureInfo.dist < 1 ? `${(measureInfo.dist * 100).toFixed(1)} cm` : `${measureInfo.dist.toFixed(2)} m`}</span>
          ) : (
            <span style={styles.measureHintTxt}>{measureInfo ? "Select the second point…" : "Select a point on the model (snaps to corners)"}</span>
          )}
          <button style={styles.measureClearBtn} data-testid="measure-clear" onClick={() => apiRef.current?.clearMeasure?.()}>Clear</button>
        </div>
      )}

      {sectionOn && hasContent && (
        <div style={styles.sectionPanel} data-testid="section-panel">
          <div style={styles.sectionHeadRow}>
            <span style={styles.sectionTitle}>SECTION BOX</span>
            <button
              type="button"
              data-testid="section-collapse"
              title={sectionCollapsed ? "Expand section controls" : "Collapse section controls"}
              onClick={() => setSectionCollapsed((v) => !v)}
              style={styles.sectionCollapseBtn}
            >
              {sectionCollapsed ? "+" : "–"}
            </button>
          </div>
          {!sectionCollapsed && (
            <>
              {["x", "y", "z"].map((ax) => (
                <div key={ax} style={styles.sectionRow}>
                  <span style={styles.sectionAxis}>{ax.toUpperCase()}</span>
                  <input
                    type="range" min={0.05} max={1} step={0.01}
                    value={sectionXYZ[ax]}
                    data-testid={`section-slider-${ax}`}
                    onChange={(e) => setSectionXYZ((s) => ({ ...s, [ax]: Number(e.target.value) }))}
                    style={styles.sectionSlider}
                  />
                </div>
              ))}
              <button style={styles.sectionReset} data-testid="section-reset" onClick={() => setSectionXYZ({ x: 1, y: 1, z: 1 })}>Reset</button>
            </>
          )}
        </div>
      )}

      {pickTool?.kind === "volume" && hasContent && !embedded && (
        <div style={styles.pickModePill} data-testid="pick-mode-toggle">
          <span style={styles.pickModeLabel}>Select</span>
          {[{ id: "volume", label: "Whole volume" }, { id: "surface", label: "Single surface" }].map((m) => {
            const active = (pickTool.mode || "volume") === m.id;
            return (
              <button key={m.id} data-testid={`pick-mode-${m.id}`}
                onClick={() => setPickToolStore({ ...pickTool, mode: m.id })}
                style={{ ...styles.pickModeBtn, ...(active ? styles.pickModeBtnOn : {}) }}>
                {m.label}
              </button>
            );
          })}
        </div>
      )}

      {classifyMode && hasContent && !embedded && (
        <div style={styles.classifyPanel} data-testid="classify-panel">
          <div style={styles.classifyHead}>
            <span style={styles.sectionTitle}>CLASSIFY SURFACES</span>
            <button style={styles.sectionCollapseBtn} data-testid="classify-close" title="Exit classify mode" onClick={() => setClassifyMode(false)}>×</button>
          </div>
          <div style={styles.classifySub}>
            {classifyLayer === "material"
              ? (classifyDrawMode === "face" ? `Click a surface to toggle ${classifyMaterial} on/off (layers over its type).` : `Draw a region to tag it ${classifyMaterial}.`)
              : classifyDrawMode === "rect" ? "Drag a rectangle on a face to tag a sub-region."
              : classifyDrawMode === "poly" ? "Click corners on a face to trace a region (around anything on top); click the first point or press Enter to close."
              : "Click a surface to set its type; click again to deselect. Use “Auto” to erase."}
          </div>
          <div style={styles.classifyPaletteLabel}>SURFACE TYPE</div>
          <div style={styles.classifyPalette} data-testid="classify-base-row">
            {[...BASE_CATS, { id: "auto", label: "Auto (erase)", hex: "#c7c7cc" }].map((c) => {
              const active = classifyLayer === "base" && classifyCategory === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`classify-cat-${c.id}`}
                  onClick={() => setClassifyCategory(c.id)}
                  style={{ ...styles.catBtn, ...(active ? { borderColor: c.hex, background: `${c.hex}22`, color: colors.textPrimary } : {}) }}
                >
                  <span style={{ ...styles.catDot, background: c.hex }} />
                  {c.label}
                </button>
              );
            })}
          </div>
          <div style={styles.classifyPaletteLabel}>MATERIAL <span style={{ fontWeight: 400, textTransform: "none", opacity: 0.7 }}>· layers over type</span></div>
          <div style={styles.classifyPalette} data-testid="classify-material-row">
            {MATERIAL_DEFS.map((c) => {
              const active = classifyLayer === "material" && classifyMaterial === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`classify-material-${c.id}`}
                  onClick={() => setClassifyMaterial(c.id)}
                  style={{ ...styles.catBtn, ...(active ? { borderColor: c.hex, background: `${c.hex}22`, color: colors.textPrimary } : {}) }}
                >
                  <span style={{ ...styles.catDot, background: c.hex }} />
                  {c.label}
                </button>
              );
            })}
          </div>
          <div style={styles.classifyDrawRow} data-testid="classify-draw-row">
            <span style={styles.classifyDrawLabel}>Region</span>
            {[
              { id: "face", label: "Whole face" },
              { id: "rect", label: "Rectangle" },
              { id: "poly", label: "Polygon" },
            ].map((m) => (
              <button
                key={m.id}
                data-testid={`classify-draw-${m.id}`}
                onClick={() => setClassifyDrawMode(m.id)}
                style={{ ...styles.classifyDrawBtn, ...(classifyDrawMode === m.id ? { background: colors.textPrimary, color: "#fff", borderColor: "transparent" } : {}) }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div style={styles.classifyUndoRow} data-testid="classify-undo-row">
            <button
              data-testid="classify-undo"
              disabled={!canUndoClassify}
              onClick={() => { if (canUndoClassify) { undoSurfaceTags(); onNotify?.("Reverted last classification"); } }}
              style={{ ...styles.classifyUndoBtn, ...(canUndoClassify ? {} : { opacity: 0.45, cursor: "not-allowed" }) }}
              title="Undo the last classification (add, erase or reshape)"
            >
              ↩ Undo last
            </button>
          </div>
          {surfaceTags.length > 0 && (
            <div style={styles.classifyLegend} data-testid="classify-legend">
              {BASE_CATS.map((c) => {
                const items = surfaceTags.filter((t) => t.category === c.id);
                if (!items.length) return null;
                const area = Math.round(items.reduce((s, t) => s + (t.area || 0), 0));
                return (
                  <div key={c.id} style={styles.legendRow} data-testid={`classify-legend-${c.id}`}>
                    <span style={{ ...styles.catDot, background: c.hex }} />
                    <span style={styles.legendLabel}>{c.label}</span>
                    <span style={styles.legendVal}>{items.length} · {area.toLocaleString()} m²</span>
                  </div>
                );
              })}
              {MATERIAL_DEFS.map((c) => {
                const pred = c.id === "glazed" ? tagIsGlazed : tagIsOpaque;
                const items = surfaceTags.filter(pred);
                if (!items.length) return null;
                const area = Math.round(items.reduce((s, t) => s + (t.area || 0), 0));
                return (
                  <div key={c.id} style={styles.legendRow} data-testid={`classify-legend-${c.id}`}>
                    <span style={{ ...styles.catDot, background: c.hex }} />
                    <span style={styles.legendLabel}>{c.label} <span style={{ opacity: 0.6 }}>(layer)</span></span>
                    <span style={styles.legendVal}>{items.length} · {area.toLocaleString()} m²</span>
                  </div>
                );
              })}
              <button style={styles.sectionReset} data-testid="classify-reset-all" onClick={() => clearSurfaceTags()}>Reset all to auto</button>
            </div>
          )}
          {surfaceTags.some(tagIsGlazed) && (
            <div style={styles.glazingBox} data-testid="glazing-daylight">
              <div style={styles.glazingHead}>GLAZING &amp; DAYLIGHT</div>
              {surfaceTags.filter(tagIsGlazed).map((t, i) => (
                <div key={t.id} style={styles.glazingRow}>
                  <span style={styles.glazingArea}>{Math.round(t.area || 0)} m²</span>
                  <select
                    data-testid={`glazing-type-${i}`}
                    value={t.glazing?.type || DEFAULT_GLAZING}
                    onChange={(e) => updateSurfaceTag(t.id, { glazing: { type: e.target.value } })}
                    style={styles.glazingSelect}
                  >
                    {GLAZING_PRESETS.map((p) => (<option key={p.id} value={p.id}>{p.label} · VLT {Math.round(p.vlt * 100)}%</option>))}
                  </select>
                </div>
              ))}
              {(() => {
                const agg = glazingAggregate(surfaceTags, classifyGeo?.lat);
                const dli = classifyWeather?.dli, ghi = classifyWeather?.ghi;
                const interiorDli = dli != null ? (dli * agg.vlt) : null;
                const solarKwh = ghi != null ? Math.round(ghi * 365 * agg.solarGainCoeff) : null;
                return (
                  <div style={styles.glazingReadout} data-testid="glazing-readout">
                    <div style={styles.glazingStat}><span>Interior daylight (behind glass)</span><strong data-testid="glazing-interior-dli">{interiorDli != null ? `${interiorDli.toFixed(1)} mol/m²·d` : "site DLI needed"}</strong></div>
                    <div style={styles.glazingStat}><span>Passive solar gain</span><strong data-testid="glazing-solar-gain">{solarKwh != null ? `${solarKwh.toLocaleString()} kWh/yr` : "site GHI needed"}</strong></div>
                    {agg.split.length > 0 && (
                      <div style={styles.glazingSplit} data-testid="glazing-split">
                        {agg.split.map((s) => (
                          <div key={s.key} style={styles.glazingStat}><span>{s.label} glazing</span><strong>{s.area_m2.toLocaleString()} m² · ×{s.factor.toFixed(2)}</strong></div>
                        ))}
                      </div>
                    )}
                    <div style={styles.glazingCite}>Interior DLI = site DLI × VLT (ASHS) · solar = Σ area × insolation × SHGC × orientation factor (NREL Solar Radiation Data Manual) · VLT/SHGC from LBNL WINDOW / NFRC. Feeds the Energy &amp; Spatial KPIs.</div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}


      {analysisMode && hasContent && (
        <div ref={flexBarRef} style={{ ...styles.flexBar, ...(analysisPos ? { left: analysisPos.x, top: analysisPos.y, bottom: "auto", transform: "none" } : {}) }} data-testid="analysis-flexbar">
          <div style={styles.flexHead} onMouseDown={startAnalysisDrag} data-testid="analysis-flexbar-drag">
            <span style={{ ...styles.flexDot, background: RING_CSS[rootPillar] }} />
            <span style={styles.flexTitle}>{ANALYSIS_TITLE[rootPillar]}</span>
            <span style={styles.flexSeason}>{SEASON_LABELS[season]}</span>
            <span style={styles.flexDragHint} title="Drag to move">⠿</span>
          </div>

          <div style={styles.flexSeg} data-testid="season-toggles">
            {["SPR", "SUM", "AUT", "WNT"].map((s) => (
              <button
                key={s}
                data-testid={`season-toggle-${s.toLowerCase()}`}
                onClick={() => setSeason(s)}
                style={{ ...styles.segBtn, ...(season === s ? { background: RING_CSS[rootPillar], color: "#fff" } : {}) }}
              >
                {s}
              </button>
            ))}
            <span style={{ marginLeft: 6, display: "inline-flex" }}>
              <InfoTip testId="season-info" title="Season" body="Recalculates the viability heat-map for the selected season's sun path and daylight hours. Spring and Summer expose more surfaces; Winter is the most demanding case, so a surface that remains viable in Winter is viable year-round." />
            </span>
          </div>

          <div style={styles.sliderBlock}>
            <div style={styles.sliderLabel}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>Site DLI · {SEASON_LABELS[season]}<InfoTip testId="site-dli-info" title="Site DLI" body="The daily light this location actually receives in the selected season (mol/m²·day), taken from real climate data for the pinned site. Edit it to override with your own measured or design value — the crop-viability map recomputes from this number, never from an invented estimate." /></span>
              <span style={styles.sliderVal} data-testid="site-dli-source">{dliSourceLabel}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <input
                type="number" min="0" max="60" step="0.5"
                value={effectiveDli != null ? effectiveDli : ""}
                placeholder="enter value"
                data-testid="site-dli-input"
                onChange={(e) => setSiteDliOverride(e.target.value === "" ? null : Math.max(0, Math.min(60, +e.target.value)))}
                style={{ width: 84, padding: "5px 8px", borderRadius: 7, border: `1px solid ${colors.hairline || "#3a4048"}`, background: "rgba(255,255,255,0.04)", color: colors.textPrimary, fontSize: 13, outline: "none" }}
              />
              <span style={{ fontSize: 11, color: "#8a93a0" }}>mol/m²·d</span>
              {siteDliOverride != null && (
                <button data-testid="site-dli-auto" onClick={() => setSiteDliOverride(null)} style={{ ...styles.sectionReset, marginLeft: "auto" }}>Use climate data</button>
              )}
            </div>
            {effectiveDli == null && (
              <div data-testid="site-dli-empty" style={{ fontSize: 11, color: "#d99a4a", marginTop: 6 }}>No climate data for this site — enter a site DLI to map crop viability.</div>
            )}
          </div>

          <div style={styles.sliderBlock}>
            <div style={styles.sliderLabel}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>DLI threshold<InfoTip testId="dli-info" title="DLI threshold" body="Daily Light Integral: the minimum amount of light (moles per m² per day) a surface must receive to be counted as viable for this loop. Increase it to require brighter locations; decrease it to include shadier surfaces." /></span>
              <span style={styles.sliderVal} data-testid="dli-threshold-value">{dliThreshold} mol</span>
            </div>
            <input
              type="range" min="2" max="40" step="1" value={dliThreshold}
              data-testid="dli-threshold-slider"
              onChange={(e) => setDliThreshold(+e.target.value)}
              style={{ ...styles.slider, accentColor: RING_CSS[rootPillar] }}
            />
          </div>

          <div style={styles.sliderBlock}>
            <div style={styles.sliderLabel}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>Min area<InfoTip testId="minarea-info" title="Minimum area" body="Excludes viable areas smaller than this threshold, removing fragments too small to hold practical hardware so that only usable surfaces remain highlighted." /></span>
              <span style={styles.sliderVal} data-testid="area-threshold-value">{areaThreshold} m²</span>
            </div>
            <input
              type="range" min="0" max="200" step="5" value={areaThreshold}
              data-testid="area-threshold-slider"
              onChange={(e) => setAreaThreshold(+e.target.value)}
              style={{ ...styles.slider, accentColor: RING_CSS[rootPillar] }}
            />
          </div>

          <div style={styles.legend}>
            <span style={styles.legendTxt}>0%</span>
            <span style={{ ...styles.legendBar, background: `linear-gradient(90deg, #a8b0b8, ${RING_CSS[rootPillar]})` }} />
            <span style={styles.legendTxt}>100%</span>
            <span style={{ marginLeft: 6, display: "inline-flex" }}>
              <InfoTip testId="legend-info" title="Suitability scale" body="Each surface is shaded by how well it meets this loop's requirements: grey indicates unsuitable (0%) and full colour indicates ideal (100%). Lower-suitability surfaces appear greyer; higher-suitability surfaces appear more saturated." />
            </span>
          </div>
        </div>
      )}

      {activeBlockId && rootPillar === "systems" && !analysisMode && !areaFocusSystem && hasContent && (
        <div style={styles.xformBar} data-testid="block-xform-bar">
          <span style={styles.xformLabel}>Edit volume</span>
          {[
            { id: "move", label: "Move" },
            { id: "resize", label: "Resize" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`block-xform-${m.id}`}
              onClick={() => setBlockXformMode(m.id)}
              style={{ ...styles.xformBtn, ...(blockXformMode === m.id ? { background: RING_CSS.systems, color: "#fff", borderColor: "transparent" } : {}) }}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {areaFocusSystem && hasContent && (
        <div style={styles.pickBanner} data-testid="surface-pick-banner">Drafting mode: drag a rectangle on a building surface, or tap to place a default footprint. Press Esc to cancel.</div>
      )}

      {hoverInfo && !areaFocusSystem && hasContent && (
        <div style={{ ...styles.hoverBadge, borderColor: colors.pillar[rootPillar] }} data-testid="surface-hover-badge">
          <span style={{ ...styles.hoverDot, background: colors.pillar[rootPillar] }} />
          <span style={styles.hoverTag}>{hoverInfo.tag}</span>
          <span style={styles.hoverArea}>{Math.round((hoverInfo.area || 0) * 10) / 10} m²</span>
          {rootPillar === "locations" && <span style={styles.hoverAction}>· click to set site</span>}
        </div>
      )}

      {identify && hasContent && !areaFocusSystem && !measureMode && !pickTool && !allocDraw && !placeResource && !analysisMode && !classifyMode && (
        <div
          style={{ ...styles.identifyPop, left: identify.x, top: identify.y }}
          data-testid="face-identify-popup"
        >
          <span style={{ ...styles.identifyDot, background: identify.hex || IDENTIFY_HEX[identify.kind] || "#0a84ff" }} />
          <span style={styles.identifyLabel} data-testid="face-identify-label">{identify.tag}</span>
        </div>
      )}

      {hasContent && (
        <div style={styles.fileBar} data-testid="model-filebar">
          <div style={styles.fileMeta}>
            <span style={styles.fileName}>{model.name}</span>
            {model.dims && <span style={styles.fileDims}>{Number(model.dims.x).toFixed(1)} × {Number(model.dims.z).toFixed(1)} m · {Number(model.dims.y).toFixed(1)} m tall</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.fileBtn} data-testid="model-guide-btn" onClick={() => setGuideOpen(true)}>Import guide</button>
            <label style={styles.fileBtn} data-testid="model-reupload-btn">
              Re-upload
              <input data-testid="model-file-input" type="file" accept=".3dm,.obj,.glb,.gltf" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && onPickModel(e.target.files[0])} />
            </label>
            <button style={{ ...styles.fileBtn, ...styles.fileBtnDanger }} data-testid="model-clear-btn" onClick={onClear}>Clear</button>
          </div>
        </div>
      )}

      {hasContent && layers.length > 0 && (
        <div style={styles.layerPanel} data-testid="layer-panel">
          <button style={styles.layerHeader} data-testid="layer-panel-toggle" onClick={() => setLayersOpen((v) => !v)}>
            <span style={styles.layerTitle}>Layers</span>
            <span style={styles.layerCount}>{layers.length}</span>
            <span style={{ ...styles.chev, transform: layersOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
              <ChevronGlyph />
            </span>
          </button>
          {layersOpen && (
            <div style={styles.layerList}>
              {layers.map((l) => (
                <div key={l.index} data-testid={`layer-row-${l.index}`} style={{ ...styles.layerRow, opacity: l.visible ? 1 : 0.45 }}>
                  <button
                    style={styles.layerVisBtn}
                    onClick={() => toggleLayer(l.index)}
                    title={`${l.name} · ${l.count} object${l.count === 1 ? "" : "s"} · toggle visibility`}
                  >
                    <span style={{ ...styles.swatch, background: l.swatch }} />
                    <span style={styles.layerName}>{l.name}</span>
                    <span style={styles.eye}><EyeGlyph on={l.visible} /></span>
                  </button>
                  <button
                    data-testid={`layer-role-${l.index}`}
                    onClick={() => toggleLayerRole(l.index)}
                    title="Role. Building: draftable & counts toward opportunity · Context: locked & excluded · BIA System: pre-accounted existing system (locked & excluded)"
                    style={{ ...styles.roleChip, ...(l.role === "building" ? styles.roleBuilding : l.role === "bia" ? styles.roleBia : styles.roleContext) }}
                  >
                    {l.role === "building" ? "Building" : l.role === "bia" ? "BIA System" : "Context"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {biaPrompt && (
        <div style={styles.biaPromptCard} data-testid="bia-prompt">
          <div style={styles.biaPromptTitle}>Account for existing BIA systems?</div>
          <div style={styles.biaPromptBody}>
            You tagged a layer as <b>BIA System</b>. Account for the systems already modeled in that layer? This switches to <b>optimization mode</b>: each existing volume is measured into an editable draft card (footprint, height and volume) that you then define, allocate and assign crops to.
          </div>
          <div style={styles.biaPromptRow}>
            <button data-testid="bia-prompt-yes" style={styles.biaPromptYes} onClick={() => { setOptimizationMode(true); setBiaPrompt(false); apiRef.current?.spawnBiaBlocksFromLayer?.(); }}>Yes, measure existing</button>
            <button data-testid="bia-prompt-no" style={styles.biaPromptNo} onClick={() => { setOptimizationMode(false); setBiaPrompt(false); }}>No, keep planning</button>
          </div>
        </div>
      )}


      {model.status === "empty" && (
        <div style={styles.emptyWrap} data-testid="viewport-empty-state">
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}><UploadGlyph /></div>
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              data-testid="import-guide-btn"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "center", margin: "2px 0 12px", padding: "7px 14px", borderRadius: 999, border: `1px solid ${colors.brand}55`, background: `${colors.brand}12`, color: colors.brand, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >
              Read the import guide
            </button>
            <label style={styles.emptyBtn} data-testid="viewport-upload-btn">
              Upload model
              <input data-testid="model-file-input" type="file" accept=".3dm,.obj,.glb,.gltf" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && onPickModel(e.target.files[0])} />
            </label>
            <div style={styles.formats}>.3DM (BREP + Mesh) · .OBJ · .GLB · .GLTF</div>
            <button style={styles.sampleLink} data-testid="viewport-sample-btn" onClick={onUseSample}>or explore a sample building →</button>
            <div style={styles.emptyDivider}><span>OR</span></div>
            <label style={styles.openProjectZone} data-testid="viewport-open-project-btn">
              <span style={styles.openProjectTitle}>Open Existing Project</span>
              <span style={styles.openProjectSub}>Drop or browse a saved <strong>.rcengine</strong> file to resume exactly where you left off</span>
              <input type="file" accept=".rcengine,.dsymbia,application/json" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && onImportProject?.(e.target.files[0])} />
            </label>
          </div>
        </div>
      )}

      {model.status === "loading" && (
        <div style={styles.loadingWrap} data-testid="viewport-loading">
          <div style={styles.spinner} />
          <div style={styles.loadingText}>Parsing BREP &amp; mesh geometry…</div>
        </div>
      )}

      {model.status === "error" && (
        <div style={styles.emptyWrap} data-testid="viewport-error">
          <div style={styles.emptyCard}>
            <div style={{ ...styles.emptyIcon, background: "rgba(255,59,48,0.1)", color: "#FF3B30" }}>⚠</div>
            <div style={styles.emptyTitle}>Import issue</div>
            <div style={styles.emptySub}>{model.error}</div>
            <label style={styles.emptyBtn} data-testid="viewport-upload-btn">
              Try another file
              <input data-testid="model-file-input" type="file" accept=".3dm,.obj,.glb,.gltf" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && onPickModel(e.target.files[0])} />
            </label>
            <button style={styles.sampleLink} data-testid="viewport-sample-btn" onClick={onUseSample}>or explore a sample building →</button>
          </div>
        </div>
      )}

      <ImportGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

function UploadGlyph() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
      <path d="M12 16V4M12 4l-4 4M12 4l4 4" stroke={colors.pillar.location} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" stroke={colors.pillar.location} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path d="M6 9l6 6 6-6" stroke={colors.textTertiary} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EyeGlyph({ on }) {
  const c = on ? colors.pillar.location : colors.textTertiary;
  return on ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke={c} strokeWidth="1.8" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M3 3l18 18M10.6 6.2A9.9 9.9 0 0112 6c7 0 10.5 6 10.5 6a17 17 0 01-3.3 4M6.3 7.9A17 17 0 001.5 12S5 18 12 18a10 10 0 004-.8" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RulerGlyph({ on }) {
  const c = on ? "#fff" : colors.textPrimary;
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M3 7h18a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M7 7v3M11 7v4M15 7v3M19 7v4" stroke={c} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

